// ═══════════════════════════════════════════════════════════════════
// SukukPage — التمويل (v3.3.397)
//
// Tabs:
//   1. العقود (active contracts) — list + drill-down to detail
//   2. العروض (offers) — list + convert to contract
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { sukuk } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
  BarChart, PieChart, Pie, Cell,
} from "recharts";
import {
  FiBriefcase, FiFileText, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX,
  FiCheckCircle, FiClock, FiAlertCircle, FiTrendingUp, FiCalendar,
  FiDollarSign, FiArrowLeft, FiArrowRight, FiZap, FiBarChart2,
  FiDownload, FiUpload, FiFile,
} from "react-icons/fi";

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");
// v3.3.416 — Format date as dd/mm/yyyy
const fmtDateDMY = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
// v3.3.424 — Annualize an absolute admin fee rate based on tenor (months)
//   absolute 1.2% over 24 months = 0.6% annual
//   absolute 1.2% over 6 months  = 2.4% annual
const annualizeAdminFee = (absoluteRate, tenorMonths) => {
  const r = Number(absoluteRate) || 0;
  const t = Number(tenorMonths) || 0;
  if (r === 0 || t <= 0) return 0;
  return r * 12 / t;
};
const fmt2 = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const ARABIC_MONTHS_SHORT = ["ينا","فبر","مار","أبر","ماي","يون","يول","أغس","سبت","أكت","نوف","ديس"];
const monthLabel = (key) => {
  const [y, m] = String(key).split("-");
  return `${ARABIC_MONTHS_SHORT[parseInt(m) - 1] || m} ${y.slice(2)}`;
};
// v3.3.430 — English month labels for charts (avoids RTL rendering issues with Arabic + numbers)
const ENGLISH_MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabelEN = (key) => {
  const [y, m] = String(key).split("-");
  return `${ENGLISH_MONTHS_SHORT[parseInt(m) - 1] || m} ${y.slice(2)}`;
};
// v3.3.430 — Fill in missing months in a date range to ensure continuous timeline
// Input: array of {month: "YYYY-MM", ...rest}
// Output: same array with missing months added (with zero values)
function fillMissingMonths(monthly, fromMonth, toMonth, zeroFields = ["principal", "interest", "overdue", "paid", "payment_count", "paid_count", "overdue_count"]) {
  if (!monthly || monthly.length === 0) {
    if (!fromMonth || !toMonth) return [];
  }
  const byMonth = {};
  for (const m of (monthly || [])) byMonth[m.month] = m;

  // Determine range
  let start = fromMonth;
  let end = toMonth;
  if (!start && monthly.length > 0) start = monthly[0].month;
  if (!end && monthly.length > 0) end = monthly[monthly.length - 1].month;
  if (!start || !end) return monthly || [];

  const result = [];
  const [sy, sm] = start.split("-").map(Number);
  const [ey, em] = end.split("-").map(Number);
  let y = sy, mo = sm;
  let safety = 0;
  while ((y < ey || (y === ey && mo <= em)) && safety < 240) {
    safety++;
    const key = `${y}-${String(mo).padStart(2, "0")}`;
    if (byMonth[key]) {
      result.push(byMonth[key]);
    } else {
      const empty = { month: key };
      for (const f of zeroFields) empty[f] = 0;
      result.push(empty);
    }
    mo++;
    if (mo > 12) { mo = 1; y++; }
  }
  return result;
}

const STATUS_META = {
  pending:   { label: "قيد الانتظار",  color: "#92400e", bg: "#fef3c7", icon: "⏳" },
  active:    { label: "نشط",            color: "#065f46", bg: "#d1fae5", icon: "✅" },
  matured:   { label: "مستحق/مسدد",    color: "#1e3a8a", bg: "#dbeafe", icon: "💰" },
  cancelled: { label: "ملغي",           color: "#475569", bg: "#e2e8f0", icon: "✕"  },
};

function PageHeader({ icon, title, subtitle, action }) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {icon}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{title}</h1>
        </div>
        {subtitle && <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

export default function SukukPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating) || user?.is_admin;
  // v3.3.418 — Granular sukuk permissions (with backward-compat fallback)
  const canEdit = isAdmin || user?.sukuk_can_add || user?.sukuk_can_edit || user?.sukuk_can_delete || user?.can_edit_projects;
  const canExport = isAdmin || user?.sukuk_can_export || user?.can_view_projects;
  const canImport = isAdmin || user?.sukuk_can_import || user?.can_edit_projects;
  const canActivate = isAdmin || user?.sukuk_can_activate || user?.can_edit_projects;
  const canDelete = isAdmin || user?.sukuk_can_delete || user?.can_edit_projects;

  const [tab, setTab] = useState("contracts"); // contracts | offers
  const [selectedContractId, setSelectedContractId] = useState(null);

  return (
    <div style={{ padding: "16px 20px", direction: "rtl" }}>
      <PageHeader
        icon={<FiDollarSign size={26} style={{ color: "#0891b2" }} />}
        title="التمويل (الصكوك)"
        subtitle="إدارة عقود الصكوك والعروض المعروضة من شركات الإصدار"
      />

      {selectedContractId === null && (
        <>
          {/* v3.3.428 — Tabs reordered to follow logical workflow:
              العروض (offers) → العقود الفعلية (contracts) → الإصدارات القائمة (active)
              In RTL, this displays from right to left: offers (rightmost), then contracts, then active (leftmost) */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14, borderBottom: "1px solid #e2e8f0" }}>
            <button
              onClick={() => setTab("offers")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: "8px 8px 0 0",
                background: tab === "offers" ? "#7c3aed" : "transparent",
                color: tab === "offers" ? "#fff" : "#475569",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
              title="عروض من شركات الإصدار (قبل التحويل لعقد)"
            >📋 العروض</button>
            <button
              onClick={() => setTab("contracts")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: "8px 8px 0 0",
                background: tab === "contracts" ? "#0891b2" : "transparent",
                color: tab === "contracts" ? "#fff" : "#475569",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
              title="العقود الموقعة (بعد قبول العرض)"
            >📜 العقود الفعلية</button>
            <button
              onClick={() => setTab("active")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: "8px 8px 0 0",
                background: tab === "active" ? "#dc2626" : "transparent",
                color: tab === "active" ? "#fff" : "#475569",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
              title="الصكوك المُفعَّلة فعلياً = مديونيات قائمة على الشركة"
            >🔴 الإصدارات القائمة</button>
            {/* v3.3.476 — Payment schedule tab */}
            <button
              onClick={() => setTab("schedule")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: "8px 8px 0 0",
                background: tab === "schedule" ? "#b45309" : "transparent",
                color: tab === "schedule" ? "#fff" : "#475569",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
              title="جدول كل دفعات السداد لكل الإصدارات الفاعلة مرتباً بالتاريخ، مع فلترة بالمشروع/الشهر/السنة"
            >📅 جدول السداد</button>
            {/* v3.3.429 — Analytics tab */}
            <button
              onClick={() => setTab("analytics")}
              style={{
                padding: "8px 18px", border: "none", borderRadius: "8px 8px 0 0",
                background: tab === "analytics" ? "#0f766e" : "transparent",
                color: tab === "analytics" ? "#fff" : "#475569",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
              title="تحليلات شاملة للتمويل: التكاليف، التوزيعات، APR، المخاطر"
            >📊 تحليل التمويل</button>
          </div>

          {tab === "offers" && (
            <OffersTab canEdit={canEdit} canExport={canExport} canImport={canImport} canDelete={canDelete} onConverted={(cid) => { setTab("contracts"); setSelectedContractId(cid); }} />
          )}
          {tab === "contracts" && (
            <ContractsTab canEdit={canEdit} canExport={canExport} canImport={canImport} canDelete={canDelete} onSelectContract={(id) => setSelectedContractId(id)} />
          )}
          {tab === "active" && (
            <ActiveSukuksTab canEdit={canEdit} onSelectContract={(id) => setSelectedContractId(id)} />
          )}
          {tab === "schedule" && (
            <PaymentScheduleTab onSelectContract={(id) => setSelectedContractId(id)} />
          )}
          {tab === "analytics" && (
            <FinancingAnalyticsTab />
          )}
        </>
      )}

      {selectedContractId !== null && (
        <ContractDetail
          contractId={selectedContractId}
          canEdit={canEdit}
          canExport={canExport}
          canImport={canImport}
          canActivate={canActivate}
          canDelete={canDelete}
          onBack={() => setSelectedContractId(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONTRACTS TAB
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// FINANCING ANALYTICS TAB — تحليل التمويل — v3.3.429
// Comprehensive financial analytics dashboard
// ═══════════════════════════════════════════════════════════════════
function FinancingAnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState({ monthly: [], projects: [] });
  const [loading, setLoading] = useState(true);
  const [contractFilter, setContractFilter] = useState("");
  const [contracts, setContracts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = contractFilter ? { contract_id: contractFilter } : {};
      const [analyticsR, scheduleR] = await Promise.all([
        sukuk.getAnalytics(params),
        sukuk.getPaymentSchedule(params),
      ]);
      setAnalytics(analyticsR.data);
      setPaymentSchedule(scheduleR.data);
      // Load contract list for filter (only on first load)
      if (contracts.length === 0) {
        const all = await sukuk.getPaymentSchedule({});
        setContracts(all.data.projects || []);
      }
    } catch (e) {
      console.error(e);
      toast.error("فشل تحميل التحليلات");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [contractFilter]);

  if (loading || !analytics) {
    return <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>جاري تحميل التحليلات...</div>;
  }

  const s = analytics.summary;
  const cost = analytics.cost_composition;

  // Chart colors
  const PIE_COLORS = ["#1e40af", "#0891b2", "#7c3aed", "#d97706", "#dc2626", "#15803d", "#92400e", "#0f766e"];

  // Cost composition pie data
  const costData = [
    { name: "الفوائد المتوقعة", value: cost.interest, fill: "#d97706" },
    { name: "رسوم إدارية", value: cost.admin_fee, fill: "#7c2d12" },
    { name: "رسوم برنامج (محتسبة)", value: Math.max(0, cost.program_fee_implicit), fill: "#6b21a8" },
  ].filter(d => d.value > 0);
  const totalCost = costData.reduce((sum, d) => sum + d.value, 0);

  // Tenor data
  const tenorData = (analytics.by_tenor || []).filter(t => t.count > 0).map((t, i) => ({
    ...t,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // Coupon data
  const couponData = (analytics.by_coupon_rate || []).filter(t => t.count > 0).map((t, i) => ({
    ...t,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // v3.3.430 — Fill missing months + English labels for charts
  const todayDate = new Date();
  const fromMonth = todayDate.toISOString().slice(0, 7);
  const toDate36 = new Date(todayDate);
  toDate36.setMonth(toDate36.getMonth() + 36);
  const toMonth = toDate36.toISOString().slice(0, 7);
  const filledMonthly = fillMissingMonths(paymentSchedule.monthly || [], fromMonth, toMonth).slice(0, 36);
  const monthly = filledMonthly.map(m => ({
    ...m,
    label: monthLabelEN(m.month),
  }));

  // Companies
  const companies = (analytics.by_company || []).slice(0, 6);

  return (
    <div>
      {/* Filter */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>📊 تحليلات شاملة:</span>
        <select
          value={contractFilter}
          onChange={(e) => setContractFilter(e.target.value)}
          style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid #cbd5e1", fontSize: 12, fontFamily: "inherit", minWidth: 240 }}
        >
          <option value="">📦 كل المشاريع</option>
          {contracts.map(c => (
            <option key={c.contract_id} value={c.contract_id}>{c.contract_name}</option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: "#64748b" }}>
          {s.total_outstanding_count} إصدار قائم · {s.total_paid_count} مسدد
        </span>
      </div>

      {/* KPIs Grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12, marginBottom: 16,
      }}>
        <KpiCard label="🔴 إجمالي المديونيات" value={fmt(s.total_outstanding_repayment)} sub="ريال (شامل الفوائد)" color="#991b1b" bg="linear-gradient(135deg, #991b1b 0%, #dc2626 100%)" lightText />
        <KpiCard label="💰 الأصل المتبقي" value={fmt(s.total_outstanding_principal)} sub="Principal" color="#1e40af" bg="#dbeafe" />
        <KpiCard label="📈 الفوائد المتوقعة" value={fmt(s.total_outstanding_interest)} sub={`${s.weighted_coupon_pct?.toFixed(2) || 0}% متوسط مرجح`} color="#92400e" bg="#fef3c7" />
        <KpiCard label="🎯 Effective Rate (XIRR)" value={s.aggregate_apr_pct !== null ? `${s.aggregate_apr_pct.toFixed(2)}%` : "—"} sub="التكلفة الفعلية السنوية" color="#7c2d12" bg="#fed7aa" />
        <KpiCard label="📊 إدارية سنوي" value={`${s.weighted_admin_annual_pct?.toFixed(2) || 0}%`} sub="متوسط مرجح" color="#0c4a6e" bg="#e0f2fe" />
        <KpiCard label="💵 صافي مستلم" value={fmt(s.total_net_received)} sub="ما تم استلامه فعلياً" color="#14532d" bg="#dcfce7" />
      </div>

      {/* Charts: Row 1 — Monthly cash flow + Cost composition */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 14 }}>
        {/* Monthly cash flow */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: "#0f172a" }}>
            💸 جدول الدفعات الشهرية المستحقة
          </div>
          {monthly.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 11 }}>لا توجد دفعات</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthly} margin={{ top: 5, right: 5, left: 5, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={Math.max(0, Math.floor(monthly.length / 18))} angle={-35} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : (v/1000).toFixed(0) + 'K'} />
                <Tooltip
                  formatter={(value, name) => [fmt(value) + ' ريال', name]}
                  contentStyle={{ direction: "rtl", fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="principal" stackId="a" fill="#15803d" name="أصل" />
                <Bar dataKey="interest" stackId="a" fill="#d97706" name="فائدة" />
                <Bar dataKey="overdue" stackId="a" fill="#dc2626" name="متأخر" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Cost composition pie */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: "#0f172a" }}>
            💼 توزيع تكلفة التمويل
          </div>
          {costData.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 11 }}>—</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={costData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={45} outerRadius={75}
                    paddingAngle={2}
                  >
                    {costData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${fmt(value)} ريال (${((value/totalCost)*100).toFixed(1)}%)`, name]}
                    contentStyle={{ direction: "rtl", fontSize: 11 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ fontSize: 10, marginTop: 6 }}>
                {costData.map((d, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ width: 8, height: 8, background: d.fill, borderRadius: 2, display: "inline-block" }}></span>
                    <span style={{ flex: 1 }}>{d.name}</span>
                    <span style={{ fontWeight: 700, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(d.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Charts: Row 2 — Tenor distribution + Coupon distribution */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        {/* Tenor */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: "#0f172a" }}>
            📅 توزيع المدد (Tenor Distribution)
          </div>
          {tenorData.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 11 }}>—</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tenorData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : (v/1000).toFixed(0) + 'K'} />
                <Tooltip
                  formatter={(value, name) => name === "principal" ? [fmt(value) + ' ريال', "الأصل"] : [value + " إصدار", "العدد"]}
                  contentStyle={{ direction: "rtl", fontSize: 11 }}
                />
                <Bar dataKey="principal" name="الأصل (ريال)">
                  {tenorData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Coupon distribution */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: "#0f172a" }}>
            🎯 توزيع نسب الكوبون
          </div>
          {couponData.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 11 }}>—</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={couponData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : (v/1000).toFixed(0) + 'K'} />
                <Tooltip
                  formatter={(value, name) => [fmt(value) + ' ريال', "الأصل"]}
                  contentStyle={{ direction: "rtl", fontSize: 11 }}
                />
                <Bar dataKey="principal" name="الأصل">
                  {couponData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Companies bar */}
      {companies.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8, color: "#0f172a" }}>
            🏢 توزيع المديونيات حسب شركة الإصدار
          </div>
          <ResponsiveContainer width="100%" height={Math.max(180, companies.length * 38 + 40)}>
            <BarChart data={companies} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : (v/1000).toFixed(0) + 'K'} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
              <Tooltip
                formatter={(value, name) => [fmt(value) + ' ريال', name]}
                contentStyle={{ direction: "rtl", fontSize: 11 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="principal" fill="#1e40af" name="الأصل" />
              <Bar dataKey="repayment" fill="#dc2626" name="إجمالي السداد" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Insights box */}
      <div style={{
        background: "#f0fdf4", border: "1px solid #16a34a", borderRadius: 10, padding: 14,
        fontSize: 12, color: "#14532d", lineHeight: 1.7,
      }}>
        <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 13 }}>💡 ملاحظات تحليلية:</div>
        <ul style={{ margin: 0, paddingRight: 20 }}>
          <li>
            <strong>التكلفة الفعلية السنوية (XIRR):</strong> {s.aggregate_apr_pct !== null ? `${s.aggregate_apr_pct.toFixed(2)}%` : "غير متاح"}
            {" "}— تشمل كل التدفقات (الأصل، الفوائد، الرسوم، توقيت الدفع)
          </li>
          <li>
            <strong>الفرق بين الكوبون و APR:</strong> الكوبون الاسمي {s.weighted_coupon_pct?.toFixed(2) || 0}% +
            إدارية سنوي {s.weighted_admin_annual_pct?.toFixed(2) || 0}% = {((Number(s.weighted_coupon_pct) || 0) + (Number(s.weighted_admin_annual_pct) || 0)).toFixed(2)}%
            {s.aggregate_apr_pct !== null && (
              <span> · APR الفعلي {s.aggregate_apr_pct.toFixed(2)}% (الفرق من رسوم البرنامج upfront)</span>
            )}
          </li>
          <li>
            <strong>عدد شركات الإصدار:</strong> {companies.length} شركة — {companies.length === 1 ? "تركز عالٍ في الموردين" : companies.length <= 2 ? "تركز متوسط" : "توزيع متوازن"}
          </li>
          <li>
            <strong>إجمالي المديونيات شامل الفوائد:</strong> {fmt(s.total_outstanding_repayment)} ريال
            {" "}({fmt(s.total_outstanding_principal)} أصل + {fmt(s.total_outstanding_interest)} فوائد)
          </li>
        </ul>
      </div>
    </div>
  );
}

// KPI card helper
function KpiCard({ label, value, sub, color, bg, lightText }) {
  const isGradient = bg && bg.includes("gradient");
  return (
    <div style={{
      background: bg || "#f8fafc",
      borderRadius: 10,
      padding: "12px 14px",
      border: isGradient ? "none" : "1px solid #e2e8f0",
      boxShadow: isGradient ? "0 4px 14px rgba(220,38,38,0.2)" : "none",
      color: lightText ? "#fff" : "#0f172a",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, opacity: lightText ? 0.9 : 0.7, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, direction: "ltr", fontVariantNumeric: "tabular-nums", color: lightText ? "#fff" : color }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 9, opacity: lightText ? 0.85 : 0.65, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SUKUK CHARTS SECTION — v3.3.429 (used in ActiveSukuksTab)
// Charts: 1) Monthly cash outflow stacked bar, 5) Project comparison
// ═══════════════════════════════════════════════════════════════════
function SukukChartsSection({ contracts, chartContractId, setChartContractId, chartPeriod, setChartPeriod, chartData, chartLoading }) {
  // Sort & limit projects for the comparison chart
  // v3.3.476 — Full-width chart now: allow more projects (was 8)
  const projectsForChart = (chartData.projects || []).slice(0, 20).map(p => ({
    name: p.contract_name && p.contract_name.length > 30 ? p.contract_name.slice(0, 28) + '…' : p.contract_name,
    fullName: p.contract_name,
    outstanding: p.total_outstanding,
    overdue: p.total_overdue,
    paid: p.total_paid,
  }));

  // v3.3.430 — Fill missing months + use English labels (avoids RTL rendering issues)
  // Determine date range from chartPeriod and current date
  const today = new Date();
  const fromMonth = today.toISOString().slice(0, 7);
  const toDate = new Date(today);
  if (chartPeriod && chartPeriod !== "0") {
    toDate.setMonth(toDate.getMonth() + parseInt(chartPeriod));
  } else if ((chartData.monthly || []).length > 0) {
    const lastMonth = chartData.monthly[chartData.monthly.length - 1].month;
    const [ly, lm] = lastMonth.split("-").map(Number);
    toDate.setFullYear(ly, lm - 1, 1);
  }
  const toMonth = toDate.toISOString().slice(0, 7);
  const filled = fillMissingMonths(chartData.monthly || [], fromMonth, toMonth);
  const monthly = filled.map(m => ({ ...m, label: monthLabelEN(m.month) }));

  return (
    <div style={{ marginBottom: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>📊 لوحة الدفعات</span>
        <select
          value={chartContractId}
          onChange={(e) => setChartContractId(e.target.value)}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12, fontFamily: "inherit", minWidth: 200 }}
        >
          <option value="">📦 كل المشاريع</option>
          {contracts.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={chartPeriod}
          onChange={(e) => setChartPeriod(e.target.value)}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12, fontFamily: "inherit" }}
        >
          <option value="6">6 أشهر قادمة</option>
          <option value="12">سنة قادمة</option>
          <option value="24">سنتان قادمتان</option>
          <option value="36">3 سنوات قادمة</option>
          <option value="0">كل المدة</option>
        </select>
        {chartLoading && <span style={{ fontSize: 11, color: "#94a3b8" }}>...جاري التحميل</span>}
      </div>

      {monthly.length === 0 && projectsForChart.length === 0 ? (
        <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 12 }}>
          لا توجد بيانات للفترة المختارة
        </div>
      ) : (
        // v3.3.476 — Stack vertically: monthly chart full-width, project comparison below
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Chart 1: Monthly stacked bar — full width */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 6 }}>
              💰 التدفقات الشهرية (الأصل + الفائدة + المتأخرات)
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthly} margin={{ top: 5, right: 5, left: 5, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(monthly.length / 24))} angle={-35} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : v >= 1000 ? (v/1000).toFixed(0) + 'K' : v} />
                <Tooltip
                  // v3.3.476 — Custom tooltip showing total + breakdown + counts
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const dataPoint = payload[0].payload || {};
                    const principal = Number(dataPoint.principal) || 0;
                    const interest = Number(dataPoint.interest) || 0;
                    const overdue = Number(dataPoint.overdue) || 0;
                    const paid = Number(dataPoint.paid) || 0;
                    const total = principal + interest + overdue;
                    return (
                      <div style={{
                        background: "#fff",
                        border: "1px solid #cbd5e1",
                        borderRadius: 8,
                        padding: "10px 14px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        direction: "rtl",
                        textAlign: "right",
                        fontSize: 11,
                        minWidth: 220,
                      }}>
                        <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e2e8f0", direction: "ltr", textAlign: "center", fontSize: 12 }}>
                          📅 {label}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#15803d", fontWeight: 700 }}>
                          <span>🟢 الأصل:</span>
                          <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(principal)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#d97706", fontWeight: 700 }}>
                          <span>🟠 الفائدة:</span>
                          <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(interest)}</span>
                        </div>
                        {overdue > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#dc2626", fontWeight: 700 }}>
                            <span>🔴 متأخرات:</span>
                            <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(overdue)}</span>
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0 2px", marginTop: 4, borderTop: "1px solid #e2e8f0", color: "#0f172a", fontWeight: 800, fontSize: 12 }}>
                          <span>💎 الإجمالي:</span>
                          <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(total)}</span>
                        </div>
                        {paid > 0 && (
                          <div style={{ marginTop: 6, paddingTop: 4, borderTop: "1px dashed #e2e8f0", color: "#16a34a", fontSize: 10, display: "flex", justifyContent: "space-between" }}>
                            <span>✅ مسدد فعلياً:</span>
                            <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(paid)}</span>
                          </div>
                        )}
                        <div style={{ marginTop: 4, fontSize: 10, color: "#64748b", textAlign: "center" }}>
                          {dataPoint.payment_count || 0} دفعة · {dataPoint.paid_count || 0} مسدد · {dataPoint.overdue_count || 0} متأخر
                        </div>
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="principal" stackId="a" fill="#15803d" name="أصل" />
                <Bar dataKey="interest" stackId="a" fill="#d97706" name="فائدة" />
                <Bar dataKey="overdue" stackId="a" fill="#dc2626" name="متأخرات" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 5: Project comparison (horizontal bar) — full width below */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 6 }}>
              🏢 مقارنة المديونيات بين المشاريع
            </div>
            <ResponsiveContainer width="100%" height={Math.max(240, projectsForChart.length * 42)}>
              <BarChart data={projectsForChart} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : (v/1000).toFixed(0) + 'K'} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                <Tooltip
                  formatter={(value, name) => [fmt(value) + ' ريال', name]}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  contentStyle={{ direction: "rtl", fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="outstanding" fill="#1e40af" name="قائم" />
                <Bar dataKey="overdue" fill="#dc2626" name="متأخر" />
                <Bar dataKey="paid" fill="#15803d" name="مسدد" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAYMENT SCHEDULE TAB — جدول السداد (v3.3.476)
// Shows every payment across all active issuances, sorted by date,
// filterable by project / year / month.
// ═══════════════════════════════════════════════════════════════════
function PaymentScheduleTab({ onSelectContract }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterContractId, setFilterContractId] = useState("");
  const [filterYear, setFilterYear] = useState(""); // "" = all
  const [filterMonth, setFilterMonth] = useState(""); // "" = all
  const [filterStatus, setFilterStatus] = useState("all"); // all | paid | unpaid | overdue

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.getPaymentSchedule({ include_payments: 1 });
      setPayments(r.data?.payments || []);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "فشل تحميل جدول السداد");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Distinct projects + years for the filter dropdowns
  const projects = useMemo(() => {
    const m = new Map();
    for (const p of payments) {
      if (!m.has(p.contract_id)) m.set(p.contract_id, p.contract_name);
    }
    return Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name), "ar"));
  }, [payments]);

  const years = useMemo(() => {
    const s = new Set();
    for (const p of payments) {
      if (p.payment_date) s.add(String(p.payment_date).slice(0, 4));
    }
    return Array.from(s).sort();
  }, [payments]);

  // Apply filters
  const filtered = useMemo(() => {
    return payments.filter(p => {
      if (filterContractId && String(p.contract_id) !== String(filterContractId)) return false;
      if (filterYear && String(p.payment_date).slice(0, 4) !== filterYear) return false;
      if (filterMonth && String(p.payment_date).slice(5, 7) !== filterMonth) return false;
      if (filterStatus === "paid" && !p.is_paid) return false;
      if (filterStatus === "unpaid" && p.is_paid) return false;
      if (filterStatus === "overdue" && (!p.is_overdue || p.is_paid)) return false;
      return true;
    }).sort((a, b) => String(a.payment_date).localeCompare(String(b.payment_date)));
  }, [payments, filterContractId, filterYear, filterMonth, filterStatus]);

  // Totals
  const totals = useMemo(() => {
    const t = { count: filtered.length, principal: 0, interest: 0, total: 0, paid: 0, paid_count: 0, overdue: 0, overdue_count: 0, upcoming: 0, upcoming_count: 0 };
    for (const p of filtered) {
      t.principal += Number(p.principal_amount) || 0;
      t.interest += Number(p.interest_amount) || 0;
      t.total += Number(p.total_amount) || 0;
      if (p.is_paid) { t.paid += Number(p.total_amount) || 0; t.paid_count++; }
      else if (p.is_overdue) { t.overdue += Number(p.total_amount) || 0; t.overdue_count++; }
      else { t.upcoming += Number(p.total_amount) || 0; t.upcoming_count++; }
    }
    return t;
  }, [filtered]);

  const MONTH_LABELS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

  // v3.3.478 — Group all payments by sukuk_id so the IssuanceCell popup can show full schedule
  // (we group from the full `payments` array, not the filtered one, to show complete issuance schedule)
  const paymentsBySukukId = useMemo(() => {
    const m = {};
    for (const p of payments) {
      if (!m[p.sukuk_id]) m[p.sukuk_id] = [];
      m[p.sukuk_id].push(p);
    }
    // Sort each issuance's payments chronologically
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => String(a.payment_date).localeCompare(String(b.payment_date)));
    }
    return m;
  }, [payments]);

  // v3.3.478 — Export to CSV (filtered rows)
  const exportCSV = () => {
    if (filtered.length === 0) {
      toast.error("لا توجد دفعات للتصدير");
      return;
    }
    const header = ["#", "الحالة", "تاريخ الاستحقاق", "المشروع", "شركة الإصدار", "الإصدار", "دفعة #", "الأصل", "الفائدة", "الإجمالي", "تاريخ السداد الفعلي"];
    const rows = filtered.map((p, idx) => {
      const status = p.is_paid ? "مسدد" : (p.is_overdue ? "متأخر" : "قادم");
      return [
        idx + 1,
        status,
        fmtDateDMY(p.payment_date),
        p.contract_name || "",
        p.issuing_company || "",
        p.sukuk_sequence_number || p.sukuk_id,
        p.sequence_number || "",
        p.principal_amount || 0,
        p.interest_amount || 0,
        p.total_amount || 0,
        p.actual_payment_date ? fmtDateDMY(p.actual_payment_date) : "",
      ];
    });
    // Totals row
    rows.push(["", "الإجمالي", "", "", "", "", `${filtered.length} دفعة`, totals.principal, totals.interest, totals.total, ""]);
    const csvContent = [header, ...rows]
      .map(r => r.map(c => {
        const s = String(c ?? "");
        if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
        return s;
      }).join(","))
      .join("\n");
    // Add UTF-8 BOM for Excel to recognize Arabic encoding
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `جدول_السداد_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`تم تصدير ${filtered.length} دفعة`);
  };

  // v3.3.478 — Export to PDF via window.print (RTL-friendly)
  const exportPDF = () => {
    if (filtered.length === 0) {
      toast.error("لا توجد دفعات للتصدير");
      return;
    }
    const filterDesc = [];
    if (filterContractId) {
      const p = projects.find(x => String(x.id) === String(filterContractId));
      filterDesc.push(`المشروع: ${p?.name || filterContractId}`);
    }
    if (filterYear) filterDesc.push(`السنة: ${filterYear}`);
    if (filterMonth) filterDesc.push(`الشهر: ${MONTH_LABELS_AR[parseInt(filterMonth, 10) - 1] || filterMonth}`);
    if (filterStatus !== "all") {
      const labels = { paid: "مسدد", unpaid: "غير مسدد", overdue: "متأخر" };
      filterDesc.push(`الحالة: ${labels[filterStatus] || filterStatus}`);
    }

    const win = window.open("", "_blank");
    if (!win) { toast.error("الرجاء السماح بالنوافذ المنبثقة لإصدار PDF"); return; }
    const rowsHtml = filtered.map((p, idx) => {
      const status = p.is_paid ? "مسدد ✅" : (p.is_overdue ? "متأخر 🔴" : "قادم ⏳");
      const statusBg = p.is_paid ? "#dcfce7" : (p.is_overdue ? "#fee2e2" : "#cffafe");
      return `
        <tr>
          <td style="text-align:center">${idx + 1}</td>
          <td style="text-align:center;background:${statusBg};font-weight:700">${status}</td>
          <td style="text-align:center;direction:ltr">${fmtDateDMY(p.payment_date)}</td>
          <td>${p.contract_name || ""}<br><small style="color:#64748b">${p.issuing_company || ""}</small></td>
          <td style="text-align:center">#${p.sukuk_sequence_number || p.sukuk_id}</td>
          <td style="text-align:center">${p.sequence_number || ""}</td>
          <td style="text-align:left;direction:ltr;color:#15803d;font-weight:700">${Number(p.principal_amount) > 0 ? fmt(p.principal_amount) : "—"}</td>
          <td style="text-align:left;direction:ltr;color:#b45309;font-weight:700">${Number(p.interest_amount) > 0 ? fmt(p.interest_amount) : "—"}</td>
          <td style="text-align:left;direction:ltr;font-weight:800">${fmt(p.total_amount)}</td>
          <td style="text-align:center;direction:ltr;color:${p.actual_payment_date ? '#16a34a' : '#cbd5e1'}">${p.actual_payment_date ? fmtDateDMY(p.actual_payment_date) : "—"}</td>
        </tr>`;
    }).join("");

    win.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="utf-8">
  <title>جدول السداد — ${new Date().toLocaleDateString("ar-SA")}</title>
  <style>
    body { font-family: "Tahoma", "Arial", sans-serif; padding: 20px; direction: rtl; }
    h1 { color: #b45309; font-size: 22px; margin: 0 0 4px; }
    .meta { color: #64748b; font-size: 11px; margin-bottom: 10px; }
    .filters { background: #fef3c7; padding: 8px 12px; border-radius: 6px; font-size: 11px; margin-bottom: 12px; display: ${filterDesc.length ? "block" : "none"}; }
    .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-bottom: 14px; }
    .kpi { background: linear-gradient(135deg,#92400e,#b45309); color:#fff; padding:8px 10px; border-radius:6px; }
    .kpi .label { font-size: 10px; opacity: .9; }
    .kpi .value { font-size: 16px; font-weight: 800; direction: ltr; text-align: right; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { background: #f8fafc; padding: 6px 4px; border: 1px solid #cbd5e1; font-size: 10px; }
    td { padding: 5px 4px; border-bottom: 1px solid #f1f5f9; font-size: 10px; }
    tfoot tr { background: #0f172a !important; color: #fff; font-weight: 800; }
    tfoot td { color: #fff !important; padding: 7px 4px; }
    @media print { body { padding: 8px; } }
  </style>
</head>
<body>
  <h1>📅 جدول السداد</h1>
  <div class="meta">تاريخ التصدير: ${new Date().toLocaleString("ar-SA")} · إجمالي الدفعات: ${filtered.length} من أصل ${payments.length}</div>
  ${filterDesc.length ? `<div class="filters"><strong>الفلاتر:</strong> ${filterDesc.join(" · ")}</div>` : ""}
  <div class="kpis">
    <div class="kpi"><div class="label">📅 إجمالي الدفعات</div><div class="value">${totals.count}</div></div>
    <div class="kpi"><div class="label">💎 الإجمالي</div><div class="value">${fmt(totals.total)}</div></div>
    <div class="kpi"><div class="label">✅ مسدد</div><div class="value">${fmt(totals.paid)}</div></div>
    <div class="kpi"><div class="label">🔴 متأخر</div><div class="value">${fmt(totals.overdue)}</div></div>
    <div class="kpi"><div class="label">⏳ قادم</div><div class="value">${fmt(totals.upcoming)}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>الحالة</th><th>تاريخ الاستحقاق</th><th>المشروع / العقد</th>
        <th>الإصدار</th><th>دفعة #</th><th>الأصل</th><th>الفائدة</th><th>الإجمالي</th><th>السداد الفعلي</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot>
      <tr>
        <td colspan="6" style="text-align:center">الإجمالي (${filtered.length} دفعة)</td>
        <td style="text-align:left;direction:ltr">${fmt(totals.principal)}</td>
        <td style="text-align:left;direction:ltr">${fmt(totals.interest)}</td>
        <td style="text-align:left;direction:ltr">${fmt(totals.total)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <script>setTimeout(() => window.print(), 250);</script>
</body>
</html>`);
    win.document.close();
    toast.success("جاري إنشاء PDF — اختر 'حفظ كـ PDF' في نافذة الطباعة");
  };

  return (
    <div>
      {/* Hero KPI strip */}
      <div style={{
        marginBottom: 14, padding: "16px 20px",
        background: "linear-gradient(135deg, #92400e 0%, #b45309 100%)",
        borderRadius: 12, color: "#fff",
        boxShadow: "0 4px 14px rgba(180, 83, 9, 0.25)",
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14,
      }}>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>📅 إجمالي الدفعات</div>
          <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, direction: "ltr", textAlign: "right" }}>{totals.count}</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>دفعة في النطاق المُحدَّد</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>💎 إجمالي المبلغ</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(totals.total)}</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>أصل {fmt(totals.principal)} + فائدة {fmt(totals.interest)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>✅ مسدد</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(totals.paid)}</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{totals.paid_count} دفعة</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>🔴 متأخر</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(totals.overdue)}</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{totals.overdue_count} دفعة</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⏳ قادم</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(totals.upcoming)}</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{totals.upcoming_count} دفعة</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap", padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>🔍 الفلاتر:</span>
        <select
          value={filterContractId}
          onChange={(e) => setFilterContractId(e.target.value)}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12, fontFamily: "inherit", minWidth: 200 }}
        >
          <option value="">📦 كل المشاريع</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12, fontFamily: "inherit" }}
        >
          <option value="">📅 كل السنوات</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12, fontFamily: "inherit" }}
        >
          <option value="">🗓️ كل الأشهر</option>
          {MONTH_LABELS_AR.map((m, i) => (
            <option key={i} value={String(i + 1).padStart(2, "0")}>{String(i + 1).padStart(2, "0")} · {m}</option>
          ))}
        </select>
        <div style={{ display: "flex", gap: 4, background: "#fff", padding: 3, borderRadius: 8, border: "1px solid #cbd5e1" }}>
          <button onClick={() => setFilterStatus("all")} style={{
            padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
            background: filterStatus === "all" ? "#0f172a" : "transparent",
            color: filterStatus === "all" ? "#fff" : "#475569",
          }}>الكل</button>
          <button onClick={() => setFilterStatus("unpaid")} style={{
            padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
            background: filterStatus === "unpaid" ? "#0891b2" : "transparent",
            color: filterStatus === "unpaid" ? "#fff" : "#475569",
          }}>⏳ غير مسدد</button>
          <button onClick={() => setFilterStatus("overdue")} style={{
            padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
            background: filterStatus === "overdue" ? "#dc2626" : "transparent",
            color: filterStatus === "overdue" ? "#fff" : "#475569",
          }}>🔴 متأخر</button>
          <button onClick={() => setFilterStatus("paid")} style={{
            padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
            background: filterStatus === "paid" ? "#16a34a" : "transparent",
            color: filterStatus === "paid" ? "#fff" : "#475569",
          }}>✅ مسدد</button>
        </div>
        {(filterContractId || filterYear || filterMonth || filterStatus !== "all") && (
          <button
            onClick={() => { setFilterContractId(""); setFilterYear(""); setFilterMonth(""); setFilterStatus("all"); }}
            style={{ padding: "5px 12px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
          >🔄 إعادة تعيين</button>
        )}
        <div style={{ marginRight: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            style={{ padding: "6px 12px", background: filtered.length > 0 ? "#16a34a" : "#cbd5e1", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: filtered.length > 0 ? "pointer" : "not-allowed" }}
            title="تصدير النتائج المفلترة لملف Excel/CSV"
          >📊 Excel</button>
          <button
            onClick={exportPDF}
            disabled={filtered.length === 0}
            style={{ padding: "6px 12px", background: filtered.length > 0 ? "#dc2626" : "#cbd5e1", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: filtered.length > 0 ? "pointer" : "not-allowed" }}
            title="تصدير النتائج المفلترة لملف PDF"
          >📄 PDF</button>
          <span style={{ fontSize: 11, color: "#64748b", marginRight: 6 }}>
            {loading ? "...جاري التحميل" : `عرض ${filtered.length} من أصل ${payments.length}`}
          </span>
        </div>
      </div>

      {/* Payments table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>
          لا توجد دفعات تطابق الفلاتر
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                <th style={th}>#</th>
                <th style={th}>الحالة</th>
                <th style={th}>تاريخ الاستحقاق</th>
                <th style={th}>المشروع / العقد</th>
                <th style={th}>الإصدار</th>
                <th style={th}>دفعة #</th>
                <th style={{ ...th, textAlign: "left" }}>الأصل</th>
                <th style={{ ...th, textAlign: "left" }}>الفائدة</th>
                <th style={{ ...th, textAlign: "left" }}>الإجمالي</th>
                <th style={th}>تاريخ السداد الفعلي</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => {
                const statusInfo = p.is_paid
                  ? { label: "مسدد", color: "#16a34a", bg: "#dcfce7", icon: "✅" }
                  : p.is_overdue
                    ? { label: "متأخر", color: "#dc2626", bg: "#fee2e2", icon: "🔴" }
                    : { label: "قادم", color: "#0891b2", bg: "#cffafe", icon: "⏳" };
                return (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ ...td, color: "#64748b", textAlign: "center" }}>{idx + 1}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{ background: statusInfo.bg, color: statusInfo.color, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" }}>
                        {statusInfo.icon} {statusInfo.label}
                      </span>
                    </td>
                    <td style={{ ...td, direction: "ltr", textAlign: "center", color: p.is_overdue && !p.is_paid ? "#dc2626" : "#0f172a", fontWeight: 700 }}>
                      {fmtDateDMY(p.payment_date)}
                    </td>
                    <td style={{ ...td, fontWeight: 700, color: "#1e40af", cursor: onSelectContract ? "pointer" : "default" }}
                        onClick={() => onSelectContract && onSelectContract(p.contract_id)}
                        title={onSelectContract ? "اضغط لفتح العقد" : ""}>
                      {p.contract_name || "—"}
                      {p.issuing_company && <div style={{ fontSize: 9, color: "#64748b", fontWeight: 400, marginTop: 1 }}>{p.issuing_company}</div>}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <IssuanceCell payment={p} issuancePayments={paymentsBySukukId[p.sukuk_id]} onSelectContract={onSelectContract} />
                    </td>
                    <td style={{ ...td, textAlign: "center", color: "#64748b" }}>{p.sequence_number || "—"}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(p.principal_amount) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>
                      {Number(p.principal_amount) > 0 ? fmt(p.principal_amount) : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(p.interest_amount) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>
                      {Number(p.interest_amount) > 0 ? fmt(p.interest_amount) : "—"}
                    </td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0f172a", fontWeight: 800 }}>
                      {fmt(p.total_amount)}
                    </td>
                    <td style={{ ...td, direction: "ltr", textAlign: "center", color: p.actual_payment_date ? "#16a34a" : "#cbd5e1" }}>
                      {p.actual_payment_date ? fmtDateDMY(p.actual_payment_date) : "—"}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr style={{ background: "#0f172a", color: "#fff", fontWeight: 800 }}>
                <td colSpan={6} style={{ ...td, color: "#fff", textAlign: "center", fontSize: 12 }}>الإجمالي ({filtered.length} دفعة)</td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#86efac" }}>{fmt(totals.principal)}</td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#fbbf24" }}>{fmt(totals.interest)}</td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#fff", fontSize: 13 }}>{fmt(totals.total)}</td>
                <td style={td}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// v3.3.478 — Cell that shows issuance # with hover popup (full payment schedule of that issuance)
// and click navigation to the contract details page
function IssuanceCell({ payment, issuancePayments, onSelectContract }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  // Show contract-local sukuk seq# (e.g. 1, 2, 3) — falls back to DB sukuk_id if seq missing
  const displaySeq = payment.sukuk_sequence_number || payment.sukuk_id;
  const allForThisIssuance = issuancePayments || [];

  const handleEnter = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const popupWidth = 480;
      let left = r.left + r.width / 2 - popupWidth / 2;
      if (left < 8) left = 8;
      if (left + popupWidth > window.innerWidth - 8) left = window.innerWidth - popupWidth - 8;
      setPos({
        top: r.bottom + window.scrollY + 4,
        left: left + window.scrollX,
      });
    }
    setShow(true);
  };
  const handleLeave = () => setShow(false);

  // Aggregate totals for this issuance
  const issuanceTotals = useMemo(() => {
    return allForThisIssuance.reduce((acc, p) => {
      acc.principal += Number(p.principal_amount) || 0;
      acc.interest += Number(p.interest_amount) || 0;
      acc.total += Number(p.total_amount) || 0;
      if (p.is_paid) acc.paid_count++;
      else if (p.is_overdue) acc.overdue_count++;
      else acc.upcoming_count++;
      return acc;
    }, { principal: 0, interest: 0, total: 0, paid_count: 0, overdue_count: 0, upcoming_count: 0 });
  }, [allForThisIssuance]);

  const popupNode = show && allForThisIssuance.length > 0 ? (
    <div
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        zIndex: 99999,
        width: 480,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={handleLeave}
    >
      <div style={{
        background: "#fff",
        border: "1px solid #93c5fd",
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(30, 64, 175, 0.30)",
        padding: "12px 14px",
        direction: "rtl",
        textAlign: "right",
        fontSize: 11,
        color: "#0f172a",
      }}>
        <div style={{ fontWeight: 800, color: "#1e40af", marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid #dbeafe", fontSize: 12 }}>
          📋 الإصدار #{displaySeq} — {allForThisIssuance.length} دفعة
          <div style={{ fontSize: 10, fontWeight: 500, color: "#64748b", marginTop: 2 }}>
            {payment.contract_name}
            {payment.issuing_company && ` · ${payment.issuing_company}`}
          </div>
        </div>
        {/* Mini-summary strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 8, fontSize: 10 }}>
          <div style={{ background: "#dcfce7", color: "#15803d", padding: "4px 6px", borderRadius: 4, textAlign: "center", fontWeight: 700 }}>
            ✅ {issuanceTotals.paid_count} مسدد
          </div>
          <div style={{ background: "#fee2e2", color: "#dc2626", padding: "4px 6px", borderRadius: 4, textAlign: "center", fontWeight: 700 }}>
            🔴 {issuanceTotals.overdue_count} متأخر
          </div>
          <div style={{ background: "#cffafe", color: "#0891b2", padding: "4px 6px", borderRadius: 4, textAlign: "center", fontWeight: 700 }}>
            ⏳ {issuanceTotals.upcoming_count} قادم
          </div>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
          <thead>
            <tr style={{ background: "#eff6ff", color: "#1e40af" }}>
              <th style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>#</th>
              <th style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>التاريخ</th>
              <th style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>الحالة</th>
              <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>الأصل</th>
              <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>الفائدة</th>
              <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {allForThisIssuance.map((p, idx) => {
              const pAmt = Number(p.principal_amount) || 0;
              const iAmt = Number(p.interest_amount) || 0;
              const tot = Number(p.total_amount) || 0;
              const statusBg = p.is_paid ? "#dcfce7" : (p.is_overdue ? "#fee2e2" : "#cffafe");
              const statusColor = p.is_paid ? "#15803d" : (p.is_overdue ? "#dc2626" : "#0891b2");
              const statusIcon = p.is_paid ? "✅" : (p.is_overdue ? "🔴" : "⏳");
              // Highlight the row that the user is currently looking at
              const isCurrent = String(p.payment_date) === String(payment.payment_date) && p.sequence_number === payment.sequence_number;
              return (
                <tr key={idx} style={{ borderBottom: idx < allForThisIssuance.length - 1 ? "1px solid #f1f5f9" : "none", background: isCurrent ? "#fefce8" : undefined }}>
                  <td style={{ padding: "3px 6px", textAlign: "center", color: "#64748b" }}>{p.sequence_number || idx + 1}</td>
                  <td style={{ padding: "3px 6px", textAlign: "center", direction: "ltr", color: "#0f172a" }}>{fmtDateDMY(p.payment_date)}</td>
                  <td style={{ padding: "3px 6px", textAlign: "center" }}>
                    <span style={{ background: statusBg, color: statusColor, padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700 }}>{statusIcon}</span>
                  </td>
                  <td style={{ padding: "3px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: pAmt > 0 ? "#15803d" : "#cbd5e1", fontWeight: pAmt > 0 ? 700 : 400 }}>
                    {pAmt > 0 ? fmt(pAmt) : "—"}
                  </td>
                  <td style={{ padding: "3px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: iAmt > 0 ? "#b45309" : "#cbd5e1", fontWeight: iAmt > 0 ? 700 : 400 }}>
                    {iAmt > 0 ? fmt(iAmt) : "—"}
                  </td>
                  <td style={{ padding: "3px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0f172a", fontWeight: 700 }}>
                    {fmt(tot)}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: "#eff6ff", color: "#1e40af", fontWeight: 800, borderTop: "2px solid #93c5fd" }}>
              <td colSpan={3} style={{ padding: "4px 6px", textAlign: "center" }}>الإجمالي</td>
              <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(issuanceTotals.principal)}</td>
              <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(issuanceTotals.interest)}</td>
              <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(issuanceTotals.total)}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px dashed #dbeafe", fontSize: 10, color: "#1e40af", textAlign: "center", fontWeight: 600 }}>
          👆 اضغط على رقم الإصدار للانتقال لتفاصيل العقد
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={() => onSelectContract && onSelectContract(payment.contract_id)}
        style={{
          display: "inline-block",
          padding: "3px 10px",
          background: show ? "#1e40af" : "#eff6ff",
          color: show ? "#fff" : "#1e40af",
          border: "1px solid #93c5fd",
          borderRadius: 4,
          fontWeight: 800,
          cursor: "pointer",
          fontSize: 11,
          transition: "background 0.15s, color 0.15s",
        }}
        title="مرّر لعرض جدول الإصدار · اضغط للانتقال لتفاصيل العقد"
      >
        #{displaySeq}
      </span>
      {popupNode && createPortal(popupNode, document.body)}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ACTIVE SUKUKS TAB — الإصدارات القائمة (المديونيات النشطة) — v3.3.420
// ═══════════════════════════════════════════════════════════════════
function ActiveSukuksTab({ canEdit, onSelectContract }) {
  const [data, setData] = useState({ sukuks: [], summary: {} });
  const [loading, setLoading] = useState(true);
  // v3.3.463 — Default: show ALL sukuks (was "active") so paid sukuks visible by default
  const [statusFilter, setStatusFilter] = useState("all"); // active | paid | all
  const [contractFilter, setContractFilter] = useState(""); // contract name search
  const [markingId, setMarkingId] = useState(null);
  // v3.3.427 — Expanded rows for showing per-payment details
  const [expandedSukukId, setExpandedSukukId] = useState(null);
  // v3.3.429 — Chart filters
  const [chartContractId, setChartContractId] = useState(""); // empty = all
  // v3.3.463 — Default: full period (was "12" months)
  const [chartPeriod, setChartPeriod] = useState("0"); // months: 6, 12, 24, 36, 0=all
  const [chartData, setChartData] = useState({ monthly: [], projects: [] });
  const [chartLoading, setChartLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.listActiveSukuks({ status: statusFilter });
      setData(r.data);
    } catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  // Load chart data based on filters
  const loadChartData = async () => {
    setChartLoading(true);
    try {
      const params = {};
      if (chartContractId) params.contract_id = chartContractId;
      if (chartPeriod && chartPeriod !== "0") {
        const today = new Date();
        const fromMonth = today.toISOString().slice(0, 7) + "-01";
        const toDate = new Date(today);
        toDate.setMonth(toDate.getMonth() + parseInt(chartPeriod));
        params.from_date = fromMonth;
        params.to_date = toDate.toISOString().slice(0, 10);
      }
      const r = await sukuk.getPaymentSchedule(params);
      setChartData(r.data);
    } catch (e) { console.error(e); }
    finally { setChartLoading(false); }
  };
  useEffect(() => { loadChartData(); }, [chartContractId, chartPeriod]);

  // Client-side contract name filtering
  // v3.3.469 — Filter sukuks by BOTH:
  //   1. contractFilter (text search in box)
  //   2. chartContractId (project dropdown) — was only filtering chart, now filters table too
  const filtered = data.sukuks.filter(s => {
    // First: dropdown filter (exact contract match)
    if (chartContractId && String(s.contract_id) !== String(chartContractId)) return false;
    // Then: text search filter
    if (!contractFilter.trim()) return true;
    const q = contractFilter.trim().toLowerCase();
    return (
      String(s.contract_name || "").toLowerCase().includes(q) ||
      String(s.issuing_company || "").toLowerCase().includes(q)
    );
  });

  const markRepaid = async (sukukId) => {
    const today = new Date().toISOString().slice(0, 10);
    const dateInput = window.prompt("تاريخ السداد (YYYY-MM-DD):", today);
    if (!dateInput) return;
    setMarkingId(sukukId);
    try {
      await sukuk.markRepaid(sukukId, { actual_repayment_date: dateInput });
      toast.success("تم تسجيل السداد");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التحديث");
    } finally { setMarkingId(null); }
  };

  // v3.3.425 — Edit activation date
  const editActivationDate = async (s) => {
    const current = s.actual_issue_date ? String(s.actual_issue_date).slice(0, 10) : new Date().toISOString().slice(0, 10);
    const dateInput = window.prompt(
      `تعديل تاريخ تفعيل الصك #${s.sequence_number}\n\nالتاريخ الحالي: ${current}\nأدخل التاريخ الجديد (YYYY-MM-DD):`,
      current
    );
    if (!dateInput || dateInput === current) return;
    setMarkingId(s.id);
    try {
      await sukuk.activateSukuk(s.id, { actual_issue_date: dateInput });
      toast.success("تم تحديث تاريخ التفعيل");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التحديث");
    } finally { setMarkingId(null); }
  };

  // v3.3.425 — Deactivate (revert to pending)
  const deactivate = async (s) => {
    if (!window.confirm(
      `⚠️ هل أنت متأكد من إلغاء تفعيل الصك #${s.sequence_number}؟\n\n` +
      `سيتم:\n` +
      `  • إرجاع الصك إلى حالة "قيد الانتظار"\n` +
      `  • مسح تاريخ التفعيل والاستحقاق\n` +
      `  • إخراجه من قائمة المديونيات القائمة`
    )) return;
    setMarkingId(s.id);
    try {
      await sukuk.deactivateSukuk(s.id);
      toast.success("تم إلغاء التفعيل");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإلغاء");
    } finally { setMarkingId(null); }
  };

  // v3.3.425 — Unmark repaid (revert paid → active)
  const unmarkRepaid = async (s) => {
    if (!window.confirm(
      `هل تريد إلغاء تسجيل السداد للصك #${s.sequence_number}؟\n\n` +
      `سيعود الصك لحالة "قائم" ويظهر مرة أخرى في المديونيات.`
    )) return;
    setMarkingId(s.id);
    try {
      await sukuk.unmarkRepaid(s.id);
      toast.success("تم إلغاء السداد");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل");
    } finally { setMarkingId(null); }
  };

  // Group by contract for visual structure
  const byContract = {};
  for (const s of filtered) {
    const key = s.contract_id;
    if (!byContract[key]) byContract[key] = { contract: { id: s.contract_id, name: s.contract_name, issuing_company: s.issuing_company }, sukuks: [] };
    byContract[key].sukuks.push(s);
  }
  const groups = Object.values(byContract).sort((a, b) => String(a.contract.name).localeCompare(String(b.contract.name)));

  // v3.3.463/466 — Compute summary respecting project (contract) filter
  // The hero bar should reflect the contract filter (but NOT the period filter)
  // v3.3.466 — Also compute "paid" metrics separately when filtering by project
  const summary = React.useMemo(() => {
    const base = data.summary || {};
    if (!chartContractId) return base;  // no filter → use full backend summary

    const filteredByContract = data.sukuks.filter(s => String(s.contract_id) === String(chartContractId));
    const outstandingSukuks = filteredByContract.filter(s => (s.repayment_status || 'pending') !== 'paid');
    const paidSukuks = filteredByContract.filter(s => (s.repayment_status || 'pending') === 'paid');

    // OUTSTANDING totals
    const total_outstanding_principal = outstandingSukuks.reduce((sum, s) => sum + (Number(s.principal) || 0) - (Number(s.paid_amount) || 0), 0);
    const total_outstanding_interest = outstandingSukuks.reduce((sum, s) => sum + (Number(s.total_interest) || Number(s.total_interest_amount) || Number(s.interest) || 0), 0);
    const total_outstanding_repayment = total_outstanding_principal + total_outstanding_interest;

    // v3.3.466 — PAID totals (separate KPI cards)
    const total_paid_principal = paidSukuks.reduce((sum, s) => sum + (Number(s.principal) || 0), 0);
    const total_paid_interest = paidSukuks.reduce((sum, s) => sum + (Number(s.total_interest) || Number(s.total_interest_amount) || Number(s.interest) || 0), 0);
    const total_paid_repayment = total_paid_principal + total_paid_interest;

    // Aggregate APR: weighted avg by principal (across ALL sukuks of project — both outstanding & paid)
    const aprBase = filteredByContract.filter(s => Number(s.apr_pct) > 0);
    const aprSum = aprBase.reduce((sum, s) => sum + (Number(s.apr_pct || 0) * Number(s.principal || 0)), 0);
    const principalSum = aprBase.reduce((sum, s) => sum + Number(s.principal || 0), 0);
    const aggregate_apr_pct = principalSum > 0 ? aprSum / principalSum : null;

    return {
      ...base,
      total_outstanding_principal,
      total_outstanding_interest,
      total_outstanding_repayment,
      total_outstanding_count: outstandingSukuks.length,
      // v3.3.466 — Paid KPIs
      total_paid_principal,
      total_paid_interest,
      total_paid_repayment,
      total_paid_count: paidSukuks.length,
      aggregate_apr_pct,
    };
  }, [data, chartContractId]);

  return (
    <div>
      {/* Hero Box: Outstanding debt summary */}
      {(summary.total_outstanding_count > 0 || summary.total_paid_count > 0) && (
        <>
        {/* v3.3.466 — RED: Outstanding sukuks */}
        {summary.total_outstanding_count > 0 && (
        <div style={{
          marginBottom: chartContractId && summary.total_paid_count > 0 ? 10 : 14, padding: "16px 20px",
          background: "linear-gradient(135deg, #991b1b 0%, #dc2626 100%)",
          borderRadius: 12, color: "#fff",
          boxShadow: "0 4px 14px rgba(220,38,38,0.25)",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14,
        }}>
          {/* v3.3.463 — Right-aligned (RTL) for all KPI cells */}
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, letterSpacing: "0.5px" }}>🔴 إجمالي المديونيات القائمة</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
              {fmt(summary.total_outstanding_repayment || 0)}
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              ريال (شامل الفوائد) · {summary.total_outstanding_count} إصدار قائم
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>💰 الأصل القائم</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
              {fmt(summary.total_outstanding_principal || 0)}
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>ريال</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>📈 الفوائد على القائم</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
              {fmt(summary.total_outstanding_interest || 0)}
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>ريال</div>
          </div>
          {summary.aggregate_apr_pct !== null && summary.aggregate_apr_pct !== undefined && (
            <div style={{ borderRight: "1px solid rgba(255,255,255,0.3)", paddingRight: 12, textAlign: "right" }}>
              <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>🎯 Effective Rate المجمّع</div>
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                {summary.aggregate_apr_pct.toFixed(2)}%
              </div>
              <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
                XIRR على كل التدفقات القائمة
              </div>
            </div>
          )}
        </div>
        )}

        {/* v3.3.466 — GREEN: Paid sukuks (only shown when a project is filtered AND there are paid sukuks) */}
        {chartContractId && summary.total_paid_count > 0 && (
        <div style={{
          marginBottom: 14, padding: "16px 20px",
          background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
          borderRadius: 12, color: "#fff",
          boxShadow: "0 4px 14px rgba(22,163,74,0.25)",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14,
        }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, letterSpacing: "0.5px" }}>✅ إجمالي المسدّد</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
              {fmt(summary.total_paid_repayment || 0)}
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              ريال (شامل الفوائد) · {summary.total_paid_count} إصدار مسدّد
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>💰 الأصل المسدّد</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
              {fmt(summary.total_paid_principal || 0)}
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>ريال</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>💵 الفوائد المدفوعة</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, direction: "ltr", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
              {fmt(summary.total_paid_interest || 0)}
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>ريال (تكلفة فعلية مكتملة)</div>
          </div>
        </div>
        )}
        </>
      )}

      {/* v3.3.429 — Chart filters + charts */}
      <SukukChartsSection
        contracts={Array.from(new Map(data.sukuks.map(s => [s.contract_id, { id: s.contract_id, name: s.contract_name }])).values())}
        chartContractId={chartContractId}
        setChartContractId={setChartContractId}
        chartPeriod={chartPeriod}
        setChartPeriod={setChartPeriod}
        chartData={chartData}
        chartLoading={chartLoading}
      />

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4, background: "#f1f5f9", padding: 3, borderRadius: 8 }}>
          <button onClick={() => setStatusFilter("active")} style={{
            padding: "6px 14px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
            background: statusFilter === "active" ? "#dc2626" : "transparent",
            color: statusFilter === "active" ? "#fff" : "#475569",
          }}>🔴 قائمة</button>
          <button onClick={() => setStatusFilter("paid")} style={{
            padding: "6px 14px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
            background: statusFilter === "paid" ? "#16a34a" : "transparent",
            color: statusFilter === "paid" ? "#fff" : "#475569",
          }}>✅ مسددة</button>
          <button onClick={() => setStatusFilter("all")} style={{
            padding: "6px 14px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
            background: statusFilter === "all" ? "#0f172a" : "transparent",
            color: statusFilter === "all" ? "#fff" : "#475569",
          }}>الكل</button>
        </div>
        <input
          type="text"
          placeholder="🔍 ابحث باسم العقد أو الشركة..."
          value={contractFilter}
          onChange={(e) => setContractFilter(e.target.value)}
          style={{
            padding: "7px 12px", border: "1px solid #cbd5e1", borderRadius: 7,
            fontSize: 12, fontFamily: "inherit", minWidth: 240, flex: "1 1 240px",
          }}
        />
        <div style={{ fontSize: 11, color: "#64748b" }}>
          {filtered.length} من {data.sukuks.length} إصدار
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 30, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b" }}>
          {statusFilter === "active" ? "لا توجد إصدارات قائمة حالياً" :
           statusFilter === "paid" ? "لا توجد إصدارات مسددة" :
           "لا توجد إصدارات مفعّلة"}
        </div>
      ) : (
        groups.map(g => (
          <div key={g.contract.id} style={{ marginBottom: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            {/* Group header */}
            <div
              onClick={() => onSelectContract && onSelectContract(g.contract.id)}
              style={{
                padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
                cursor: onSelectContract ? "pointer" : "default",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>📜 {g.contract.name}</div>
                {g.contract.issuing_company && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>🏢 {g.contract.issuing_company}</div>}
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>
                {g.sukuks.length} إصدار · {fmt(g.sukuks.reduce((s, x) => s + (x.is_outstanding ? x.total_repayment : 0), 0))} قائم
              </div>
            </div>
            {/* Sukuks table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={tdHead}></th>{/* expand toggle */}
                    <th style={tdHead}>#</th>
                    <th style={tdHead}>الحالة</th>
                    <th style={tdHead}>الأصل</th>
                    <th style={tdHead}>الصافي</th>
                    <th style={tdHead}>الفائدة</th>
                    <th style={{ ...tdHead, textAlign: "left" }}>إجمالي السداد</th>
                    <th style={tdHead}>الدفعات</th>
                    <th style={tdHead}>تاريخ الإصدار</th>
                    <th style={tdHead}>الاستحقاق</th>
                    <th style={tdHead}>المدة المتبقية</th>
                    {canEdit && <th style={tdHead}>إجراءات</th>}
                  </tr>
                </thead>
                <tbody>
                  {g.sukuks.map(s => {
                    const isPaid = s.repayment_status === 'paid' || s.status === 'matured';
                    const ps = s.payment_summary;
                    const isExpanded = expandedSukukId === s.id;
                    // Status logic with payment-aware states
                    let statusBadge;
                    if (isPaid) {
                      statusBadge = <span style={{ padding: "2px 8px", background: "#dcfce7", color: "#15803d", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>✅ مسدد</span>;
                    } else if (ps?.overdue_count > 0) {
                      statusBadge = <span style={{ padding: "2px 8px", background: "#fee2e2", color: "#991b1b", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>⚠ متأخر ({ps.overdue_count})</span>;
                    } else if (ps?.paid_count > 0) {
                      statusBadge = <span style={{ padding: "2px 8px", background: "#dbeafe", color: "#1e40af", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>🔵 جاري</span>;
                    } else {
                      statusBadge = <span style={{ padding: "2px 8px", background: "#fef3c7", color: "#92400e", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>🔴 قائم</span>;
                    }
                    return (
                      <React.Fragment key={s.id}>
                      <tr style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isPaid ? "#f0fdf4" : (ps?.overdue_count > 0 ? "#fef2f2" : "#fff"),
                      }}>
                        <td style={{ ...td, textAlign: "center", padding: "4px 6px" }}>
                          <button
                            onClick={() => setExpandedSukukId(isExpanded ? null : s.id)}
                            title={isExpanded ? "إخفاء الدفعات" : "عرض الدفعات"}
                            style={{
                              width: 22, height: 22, padding: 0,
                              background: isExpanded ? "#0891b2" : "#f1f5f9",
                              color: isExpanded ? "#fff" : "#475569",
                              border: "none", borderRadius: 4, cursor: "pointer",
                              fontSize: 10, fontWeight: 700,
                            }}
                          >{isExpanded ? "▼" : "▶"}</button>
                        </td>
                        <td style={{ ...td, fontWeight: 700, direction: "ltr" }}>#{s.sequence_number}</td>
                        <td style={td}>{statusBadge}</td>
                        <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{fmt(s.principal)}</td>
                        <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0891b2" }}>{fmt(s.net_received)}</td>
                        <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#15803d" }}>{fmt(s.total_interest)}</td>
                        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#dc2626", fontWeight: 700 }}>{fmt(s.total_repayment)}</td>
                        <td style={{ ...td, fontSize: 10, color: "#475569", direction: "ltr", textAlign: "center" }}>
                          {ps ? (
                            <>
                              <span style={{ color: "#15803d", fontWeight: 700 }}>{ps.paid_count}</span>
                              <span style={{ color: "#94a3b8" }}> / {ps.total_count}</span>
                            </>
                          ) : "—"}
                        </td>
                        <td style={{ ...td, color: "#475569" }}>{s.actual_issue_date ? fmtDateDMY(s.actual_issue_date) : "—"}</td>
                        <td style={{ ...td, color: isPaid ? "#15803d" : "#dc2626", fontWeight: 700 }}>{s.maturity_date ? fmtDateDMY(s.maturity_date) : "—"}</td>
                        <td style={{ ...td, color: "#475569", fontSize: 10 }}>
                          {isPaid ? (
                            s.actual_repayment_date ? `سُدد ${fmtDateDMY(s.actual_repayment_date)}` : "تم"
                          ) : s.days_to_maturity !== null ? (
                            s.days_to_maturity > 0 ? `${s.days_to_maturity} يوم` :
                            s.days_to_maturity === 0 ? "اليوم" :
                            `${Math.abs(s.days_to_maturity)} يوم متأخر`
                          ) : "—"}
                        </td>
                        {canEdit && (
                          <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                            {!isPaid ? (
                              <>
                                {/* تعديل تاريخ التفعيل */}
                                <button
                                  onClick={() => editActivationDate(s)}
                                  disabled={markingId === s.id}
                                  style={{
                                    padding: "4px 8px", background: "#dbeafe", color: "#1e40af",
                                    border: "none", borderRadius: 4, cursor: markingId === s.id ? "wait" : "pointer",
                                    fontSize: 10, fontWeight: 700, marginLeft: 3,
                                  }}
                                  title="تعديل تاريخ التفعيل"
                                >📅</button>
                                {/* تم السداد */}
                                <button
                                  onClick={() => markRepaid(s.id)}
                                  disabled={markingId === s.id}
                                  style={{
                                    padding: "4px 8px", background: "#16a34a", color: "#fff",
                                    border: "none", borderRadius: 4, cursor: markingId === s.id ? "wait" : "pointer",
                                    fontSize: 10, fontWeight: 700, marginLeft: 3,
                                  }}
                                  title="تسجيل السداد"
                                >✓ سداد</button>
                                {/* إلغاء التفعيل */}
                                <button
                                  onClick={() => deactivate(s)}
                                  disabled={markingId === s.id}
                                  style={{
                                    padding: "4px 8px", background: "#fee2e2", color: "#991b1b",
                                    border: "none", borderRadius: 4, cursor: markingId === s.id ? "wait" : "pointer",
                                    fontSize: 10, fontWeight: 700,
                                  }}
                                  title="إلغاء التفعيل (إرجاع لحالة قيد الانتظار)"
                                >↶ إلغاء</button>
                              </>
                            ) : (
                              <button
                                onClick={() => unmarkRepaid(s)}
                                disabled={markingId === s.id}
                                style={{
                                  padding: "4px 8px", background: "#fef3c7", color: "#92400e",
                                  border: "none", borderRadius: 4, cursor: markingId === s.id ? "wait" : "pointer",
                                  fontSize: 10, fontWeight: 700,
                                }}
                                title="إلغاء تسجيل السداد"
                              >↶ إلغاء السداد</button>
                            )}
                          </td>
                        )}
                      </tr>
                      {/* v3.3.427 — Expandable: per-payment details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={canEdit ? 12 : 11} style={{ padding: 0, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                            <SukukRepaymentsPanel sukukId={s.id} canEdit={canEdit} onChanged={load} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// v3.3.427 — Per-payment repayment panel (shown when sukuk row is expanded)
function SukukRepaymentsPanel({ sukukId, canEdit, onChanged }) {
  const [data, setData] = useState({ payments: [], no_schedule: false });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.getSukukRepayments(sukukId);
      setData(r.data);
    } catch (e) { console.error(e); toast.error("فشل تحميل الدفعات"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sukukId]);

  const handleMarkPaid = async (p) => {
    const today = new Date().toISOString().slice(0, 10);
    const dt = window.prompt(
      `تسجيل سداد دفعة #${p.sequence_number || ''}\n` +
      `المبلغ: ${fmt(p.total_amount)} ريال\n` +
      `تاريخ الدفعة المخطط: ${p.payment_date ? fmtDateDMY(p.payment_date) : 'غير محدد'}\n\n` +
      `أدخل تاريخ السداد الفعلي (YYYY-MM-DD):`,
      today
    );
    if (!dt) return;
    setBusyId(p.id);
    try {
      await sukuk.markPaymentPaid(p.id, { actual_payment_date: dt, paid_amount: p.total_amount });
      toast.success("تم تسجيل السداد");
      await load();
      if (onChanged) await onChanged();
    } catch (e) { toast.error(e?.response?.data?.error || "فشل"); }
    finally { setBusyId(null); }
  };

  const handleUnmarkPaid = async (p) => {
    if (!window.confirm(`إلغاء تسجيل سداد دفعة #${p.sequence_number}؟`)) return;
    setBusyId(p.id);
    try {
      await sukuk.unmarkPaymentPaid(p.id);
      toast.success("تم إلغاء السداد");
      await load();
      if (onChanged) await onChanged();
    } catch (e) { toast.error(e?.response?.data?.error || "فشل"); }
    finally { setBusyId(null); }
  };

  if (loading) return <div style={{ padding: 14, textAlign: "center", color: "#64748b", fontSize: 11 }}>جاري التحميل...</div>;

  if (data.no_schedule) {
    return (
      <div style={{ padding: 14, fontSize: 11, color: "#92400e", background: "#fef3c7" }}>
        ⚠ هذا الصك لا يحتوي على جدول دفعات تفصيلي. الدفعة الافتراضية تظهر عند الاستحقاق.
      </div>
    );
  }

  if (data.payments.length === 0) {
    return <div style={{ padding: 14, fontSize: 11, color: "#64748b" }}>لا توجد دفعات مخططة لهذا الصك.</div>;
  }

  // Compute summary
  const total = data.payments.reduce((s, p) => s + p.total_amount, 0);
  const paid = data.payments.filter(p => p.is_paid).reduce((s, p) => s + (p.paid_amount || p.total_amount), 0);
  const overdueCount = data.payments.filter(p => p.is_overdue && !p.is_paid).length;
  const overdueAmount = data.payments.filter(p => p.is_overdue && !p.is_paid).reduce((s, p) => s + p.total_amount, 0);
  const remaining = total - paid;

  return (
    <div style={{ padding: "10px 14px" }}>
      {/* Summary */}
      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap", fontSize: 11 }}>
        <div style={{ padding: "4px 10px", background: "#dcfce7", color: "#15803d", borderRadius: 4, fontWeight: 700 }}>
          ✅ مسدد: {fmt(paid)} ({data.payments.filter(p => p.is_paid).length}/{data.payments.length})
        </div>
        <div style={{ padding: "4px 10px", background: "#fef3c7", color: "#92400e", borderRadius: 4, fontWeight: 700 }}>
          🟡 متبقي: {fmt(remaining)}
        </div>
        {overdueCount > 0 && (
          <div style={{ padding: "4px 10px", background: "#fee2e2", color: "#991b1b", borderRadius: 4, fontWeight: 700 }}>
            ⚠ متأخر: {fmt(overdueAmount)} ({overdueCount} دفعة)
          </div>
        )}
      </div>

      {/* Payments table */}
      <div style={{ overflowX: "auto", background: "#fff", borderRadius: 6, border: "1px solid #e2e8f0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              <th style={tdHead}>#</th>
              <th style={tdHead}>الحالة</th>
              <th style={tdHead}>تاريخ الدفعة</th>
              <th style={tdHead}>الأصل</th>
              <th style={tdHead}>الفائدة</th>
              <th style={{ ...tdHead, textAlign: "left" }}>الإجمالي</th>
              <th style={tdHead}>تاريخ السداد الفعلي</th>
              {canEdit && <th style={tdHead}>إجراءات</th>}
            </tr>
          </thead>
          <tbody>
            {data.payments.map(p => (
              <tr key={p.id || p.sequence_number} style={{
                borderBottom: "1px solid #f1f5f9",
                background: p.is_paid ? "#f0fdf4" : (p.is_overdue ? "#fef2f2" : "#fff"),
              }}>
                <td style={{ ...td, fontWeight: 700, direction: "ltr" }}>{p.sequence_number || "—"}</td>
                <td style={td}>
                  {p.is_paid ? (
                    <span style={{ padding: "2px 6px", background: "#dcfce7", color: "#15803d", borderRadius: 3, fontSize: 9, fontWeight: 700 }}>✅ مسدد</span>
                  ) : p.is_overdue ? (
                    <span style={{ padding: "2px 6px", background: "#fee2e2", color: "#991b1b", borderRadius: 3, fontSize: 9, fontWeight: 700 }}>⚠ متأخر</span>
                  ) : (
                    <span style={{ padding: "2px 6px", background: "#fef3c7", color: "#92400e", borderRadius: 3, fontSize: 9, fontWeight: 700 }}>🟡 قائم</span>
                  )}
                </td>
                <td style={{ ...td, color: "#475569" }}>{p.payment_date ? fmtDateDMY(p.payment_date) : "—"}</td>
                <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(p.principal_amount)}</td>
                <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#15803d" }}>{fmt(p.interest_amount)}</td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#dc2626" }}>{fmt(p.total_amount)}</td>
                <td style={{ ...td, color: "#15803d", fontSize: 10 }}>
                  {p.actual_payment_date ? fmtDateDMY(p.actual_payment_date) : "—"}
                </td>
                {canEdit && (
                  <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                    {!p.is_paid ? (
                      <button
                        onClick={() => handleMarkPaid(p)}
                        disabled={busyId === p.id || !p.id}
                        style={{
                          padding: "3px 8px", background: "#16a34a", color: "#fff",
                          border: "none", borderRadius: 3, cursor: "pointer",
                          fontSize: 10, fontWeight: 700,
                        }}
                      >✓ سداد</button>
                    ) : (
                      <button
                        onClick={() => handleUnmarkPaid(p)}
                        disabled={busyId === p.id}
                        style={{
                          padding: "3px 8px", background: "#fef3c7", color: "#92400e",
                          border: "none", borderRadius: 3, cursor: "pointer",
                          fontSize: 10, fontWeight: 700,
                        }}
                      >↶ إلغاء</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Reuse helpers
const tdHead = {
  padding: "8px 10px",
  textAlign: "right",
  fontSize: 10,
  fontWeight: 800,
  color: "#475569",
  whiteSpace: "nowrap",
};

// ═══════════════════════════════════════════════════════════════════
// CONTRACTS TAB
// ═══════════════════════════════════════════════════════════════════
function ContractsTab({ canEdit, canExport, canImport, canDelete, onSelectContract }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.listContracts();
      setContracts(r.data.contracts || []);
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      {(canEdit || canImport) && (
        <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canEdit && (
            <button
              onClick={() => setCreating(true)}
              style={{ padding: "8px 16px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
            ><FiPlus size={13} /> + عقد جديد</button>
          )}
          {canImport && (
            <button
              onClick={() => setImporting(true)}
              style={{ padding: "8px 14px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
            ><FiUpload size={13} /> استيراد عقد</button>
          )}
        </div>
      )}
      {creating && <NewContractForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {importing && <ContractImportModal contracts={contracts} onClose={() => setImporting(false)} onImported={(cid) => { setImporting(false); load(); if (cid) onSelectContract(cid); }} />}
      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>
      ) : contracts.length === 0 ? (
        <div style={{ padding: 30, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b" }}>
          لا توجد عقود — اضغط "+ عقد جديد" أو حوّل عرضاً من تبويب "العروض"
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
          {contracts.map(c => (
            <ContractCard
              key={c.id}
              contract={c}
              canDelete={canDelete}
              onClick={() => onSelectContract(c.id)}
              onDeleted={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ContractCard({ contract, canDelete, onClick, onDeleted }) {
  const c = contract;
  const total = Number(c.total_committed) || 0;
  const received = Number(c.total_received_net) || 0;
  const pct = total > 0 ? (received / total) * 100 : 0;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(
      `⚠️ هل أنت متأكد من حذف العقد "${c.name}"؟\n\n` +
      `سيتم:\n` +
      `  • حذف العقد وجميع برامجه وصكوكه\n` +
      `  • إلغاء الصكوك النشطة (غير المسددة)\n` +
      `  • إرجاع العرض المرتبط إلى حالة "قابل للتحويل" 🔄\n\n` +
      `هذا الإجراء يمكن التراجع عنه.`
    )) return;
    try {
      const r = await sukuk.deleteContract(c.id);
      // v3.3.446 — Inform user about reverted offers
      const reverted = r?.data?.reverted_offers || 0;
      if (reverted > 0) {
        toast.success(`تم حذف العقد · رجع ${reverted} عرض إلى قابل للتحويل`, { duration: 5000 });
      } else {
        toast.success("تم حذف العقد");
      }
      if (onDeleted) onDeleted();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "فشل الحذف");
    }
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14,
        cursor: "pointer", transition: "all 0.15s", position: "relative",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#0891b2"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(8,145,178,0.15)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* v3.3.421 — Delete button (visible only to users with delete permission) */}
      {canDelete && (
        <button
          onClick={handleDelete}
          title="حذف العقد"
          style={{
            position: "absolute", top: 10, left: 10,
            width: 26, height: 26, padding: 0,
            background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca",
            borderRadius: 6, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#fecaca"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#fee2e2"; }}
        >🗑</button>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>{c.name}</div>
          {c.issuing_company && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>🏢 {c.issuing_company}</div>}
        </div>
        <div style={{ background: "#dbeafe", color: "#1e40af", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
          {c.coupon_rate}% / سنة
        </div>
      </div>

      {/* v3.3.643 — Zone 1: معلومات البرنامج (facts that don't change with issuance status) */}
      <div style={{ background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 8, padding: "8px 10px", marginTop: 10 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: "#64748b", letterSpacing: 0.3, marginBottom: 6 }}>🏛️ البرنامج</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
          <StatCell label="القيمة الإجمالية" value={fmt(c.total_committed)} color="#0f172a" />
          <StatCell label="رسوم البرنامج" value={fmt(c.program_fee_paid_at_signing)} color="#7c3aed" />
          <StatCell label="تاريخ البرنامج" value={c.signing_date ? fmtDateDMY(c.signing_date) : "—"} color="#334155" ltr={false} />
          <StatCell label="قيمة المتبقية" value={fmt(c.pending_principal)} color="#b45309" />
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8, fontSize: 10, flexWrap: "wrap" }}>
          <span style={{ background: "#eef2ff", color: "#3730a3", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
            الكل {c.total_count ?? 0}
          </span>
          <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
            ✅ نشطة {c.active_count || 0}
          </span>
          <span style={{ background: "#fef9c3", color: "#854d0e", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
            ⏳ متبقية {c.pending_count || 0}
          </span>
          {(c.matured_count || 0) > 0 && (
            <span style={{ background: "#e2e8f0", color: "#475569", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
              🏁 مسددة {c.matured_count}
            </span>
          )}
        </div>
      </div>

      {/* v3.3.643 — Zone 2: أداء الاصدارات النشطة */}
      <div style={{ background: "#f0fdf4", border: "1px solid #dcfce7", borderRadius: 8, padding: "8px 10px", marginTop: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#166534", letterSpacing: 0.3 }}>📈 أداء الاصدارات النشطة</span>
          {c.avg_apr_net != null && (
            <span style={{ background: "#065f46", color: "#fff", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800, direction: "ltr" }}>
              متوسط APR {Number(c.avg_apr_net).toFixed(2)}%
            </span>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
          <StatCell label="حجم النشطة (أصل)" value={fmt(c.active_principal)} color="#0f172a" />
          <StatCell label="مستلم صافي" value={fmt(c.total_received_net)} color="#15803d" />
          <StatCell label="الرسوم الإدارية" value={fmt(c.active_admin_fee)} color="#b45309" />
          <StatCell label="إعادة السداد" value={fmt(c.active_repayment)} color="#dc2626" />
          <StatCell label="الفوائد (تكلفة التمويل)" value={fmt(c.active_interest)} color="#be123c" />
          <StatCell label="عدد النشطة" value={String(c.active_count || 0)} color="#166534" ltr={false} />
        </div>
      </div>

      {/* Progress bar — نسبة المستلم من الملتزم */}
      {total > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748b", marginBottom: 3 }}>
            <span>المستلم من الملتزم</span>
            <span>{pct.toFixed(1)}%</span>
          </div>
          <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: "#0891b2", transition: "width 0.3s" }} />
          </div>
        </div>
      )}
    </div>
  );
}

// v3.3.643 — Compact label/value cell used inside the contract card zones.
function StatCell({ label, value, color, ltr = true }) {
  return (
    <div>
      <div style={{ color: "#64748b", fontSize: 10 }}>{label}</div>
      <div style={{
        fontWeight: 800, color, fontSize: 11.5,
        direction: ltr ? "ltr" : "rtl", textAlign: ltr ? "right" : "right",
        fontVariantNumeric: "tabular-nums",
      }}>{value}</div>
    </div>
  );
}

function NewContractForm({ onCancel, onCreated }) {
  const [b, setB] = useState({
    name: "", issuing_company: "",
    signing_date: new Date().toISOString().slice(0, 10),
    coupon_rate: 13, program_fee_rate: 1.5, admin_fee_rate: 1.2, tenor_months: 24,
    program_fee_paid_at_signing: 0,
    program_fee_paid_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const set = (k, v) => setB(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!b.name.trim()) { toast.error("الاسم مطلوب"); return; }
    try {
      await sukuk.createContract(b);
      toast.success("تم الإنشاء");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  return (
    <div style={{ marginBottom: 14, padding: 14, background: "#ecfeff", border: "1px solid #67e8f9", borderRadius: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: 10, color: "#155e75" }}>عقد صكوك جديد</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr", gap: 8, marginBottom: 8 }}>
        <input value={b.name} onChange={e => set("name", e.target.value)} placeholder="اسم العقد *" style={inputS} />
        <input value={b.issuing_company} onChange={e => set("issuing_company", e.target.value)} placeholder="شركة الإصدار" style={inputS} />
        <input type="date" value={b.signing_date} onChange={e => set("signing_date", e.target.value)} style={inputS} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
        <NumInput label="معدل الكوبون %" value={b.coupon_rate} onChange={v => set("coupon_rate", v)} />
        <NumInput label="رسوم البرنامج %" value={b.program_fee_rate} onChange={v => set("program_fee_rate", v)} />
        <NumInput label="رسوم إدارية % سنوي" value={b.admin_fee_rate} onChange={v => set("admin_fee_rate", v)} />
        <NumInput label="المدة (شهر)" value={b.tenor_months} onChange={v => set("tenor_months", v)} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <NumInput label="رسوم البرنامج المدفوعة (دفعة واحدة)" value={b.program_fee_paid_at_signing} onChange={v => set("program_fee_paid_at_signing", v)} />
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}>تاريخ دفع رسوم البرنامج</div>
          <input type="date" value={b.program_fee_paid_date} onChange={e => set("program_fee_paid_date", e.target.value)} style={inputS} />
        </div>
      </div>
      <input value={b.notes} onChange={e => set("notes", e.target.value)} placeholder="ملاحظات" style={{ ...inputS, width: "100%", marginBottom: 8 }} />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={btnGrey}>إلغاء</button>
        <button onClick={submit} style={btnGreen}>إنشاء</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// OFFERS TAB
// ═══════════════════════════════════════════════════════════════════
function OffersTab({ canEdit, canExport, canImport, canDelete, onConverted }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [convertingOffer, setConvertingOffer] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [selectedTrancheId, setSelectedTrancheId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.listOffers();
      setOffers(r.data.offers || []);
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // If a tranche is selected, show its detail page (deepest level)
  if (selectedTrancheId !== null) {
    return (
      <TrancheDetailPage
        trancheId={selectedTrancheId}
        canEdit={canEdit}
        onBack={() => setSelectedTrancheId(null)}
      />
    );
  }

  // If an offer is selected, show its detail view
  if (selectedOfferId !== null) {
    return (
      <OfferDetail
        offerId={selectedOfferId}
        canEdit={canEdit}
        canExport={canExport}
        canImport={canImport}
        onBack={() => { setSelectedOfferId(null); load(); }}
        onConvert={() => {
          const o = offers.find(o => o.id === selectedOfferId);
          if (o) setConvertingOffer(o);
        }}
        onSelectTranche={(trancheId) => setSelectedTrancheId(trancheId)}
      />
    );
  }

  return (
    <div>
      {(canEdit || canImport) && (
        <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canEdit && (
            <button onClick={() => setCreating(true)} style={{ padding: "8px 16px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <FiPlus size={13} /> + عرض جديد
            </button>
          )}
          {canImport && (
            <button onClick={() => setImporting(true)} style={{ padding: "8px 14px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <FiUpload size={13} /> استيراد عرض
            </button>
          )}
        </div>
      )}
      {creating && <NewOfferForm onCancel={() => setCreating(false)} onCreated={(newOfferId) => { setCreating(false); load(); if (newOfferId) setSelectedOfferId(newOfferId); }} />}
      {importing && <OfferImportModal onClose={() => setImporting(false)} onImported={(newOfferId) => { setImporting(false); load(); if (newOfferId) setSelectedOfferId(newOfferId); }} />}
      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>
      ) : offers.length === 0 ? (
        <div style={{ padding: 30, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b" }}>
          لا توجد عروض
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
          {offers.map(o => (
            <OfferCard
              key={o.id}
              offer={o}
              canEdit={canEdit}
              onClick={() => setSelectedOfferId(o.id)}
              onConvert={() => setConvertingOffer(o)}
              onChange={load}
            />
          ))}
        </div>
      )}
      {convertingOffer && (
        <ConvertOfferModal
          offer={convertingOffer}
          onCancel={() => setConvertingOffer(null)}
          onDone={(cid) => { setConvertingOffer(null); onConverted(cid); }}
        />
      )}
    </div>
  );
}

function OfferCard({ offer, canEdit, onClick, onConvert, onChange }) {
  const o = offer;
  const trancheCount = Number(o.tranche_count) || 0;
  const committedSize = Number(o.committed_size) || 0;
  const optionalSize = Number(o.optional_size) || 0;
  const totalSize = committedSize + optionalSize;
  const isConverted = o.status === 'converted';

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm("حذف هذا العرض؟")) return;
    try { await sukuk.deleteOffer(o.id); toast.success("تم"); onChange(); }
    catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل"); }
  };

  const exportFile = async (e) => {
    e.stopPropagation();
    try { await sukuk.exportOffer(o.id); toast.success("تم التحميل"); }
    catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل التصدير"); }
  };

  const handleConvert = (e) => {
    e.stopPropagation();
    if (trancheCount === 0) {
      toast.error("أضف إصدارات للعرض أولاً");
      return;
    }
    onConvert();
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: isConverted ? "#f0fdf4" : "#fff",
        border: `1px solid ${isConverted ? "#86efac" : "#e2e8f0"}`,
        borderRadius: 12, padding: 14, position: "relative", cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={e => { if (!isConverted) { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(124,58,237,0.15)"; } }}
      onMouseLeave={e => { if (!isConverted) { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; } }}
    >
      {isConverted && (
        <div style={{ position: "absolute", top: 8, left: 8, background: "#16a34a", color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
          ✓ تحوّل إلى عقد
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>{o.name}</div>
        {o.issuing_company && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>🏢 {o.issuing_company}</div>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 11, marginBottom: 8 }}>
        <Stat label="الكوبون" value={`${o.coupon_rate}%`} color="#1e40af" />
        <Stat label="رسوم البرنامج" value={`${o.program_fee_rate}%`} color="#7c2d12" />
        <Stat
          label="إدارية سنوي"
          value={`${annualizeAdminFee(o.admin_fee_rate, o.tenor_months).toFixed(4)}%`}
          color="#7c2d12"
          title={`النسبة المطلقة ${Number(o.admin_fee_rate || 0).toFixed(4)}% × (12 / ${o.tenor_months || 0} شهر)`}
        />
        <Stat label="المدة" value={`${o.tenor_months} شهر · ${(o.tenor_months || 0) * 30} يوم`} color="#475569" />
        <Stat label="الأساسي" value={fmt(committedSize)} color="#0f172a" />
        <Stat label="الاختياري" value={fmt(optionalSize)} color="#7c3aed" />
      </div>
      <div style={{ background: "#f1f5f9", padding: "6px 10px", borderRadius: 6, fontSize: 11, marginBottom: 8 }}>
        إجمالي البرنامج: <span style={{ fontWeight: 800, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totalSize)}</span>
        {" · "}{trancheCount} إصدار {trancheCount === 0 && <span style={{ color: "#dc2626", fontWeight: 700 }}>⚠ لا توجد إصدارات</span>}
      </div>
      <div style={{ fontSize: 10, color: "#7c3aed", marginBottom: 8, fontWeight: 600 }}>
        ↗ اضغط لعرض جدول الإصدارات التفصيلي
      </div>
      {canEdit && (
        <div style={{ display: "flex", gap: 6 }}>
          {!isConverted && (
            <button onClick={handleConvert} style={{ flex: 1, padding: "6px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
              <FiCheck size={12} /> تحويل إلى عقد
            </button>
          )}
          <button onClick={exportFile} title="تصدير" style={{ padding: "6px 10px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <FiDownload size={12} />
          </button>
          <button onClick={remove} style={{ padding: "6px 10px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            🗑
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, title }) {
  return (
    <div title={title} style={{ background: "#f8fafc", padding: "5px 8px", borderRadius: 5 }}>
      <div style={{ fontSize: 9, color: "#64748b" }}>{label}</div>
      <div style={{ fontWeight: 700, color, fontSize: 11, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function NewOfferForm({ onCancel, onCreated }) {
  const [b, setB] = useState({
    name: "", issuing_company: "",
    offer_date: new Date().toISOString().slice(0, 10), expiry_date: "",
    notes: "",
    // Internal defaults used when creating tranches (will be overridden per tranche)
    coupon_rate: 13, program_fee_rate: 1.5, admin_fee_rate: 1.2,
    tenor_months: 24, issuance_interval_days: 15,
    tranche_size: 0,
    num_tranches: 0, num_supplementary: 0,
    program_size: 0, supplementary_size: 0,
  });
  const set = (k, v) => setB(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!b.name.trim()) { toast.error("اسم العرض مطلوب"); return; }
    try {
      const r = await sukuk.createOffer(b);
      toast.success("تم — أضف الإصدارات الآن");
      onCreated(r.data.offer.id);
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  return (
    <div style={{ marginBottom: 14, padding: 14, background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: "#6b21a8" }}>عرض صكوك جديد</div>
      <div style={{ fontSize: 11, color: "#7c3aed", marginBottom: 12 }}>
        💡 معلومات أساسية فقط — كل الأرقام (الكوبون، الرسوم، المدة، الأحجام) ستُحسب تلقائياً من جدول الإصدارات
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
        <input value={b.name} onChange={e => set("name", e.target.value)} placeholder="اسم العرض *" style={inputS} />
        <input value={b.issuing_company} onChange={e => set("issuing_company", e.target.value)} placeholder="شركة الإصدار" style={inputS} />
        <input type="date" value={b.offer_date} onChange={e => set("offer_date", e.target.value)} style={inputS} title="تاريخ العرض" />
        <input type="date" value={b.expiry_date} onChange={e => set("expiry_date", e.target.value)} style={inputS} title="تاريخ الانتهاء" />
      </div>
      <input value={b.notes} onChange={e => set("notes", e.target.value)} placeholder="ملاحظات" style={{ ...inputS, width: "100%", marginBottom: 10 }} />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={btnGrey}>إلغاء</button>
        <button onClick={submit} style={{ ...btnGreen, background: "#7c3aed" }}>إنشاء وإضافة الإصدارات →</button>
      </div>
    </div>
  );
}

function ConvertOfferModal({ offer, onCancel, onDone }) {
  const [signingDate, setSigningDate] = useState(new Date().toISOString().slice(0, 10));
  const [feeDate, setFeeDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [trancheData, setTrancheData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await sukuk.getOffer(offer.id);
        setTrancheData(r.data);
      } catch (e) { /* ignore */ }
    })();
  }, [offer.id]);

  const committedTotal = trancheData
    ? (trancheData.tranches || []).filter(t => !t.is_optional).reduce((s, t) => s + Number(t.principal || 0), 0)
    : 0;
  const optionalTotal = trancheData
    ? (trancheData.tranches || []).filter(t => t.is_optional).reduce((s, t) => s + Number(t.principal || 0), 0)
    : 0;
  // v3.3.443 — Compute program fee tranche-by-tranche (matches Hero box logic)
  // Each tranche may have its own program_fee_rate; sum: Σ (principal × rate%)
  const programFee = trancheData
    ? (trancheData.tranches || [])
        .filter(t => !t.is_optional)
        .reduce((s, t) => s + Number(t.principal || 0) * Number(t.program_fee_rate ?? offer.program_fee_rate ?? 0) / 100, 0)
    : 0;
  const trancheCount = trancheData ? (trancheData.tranches || []).length : 0;
  const committedCount = trancheData ? (trancheData.tranches || []).filter(t => !t.is_optional).length : 0;
  const optionalCount = trancheData ? (trancheData.tranches || []).filter(t => t.is_optional).length : 0;

  const submit = async () => {
    if (trancheCount === 0) { toast.error("لا توجد إصدارات في العرض"); return; }
    setBusy(true);
    try {
      const r = await sukuk.convertOffer(offer.id, {
        signing_date: signingDate,
        program_fee_paid_date: feeDate,
      });
      toast.success("تم تحويل العرض إلى عقد");
      onDone(r.data.contract_id);
    } catch (e) {
      // v3.3.435 — Detailed error handling for permission issues
      const errData = e.response?.data;
      if (e.response?.status === 403 && errData?.current_perms) {
        const cp = errData.current_perms;
        const hasAnyPerm = cp.sukuk_can_add || cp.sukuk_can_edit || cp.sukuk_can_delete || cp.sukuk_can_activate;
        if (!hasAnyPerm) {
          toast.error("ليس لديك أي صلاحية على الصكوك. الرجاء التواصل مع المدير.", { duration: 6000 });
        } else {
          // Has some permissions but not the required ones — likely stale session
          toast.error("صلاحياتك مُحدَّثة لكن الجلسة قديمة. الرجاء تسجيل الخروج والدخول مجدداً.", { duration: 8000 });
        }
      } else {
        toast.error(errData?.error || "فشل التحويل");
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ ...modalBox, maxWidth: 580 }}>
        <div style={modalHead}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>تحويل العرض إلى عقد</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{offer.name}</div>
          </div>
          <button onClick={onCancel} style={modalClose}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          {trancheCount === 0 ? (
            <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: 12, marginBottom: 14, color: "#991b1b", fontSize: 13 }}>
              ⚠ لا توجد إصدارات في هذا العرض. يجب إضافة الإصدارات (مع تواريخها ومبالغها) قبل التحويل لعقد.
            </div>
          ) : (
            <>
              <div style={{ background: "#f1f5f9", borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8 }}>📊 ملخص الإصدارات في العرض</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <Box label="الأساسي" value={`${committedCount} × ${fmt(committedTotal)}`} color="#0891b2" bg="#ecfeff" />
                  <Box label="الاختياري" value={`${optionalCount} × ${fmt(optionalTotal)}`} color="#7c3aed" bg="#faf5ff" />
                  <Box label="الإجمالي" value={fmt(committedTotal + optionalTotal)} color="#0f172a" bg="#fff" />
                </div>
              </div>

              <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#92400e", fontWeight: 700, marginBottom: 6 }}>💰 رسوم البرنامج المستحقة عند التوقيع</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#92400e", direction: "ltr" }}>
                  {fmt(programFee)} ريال
                </div>
                <div style={{ fontSize: 10, color: "#78350f", marginTop: 4 }}>
                  {/* v3.3.443 — Detect if rates vary across tranches */}
                  {(() => {
                    const commits = trancheData ? (trancheData.tranches || []).filter(t => !t.is_optional) : [];
                    const rates = new Set(commits.map(t => Number(t.program_fee_rate ?? offer.program_fee_rate ?? 0).toFixed(4)));
                    if (rates.size <= 1) {
                      const rate = Number(offer.program_fee_rate || 0).toFixed(2);
                      return `(${rate}% × ${fmt(committedTotal)} الأساسي فقط) — تُدفع دفعة واحدة`;
                    }
                    return `(محسوبة إصداراً بإصدار — نسب الرسوم تختلف بين الإصدارات) — تُدفع دفعة واحدة`;
                  })()}
                </div>
                <div style={{ fontSize: 10, color: "#78350f", marginTop: 2, fontStyle: "italic" }}>
                  الإصدارات الاختيارية تدفع رسوم البرنامج عند الإصدار، ليس الآن
                </div>
              </div>
            </>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 4 }}>📅 تاريخ التوقيع</div>
              <input type="date" value={signingDate} onChange={e => setSigningDate(e.target.value)} style={{ ...inputS, width: "100%" }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 4 }}>💵 تاريخ دفع الرسوم</div>
              <input type="date" value={feeDate} onChange={e => setFeeDate(e.target.value)} style={{ ...inputS, width: "100%" }} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onCancel} style={btnGrey} disabled={busy}>إلغاء</button>
            <button onClick={submit} style={{ ...btnGreen, opacity: busy || trancheCount === 0 ? 0.6 : 1 }} disabled={busy || trancheCount === 0}>
              {busy ? "جاري..." : "تأكيد التحويل"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONTRACT DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════
function ContractDetail({ contractId, canEdit, canExport, canImport, canActivate, canDelete, onBack }) {
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details"); // "details" | "documents"

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        sukuk.getContract(contractId),
        sukuk.analyze(contractId),
      ]);
      setData(r1.data);
      setAnalysis(r2.data);
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [contractId]);

  if (loading || !data) return <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>;

  const { contract, programs, sukuks } = data;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
        <button onClick={onBack} style={{ padding: "6px 12px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FiArrowRight size={12} /> العودة للقائمة
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{contract.name}</div>
          {contract.issuing_company && <div style={{ fontSize: 11, color: "#64748b" }}>🏢 {contract.issuing_company}</div>}
        </div>
        {canExport && (
          <button
            onClick={async () => {
              try { await sukuk.exportContract(contractId); toast.success("تم التحميل"); }
              catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل التصدير"); }
            }}
            style={{ padding: "6px 14px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <FiDownload size={12} /> تصدير
          </button>
        )}
        {/* v3.3.421 — Delete contract button */}
        {canDelete && (
          <button
            onClick={async () => {
              if (!window.confirm(
                `⚠️ هل أنت متأكد من حذف العقد "${contract.name}"؟\n\n` +
                `سيتم:\n` +
                `  • حذف العقد وجميع برامجه وصكوكه\n` +
                `  • إلغاء الصكوك النشطة (غير المسددة)\n` +
                `  • إرجاع العرض المرتبط إلى حالة "قابل للتحويل" 🔄`
              )) return;
              try {
                const r = await sukuk.deleteContract(contractId);
                // v3.3.446 — Inform user about reverted offers
                const reverted = r?.data?.reverted_offers || 0;
                if (reverted > 0) {
                  toast.success(`تم حذف العقد · رجع ${reverted} عرض إلى قابل للتحويل`, { duration: 5000 });
                } else {
                  toast.success("تم حذف العقد");
                }
                onBack();
              } catch (err) {
                console.error(err);
                toast.error(err?.response?.data?.error || "فشل الحذف");
              }
            }}
            style={{ padding: "6px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            🗑 حذف العقد
          </button>
        )}
      </div>

      {/* v3.3.409 — Tab bar */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e8f0", marginBottom: 14 }}>
        <button
          onClick={() => setTab("details")}
          style={{
            padding: "8px 18px",
            background: tab === "details" ? "#7c3aed" : "transparent",
            color: tab === "details" ? "#fff" : "#475569",
            border: "none",
            borderRadius: "6px 6px 0 0",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          📋 التفاصيل والصكوك
        </button>
        <button
          onClick={() => setTab("documents")}
          style={{
            padding: "8px 18px",
            background: tab === "documents" ? "#7c3aed" : "transparent",
            color: tab === "documents" ? "#fff" : "#475569",
            border: "none",
            borderRadius: "6px 6px 0 0",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          📁 المستندات
        </button>
      </div>

      {tab === "documents" ? (
        <DocumentsSection
          scope="contract"
          entityId={contractId}
          canEdit={canEdit}
          title="📄 مستندات العقد"
          subtitle={`Drive: Financing/${contract.name || ""}/العقد`}
          themeColor="#7c3aed"
          themeBg="#faf5ff"
          themeBorder="#d8b4fe"
          enableCategories={true}
        />
      ) : (
        <>

      {/* Summary */}
      {analysis && <ContractSummary summary={analysis.summary} contract={contract} />}

      {/* Programs and sukuks */}
      {programs.map(prog => (
        <ProgramSection
          key={prog.id}
          program={prog}
          sukuks={sukuks.filter(s => s.program_id === prog.id)}
          aprBySukukId={analysis?.apr_by_sukuk || {}}
          canEdit={canEdit}
          onChange={load}
        />
      ))}

      {/* Add program button */}
      {canEdit && programs.length < 2 && (
        <AddProgramButton contractId={contractId} programs={programs} onAdded={load} />
      )}

      {/* Cash flow chart */}
      {analysis && analysis.monthly && analysis.monthly.length > 0 && (
        <CashFlowChart monthly={analysis.monthly} cashflows={analysis.cashflows} />
      )}

      {/* v3.3.404 — Gantt chart for sukuks */}
      {sukuks.length > 0 && (
        <SukuksGanttChart
          sukuks={sukuks}
          aprBySukukId={analysis?.apr_by_sukuk || {}}
          contract={contract}
        />
      )}
        </>
      )}
    </div>
  );
}

function ContractSummary({ summary, contract }) {
  const s = summary;
  const aprValue = s.program_apr_pct !== null && s.program_apr_pct !== undefined ? s.program_apr_pct : null;
  return (
    <div>
      {/* Hero: Program APR (most important metric) */}
      {aprValue !== null && (
        <div style={{
          marginBottom: 12, padding: "14px 18px",
          background: "linear-gradient(135deg, #581c87 0%, #7c3aed 100%)",
          borderRadius: 12, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 4px 14px rgba(124,58,237,0.25)",
        }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, letterSpacing: "0.5px" }}>📊 EFFECTIVE PROGRAM APR</div>
            <div style={{ fontSize: 28, fontWeight: 800, marginTop: 2, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
              {aprValue.toFixed(2)}%
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              {/* v3.3.480 — Weighted average of APR-on-Net (مرّر على الصكوك لرؤية الـ 3 طرق) */}
              متوسط APR على الصافي مرجح بالمبلغ للإصدارات المُفعَّلة فقط
            </div>
          </div>
          <div style={{ textAlign: "left", direction: "ltr", fontSize: 11, opacity: 0.9 }}>
            <div>✅ يحسب فقط الإصدارات النشطة والمسددة</div>
            <div>⚖️ مرجح بـ Principal كل إصدار</div>
            <div>📈 لا يشمل الإصدارات قيد الانتظار</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginBottom: 12 }}>
        <Box label="إجمالي ملتزم" value={fmt(s.total_committed)} color="#0f172a" bg="#f1f5f9" sub={`${s.total_count} صك`} />
        <Box label="مستلم نقداً (صافي)" value={fmt(s.total_received_net)} color="#15803d" bg="#f0fdf4" sub={`✅ ${s.active_count} نشط`} />
        <Box label="للسداد" value={fmt(s.total_repayment)} color="#dc2626" bg="#fef2f2" sub={`💰 ${s.matured_count} مسدد`} />
        <Box label="رسوم البرنامج (دفعة)" value={fmt(s.program_fee_upfront)} color="#7c2d12" bg="#fef3c7" />
        <Box label="رسوم إدارية" value={fmt(s.total_admin_fee)} color="#7c2d12" bg="#fef3c7" />
        <Box label="فوائد المستثمرين" value={fmt(s.total_interest)} color="#7c2d12" bg="#fef3c7" />
        <Box label="إجمالي تكلفة التمويل" value={fmt(s.total_cost_of_capital)} color="#991b1b" bg="#fef2f2" />
        <Box label="التكلفة الإجمالية %" value={`${s.effective_cost_pct}%`} color="#581c87" bg="#faf5ff" sub="مبسط (بدون TVM)" />
      </div>
    </div>
  );
}

function Box({ label, value, color, bg, sub }) {
  return (
    <div style={{ background: bg, padding: "10px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color, direction: "ltr", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ProgramSection({ program, sukuks, aprBySukukId, canEdit, onChange }) {
  const [generating, setGenerating] = useState(false);
  const isMain = program.program_type === 'main';
  const totalPrincipal = sukuks.reduce((s, x) => s + Number(x.principal), 0);
  const activeCount = sukuks.filter(s => s.status === 'active').length;
  const pendingCount = sukuks.filter(s => s.status === 'pending').length;
  const maturedCount = sukuks.filter(s => s.status === 'matured').length;

  return (
    <div style={{ marginBottom: 14, background: "#fff", border: `2px solid ${isMain ? "#0891b2" : "#7c3aed"}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: isMain ? "#ecfeff" : "#faf5ff", borderBottom: `1px solid ${isMain ? "#a5f3fc" : "#d8b4fe"}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: isMain ? "#155e75" : "#6b21a8" }}>
              {isMain ? "📌 البرنامج الأساسي" : "➕ البرنامج التكميلي"}
              {program.name && <span style={{ fontWeight: 500, marginRight: 6, opacity: 0.8 }}>· {program.name}</span>}
            </div>
            <div style={{ fontSize: 11, color: isMain ? "#0e7490" : "#7c3aed", marginTop: 2 }}>
              {sukuks.length} صك · حجم: {fmt(totalPrincipal)} · ✅ {activeCount} / ⏳ {pendingCount} / 💰 {maturedCount}
            </div>
          </div>
          {canEdit && (
            <button onClick={() => setGenerating(true)} style={{ padding: "5px 12px", background: isMain ? "#0891b2" : "#7c3aed", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <FiZap size={11} /> توليد صكوك
            </button>
          )}
        </div>
      </div>

      {generating && (
        <GenerateTranchesForm
          program={program}
          onCancel={() => setGenerating(false)}
          onDone={() => { setGenerating(false); onChange(); }}
        />
      )}

      {sukuks.length === 0 ? (
        <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
          لا توجد صكوك بعد — اضغط "توليد صكوك"
        </div>
      ) : (
        <SukukTable sukuks={sukuks} aprBySukukId={aprBySukukId} canEdit={canEdit} onChange={onChange} />
      )}
    </div>
  );
}

function GenerateTranchesForm({ program, onCancel, onDone }) {
  const [count, setCount] = useState(program.num_tranches || 22);
  const [principal, setPrincipal] = useState(program.tranche_size || 750000);
  const [firstDate, setFirstDate] = useState(new Date().toISOString().slice(0, 10));
  const [intervalDays, setIntervalDays] = useState(program.issuance_interval_days || 15);
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await sukuk.generateTranches(program.id, {
        count: parseInt(count), principal: parseFloat(principal),
        first_date: firstDate, interval_days: parseInt(intervalDays),
        replace_existing: replace,
      });
      toast.success(`تم توليد ${r.data.count} صك`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: 12, background: "#fef9c3", borderBottom: "1px solid #fde68a" }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: "#713f12", fontSize: 12 }}>⚡ توليد صكوك متسلسلة</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
        <NumInput label="العدد" value={count} onChange={setCount} />
        <NumInput label="مبلغ كل صك" value={principal} onChange={setPrincipal} />
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#713f12", marginBottom: 3 }}>تاريخ الأول</div>
          <input type="date" value={firstDate} onChange={e => setFirstDate(e.target.value)} style={{ ...inputS, width: "100%" }} />
        </div>
        <NumInput label="الفاصل (يوم)" value={intervalDays} onChange={setIntervalDays} />
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#713f12", marginBottom: 8 }}>
        <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} />
        استبدال الصكوك المعلّقة الموجودة
      </label>
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={btnGrey} disabled={busy}>إلغاء</button>
        <button onClick={submit} style={btnGreen} disabled={busy}>{busy ? "جاري..." : "توليد"}</button>
      </div>
    </div>
  );
}

// v3.3.474/475 — Cell that shows payment count and reveals full schedule on hover
// Uses a Portal to render the popup at document.body, bypassing any overflow clipping
// on parent containers (the sukuk table sits inside scrollable/clipped wrappers).
function PaymentScheduleCell({ sukuk: s, schedule }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const count = Number(s.payment_count) || 0;

  const handleEnter = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      // Popup is roughly 420px wide. Center under the trigger,
      // clamping to viewport edges with an 8px margin.
      const popupWidth = 420;
      let left = r.left + r.width / 2 - popupWidth / 2;
      if (left < 8) left = 8;
      if (left + popupWidth > window.innerWidth - 8) left = window.innerWidth - popupWidth - 8;
      setPos({
        top: r.bottom + window.scrollY + 4,
        left: left + window.scrollX,
      });
    }
    setShow(true);
  };
  const handleLeave = () => setShow(false);

  const popupNode = show ? (
    <div
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        zIndex: 99999,
        width: 420,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={handleLeave}
    >
      <div style={{
        background: "#fff",
        border: "1px solid #67e8f9",
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(14, 116, 144, 0.30)",
        padding: "10px 12px",
        direction: "rtl",
        textAlign: "right",
        fontSize: 11,
        fontWeight: 500,
        color: "#0f172a",
      }}>
        <div style={{ fontWeight: 800, color: "#0e7490", marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid #e0f2fe", fontSize: 12 }}>
          📋 جدول دفعات الصك #{s.sequence_number} — {count} دفعة
        </div>
        {schedule && schedule.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead>
              <tr style={{ background: "#f0fdfa", color: "#0f766e" }}>
                <th style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>#</th>
                <th style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>التاريخ</th>
                <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>الأصل</th>
                <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>الفائدة</th>
                <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>الإجمالي</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((p, idx) => {
                const pAmt = Number(p.principal_amount) || 0;
                const iAmt = Number(p.interest_amount) || 0;
                const total = pAmt + iAmt;
                return (
                  <tr key={idx} style={{ borderBottom: idx < schedule.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <td style={{ padding: "3px 6px", textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                    <td style={{ padding: "3px 6px", textAlign: "center", direction: "ltr", color: "#0f172a" }}>{fmtDateDMY(p.date)}</td>
                    <td style={{ padding: "3px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: pAmt > 0 ? "#15803d" : "#cbd5e1", fontWeight: pAmt > 0 ? 700 : 400 }}>
                      {pAmt > 0 ? fmt(pAmt) : "—"}
                    </td>
                    <td style={{ padding: "3px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: iAmt > 0 ? "#b45309" : "#cbd5e1", fontWeight: iAmt > 0 ? 700 : 400 }}>
                      {iAmt > 0 ? fmt(iAmt) : "—"}
                    </td>
                    <td style={{ padding: "3px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0f172a", fontWeight: 700 }}>
                      {fmt(total)}
                    </td>
                  </tr>
                );
              })}
              {(() => {
                const totals = schedule.reduce((acc, p) => {
                  acc.p += Number(p.principal_amount) || 0;
                  acc.i += Number(p.interest_amount) || 0;
                  return acc;
                }, { p: 0, i: 0 });
                return (
                  <tr style={{ background: "#f0fdfa", color: "#0f766e", fontWeight: 800, borderTop: "2px solid #67e8f9" }}>
                    <td colSpan={2} style={{ padding: "4px 6px", textAlign: "center" }}>الإجمالي</td>
                    <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.p)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.i)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.p + totals.i)}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: "10px 6px", textAlign: "center", color: "#64748b", fontSize: 11 }}>
            ⏳ جاري تحميل تفاصيل الدفعات...
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{
          display: "inline-block",
          padding: "3px 10px",
          background: show ? "#0e7490" : "#ecfeff",
          color: show ? "#fff" : "#0e7490",
          border: "1px solid #67e8f9",
          borderRadius: 4,
          fontWeight: 800,
          cursor: "help",
          fontSize: 11,
          transition: "background 0.15s, color 0.15s",
        }}
        title={`${count} دفعة مجدولة — مرّر لعرض الجدول`}
      >
        {count} 📋
      </span>
      {popupNode && createPortal(popupNode, document.body)}
    </>
  );
}

// v3.3.477 — APR cell with detailed breakdown popup explaining how Effective APR is computed
// Shows the math: admin annualized + program annualized + coupon vs. naive sum vs. XIRR result
function AprBreakdownCell({ aprInfo, sukuk: s, isPending }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const aprPct = aprInfo?.apr_pct;
  const hasApr = !isPending && aprInfo && aprPct !== null && aprPct !== undefined;

  const handleEnter = () => {
    if (triggerRef.current && hasApr) {
      const r = triggerRef.current.getBoundingClientRect();
      const popupWidth = 460;
      let left = r.left + r.width / 2 - popupWidth / 2;
      if (left < 8) left = 8;
      if (left + popupWidth > window.innerWidth - 8) left = window.innerWidth - popupWidth - 8;
      setPos({
        top: r.bottom + window.scrollY + 4,
        left: left + window.scrollX,
      });
    }
    setShow(true);
  };
  const handleLeave = () => setShow(false);

  if (!hasApr) {
    return <span style={{ color: "#94a3b8", fontSize: 10 }} title={isPending ? "يظهر بعد تفعيل الصك" : ""}>—</span>;
  }

  const b = aprInfo.breakdown || {};
  const usesSchedule = aprInfo.uses_schedule;

  const popupNode = show ? (
    <div
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        zIndex: 99999,
        width: 480,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={handleLeave}
    >
      <div style={{
        background: "#fff",
        border: "1px solid #d8b4fe",
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(124, 58, 237, 0.30)",
        padding: "12px 14px",
        direction: "rtl",
        textAlign: "right",
        fontSize: 11,
        color: "#0f172a",
      }}>
        <div style={{ fontWeight: 800, color: "#7c3aed", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #ede9fe", fontSize: 13 }}>
          🎯 تفصيل APR الفعلي — الصك #{s.sequence_number}
        </div>

        {/* v3.3.480 — Three APR rates side-by-side with explanations */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, color: "#475569", marginBottom: 5, fontSize: 11 }}>
            📊 ثلاث طرق لقياس التكلفة الفعلية
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            {/* (A) Simple on principal */}
            <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "8px 6px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#92400e", fontWeight: 700 }}>على الأصل (Simple)</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#92400e", direction: "ltr", marginTop: 2 }}>
                {b.apr_simple_on_principal !== null ? `${b.apr_simple_on_principal.toFixed(2)}%` : "—"}
              </div>
              <div style={{ fontSize: 9, color: "#92400e", marginTop: 3, lineHeight: 1.3 }}>
                نفس اتفاقية الكوبون (30/360)
              </div>
            </div>
            {/* (B) Simple on net — PRIMARY */}
            <div style={{ background: "#dcfce7", border: "2px solid #16a34a", borderRadius: 6, padding: "8px 6px", textAlign: "center", position: "relative" }}>
              <div style={{ position: "absolute", top: -8, right: "50%", transform: "translateX(50%)", background: "#16a34a", color: "#fff", fontSize: 8, fontWeight: 800, padding: "1px 6px", borderRadius: 4 }}>
                الأساس
              </div>
              <div style={{ fontSize: 9, color: "#15803d", fontWeight: 700 }}>على الصافي (Simple)</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#15803d", direction: "ltr", marginTop: 2 }}>
                {b.apr_simple_on_net !== null ? `${b.apr_simple_on_net.toFixed(2)}%` : "—"}
              </div>
              <div style={{ fontSize: 9, color: "#15803d", marginTop: 3, lineHeight: 1.3 }}>
                الأدق من منظور المُصدِر
              </div>
            </div>
            {/* (C) XIRR */}
            <div style={{ background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: 6, padding: "8px 6px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#7c3aed", fontWeight: 700 }}>XIRR (دولي)</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#7c3aed", direction: "ltr", marginTop: 2 }}>
                {b.apr_xirr !== null ? `${b.apr_xirr.toFixed(2)}%` : "—"}
              </div>
              <div style={{ fontSize: 9, color: "#7c3aed", marginTop: 3, lineHeight: 1.3 }}>
                Compound (IFRS standard)
              </div>
            </div>
          </div>
        </div>

        {/* Components breakdown — 30/360 simple convention */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, color: "#475569", marginBottom: 5, fontSize: 11 }}>
            🧮 المكوّنات (اتفاقية 30/360)
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead>
              <tr style={{ background: "#faf5ff", color: "#581c87" }}>
                <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>المكوّن</th>
                <th style={{ padding: "4px 6px", textAlign: "center", fontWeight: 700 }}>المعادلة</th>
                <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>سنوي %</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 6px", fontWeight: 700, color: "#92400e" }}>🟠 الكوبون</td>
                <td style={{ padding: "4px 6px", textAlign: "center", color: "#64748b", fontSize: 10, direction: "ltr" }}>
                  {fmt(b.total_interest)} ÷ {fmt(b.principal)} × (360 ÷ {b.tenor_days})
                </td>
                <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#92400e", fontWeight: 700 }}>
                  {b.coupon_annual_pct?.toFixed(2)}%
                </td>
              </tr>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 6px", fontWeight: 700, color: "#7c2d12" }}>🟤 الإدارية</td>
                <td style={{ padding: "4px 6px", textAlign: "center", color: "#64748b", fontSize: 10, direction: "ltr" }}
                    title={`رسم مطلق ${b.admin_fee_abs_pct}% × ${fmt(b.principal)} = ${fmt(b.admin_fee_amount)} يُدفع مرة واحدة`}>
                  {fmt(b.admin_fee_amount)} ÷ {fmt(b.principal)} × (360 ÷ {b.tenor_days})
                </td>
                <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#7c2d12", fontWeight: 700 }}>
                  {b.admin_fee_annual_pct?.toFixed(2)}%
                </td>
              </tr>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "4px 6px", fontWeight: 700, color: "#9d174d" }}>🌸 البرنامج</td>
                <td style={{ padding: "4px 6px", textAlign: "center", color: "#64748b", fontSize: 10, direction: "ltr" }}
                    title={`حصة هذا الإصدار من رسوم البرنامج، تُدفع مرة واحدة عند توقيع العقد`}>
                  {fmt(b.program_fee_total)} ÷ {fmt(b.principal)} × (360 ÷ {b.tenor_days})
                </td>
                <td style={{ padding: "4px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#9d174d", fontWeight: 700 }}>
                  {b.program_fee_annual_pct?.toFixed(2)}%
                </td>
              </tr>
              <tr style={{ background: "#fef3c7", borderTop: "2px solid #fcd34d" }}>
                <td colSpan={2} style={{ padding: "5px 6px", fontWeight: 700, color: "#92400e", textAlign: "center" }}>📐 المجموع (على الأصل)</td>
                <td style={{ padding: "5px 6px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#92400e", fontWeight: 800 }}>
                  {b.naive_sum_pct?.toFixed(2)}%
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: 4, fontSize: 9.5, color: "#64748b", lineHeight: 1.4 }}>
            💡 الإدارية والبرنامج رسوم <strong>مطلقة لمرة واحدة</strong>. النسبة السنوية = ما تعادله هذه الرسوم سنوياً على مدة الإصدار.
          </div>
        </div>

        {/* Cash flow summary */}
        <div style={{ padding: 8, background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 10.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: "#475569" }}>💰 ملخّص التدفقات</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>المبلغ الاسمي:</span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{fmt(b.principal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>المُستلَم نقداً:</span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#15803d" }}>{fmt(b.net_received)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>الإدارية (دفعة):</span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#7c2d12" }}>{fmt(b.admin_fee_amount)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>البرنامج:</span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#9d174d" }}>{fmt(b.program_fee_total)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>إجمالي الفائدة:</span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#92400e" }}>{fmt(b.total_interest)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>إجمالي التكلفة:</span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: "#dc2626" }}>{fmt(b.total_cost)}</span>
            </div>
          </div>
        </div>

        {/* Explanation footer */}
        <div style={{ marginTop: 8, padding: 8, background: "#f0fdfa", borderRadius: 6, fontSize: 10, color: "#0f766e", lineHeight: 1.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>
            💡 أيهم تختار؟
          </div>
          <div>
            • <strong>على الأصل ({b.apr_simple_on_principal?.toFixed(2)}%)</strong>: للمقارنة المباشرة مع الكوبون
            <br />• <strong>على الصافي ({b.apr_simple_on_net?.toFixed(2)}%)</strong>: التكلفة الحقيقية على المبلغ المُستلَم فعلاً ← <strong>الأساس المعروض</strong>
            <br />• <strong>XIRR ({b.apr_xirr?.toFixed(2)}%)</strong>: المعيار الدولي IFRS (Compound)، يأخذ توقيت كل دفعة
            <br /><span style={{ fontSize: 9, opacity: 0.85 }}>
              {usesSchedule ? `✅ شامل توقيت ${aprInfo.schedule_count} دفعة فعلية` : "⚠️ بدون جدول دفعات — سداد نهائي مفترض"}
            </span>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{
          display: "inline-block",
          padding: "2px 8px",
          background: show ? "#16a34a" : "#dcfce7",
          color: show ? "#fff" : "#15803d",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 800,
          border: "1px solid #86efac",
          cursor: "help",
          transition: "background 0.15s, color 0.15s",
        }}
        title="APR على الصافي · مرّر الفأرة لرؤية الثلاث طرق"
      >
        {aprPct.toFixed(2)}%
      </span>
      {popupNode && createPortal(popupNode, document.body)}
    </>
  );
}

function SukukTable({ sukuks, aprBySukukId, canEdit, onChange }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
      <thead>
        <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <th style={th}>#</th>
          <th style={th}>الحالة</th>
          <th style={th}>المبلغ</th>
          <th style={th}>تاريخ مخطط</th>
          <th style={th}>تاريخ فعلي</th>
          <th style={th}>الاستحقاق</th>
          <th style={th} title="المدة بالأيام = الأشهر × 30 (30/360)">المدة (يوم)</th>
          {/* v3.3.471 — Payment count column (mirrors offers page) */}
          <th style={th} title="عدد الدفعات المجدولة للسداد على عمر الصك">دفعات</th>
          {/* v3.3.444 — New columns: admin fee (annual % + total SAR), interest (annual % + total SAR) */}
          <th style={{ ...th, background: "#fed7aa", color: "#7c2d12" }} title="رسوم إدارية كنسبة سنوية = absolute × 12 / المدة بالأشهر">إدارية %</th>
          <th style={{ ...th, background: "#fed7aa", color: "#7c2d12", textAlign: "left" }} title="مبلغ مقطوع = المبلغ × النسبة المطلقة">إدارية ريال</th>
          <th style={{ ...th, background: "#fef3c7", color: "#92400e" }} title="معدّل الفائدة (كوبون) السنوي">فائدة %</th>
          <th style={{ ...th, background: "#fef3c7", color: "#92400e", textAlign: "left" }} title="إجمالي الفائدة على عمر الصك">فائدة ريال</th>
          <th style={th} title="معدّل الفائدة الفعلي السنوي (XIRR) — شامل: رسوم البرنامج (وقت الدفع) + الرسوم الإدارية (وقت الاستلام) + توقيت الدفعات الفعلية في جدول السداد. يظهر بعد التفعيل فقط.">APR فعلي</th>
          <th style={{ ...th, textAlign: "left" }} title="المبلغ المستلم نقداً = المبلغ − الرسوم">الصافي</th>
          <th style={{ ...th, textAlign: "left" }}>إجمالي السداد</th>
          {/* v3.4.170 — نص حر يُعبأ يدويًا لكل إصدار (مثل نسبة إنجاز المشروع) */}
          <th style={{ ...th, background: "#ede9fe", color: "#4c1d95" }} title="نص حر يُعبأ يدويًا لكل إصدار — مثل نسبة إنجاز المشروع المرتبطة بالاستحقاق">الاستحقاق حسب العقد</th>
          {canEdit && <th style={th}>إجراءات</th>}
        </tr>
      </thead>
      <tbody>
        {sukuks.map(s => (
          <SukukRow
            key={s.id}
            sk={s}
            aprInfo={aprBySukukId?.[s.id]}
            canEdit={canEdit}
            onChange={onChange}
          />
        ))}
      </tbody>
    </table>
  );
}

// v3.4.170 — خلية نص حر قابلة للتحرير المباشر (حفظ بالخروج من الحقل أو Enter، إلغاء بـ Esc)
function MilestoneCell({ value, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setV(value || ""); }, [value, editing]);
  const commit = async () => {
    if (saving) return;
    const nv = v.trim();
    if (nv === (value || "").trim()) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(nv); toast.success("تم حفظ الاستحقاق حسب العقد"); setEditing(false); }
    catch (e) { toast.error(e?.response?.data?.error || "فشل الحفظ"); }
    finally { setSaving(false); }
  };
  if (!canEdit) return <span style={{ fontSize: 11, fontWeight: 700, color: value ? "#4c1d95" : "#94a3b8" }}>{value || "—"}</span>;
  if (!editing) return (
    <button type="button" onClick={() => setEditing(true)} title="اضغط للتعديل"
      style={{ width: "100%", minWidth: 100, padding: "3px 6px", background: value ? "#fff" : "transparent", border: "1px dashed #c4b5fd", borderRadius: 4, cursor: "text", fontSize: 11, fontWeight: 700, color: value ? "#4c1d95" : "#a78bfa", fontFamily: "inherit", textAlign: "center", whiteSpace: "pre-wrap" }}>
      {value || "✎ اكتب…"}
    </button>
  );
  return (
    <input autoFocus value={v} disabled={saving} onChange={e => setV(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } else if (e.key === "Escape") { setV(value || ""); setEditing(false); } }}
      placeholder="مثال: إنجاز 40% من المشروع"
      style={{ width: "100%", minWidth: 120, padding: "3px 6px", border: "1px solid #7c3aed", borderRadius: 4, fontSize: 11, fontFamily: "inherit", textAlign: "center", boxSizing: "border-box" }} />
  );
}

function SukukRow({ sk: s, aprInfo, canEdit, onChange }) {
  const [activating, setActivating] = useState(false);
  const [activateDate, setActivateDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const meta = STATUS_META[s.status] || STATUS_META.pending;

  const activate = async () => {
    setBusy(true);
    try {
      await sukuk.activateSukuk(s.id, { actual_issue_date: activateDate });
      toast.success("تم تفعيل الصك");
      setActivating(false);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.error || "فشل التفعيل"); }
    finally { setBusy(false); }
  };

  // v3.3.438 — Deactivate (revert to pending) — only allowed for active non-paid sukuks
  const deactivate = async () => {
    if (s.repayment_status === 'paid' || s.status === 'matured') {
      toast.error("لا يمكن إلغاء تفعيل صك مسدد. ألغ السداد من صفحة 'الإصدارات القائمة' أولاً.", { duration: 6000 });
      return;
    }
    if (!window.confirm(
      `⚠️ هل أنت متأكد من إلغاء تفعيل الصك #${s.sequence_number}؟\n\n` +
      `سيتم:\n` +
      `  • إرجاع الصك إلى حالة "قيد الانتظار"\n` +
      `  • مسح تاريخ التفعيل والاستحقاق\n` +
      `  • إخراجه من قائمة المديونيات القائمة\n\n` +
      `يمكنك إعادة تفعيله لاحقاً بتاريخ آخر.`
    )) return;
    setBusy(true);
    try {
      await sukuk.deactivateSukuk(s.id);
      toast.success("تم إلغاء التفعيل");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.error || "فشل الإلغاء"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("حذف هذا الصك؟")) return;
    try {
      await sukuk.deleteSukuk(s.id);
      toast.success("تم");
      onChange();
    } catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل"); }
  };

  const totalInterest = Number(s.total_interest) || 0;
  // v3.3.444 — Compute admin fee values
  const principal = Number(s.principal) || 0;
  const adminFeeRate = Number(s.admin_fee_rate) || 0;  // absolute %
  const adminFeeAmount = Number(s.admin_fee_amount) || (principal * adminFeeRate / 100);
  // Annual admin fee = absolute × 12 / tenor_months
  // v3.3.471 — Use stored tenor_months as source of truth (matches Offers page).
  // Date-derived month diff was wrong because (m.getMonth()-a.getMonth()) ignores
  // the day-of-month — e.g. 01/04/2025 → 31/12/2028 was returning 8 months in the
  // last year but the real tranche tenor (as entered in the offer) is 35 months.
  let tenorMonths = Number(s.tenor_months) || 0;
  if (tenorMonths <= 0) {
    // Fallback: derive from dates only when stored value is missing
    if (s.actual_issue_date && s.maturity_date) {
      const a = new Date(s.actual_issue_date);
      const m = new Date(s.maturity_date);
      tenorMonths = (m.getFullYear() - a.getFullYear()) * 12 + (m.getMonth() - a.getMonth());
    } else if (s.planned_issue_date && s.maturity_date) {
      const a = new Date(s.planned_issue_date);
      const m = new Date(s.maturity_date);
      tenorMonths = (m.getFullYear() - a.getFullYear()) * 12 + (m.getMonth() - a.getMonth());
    }
  }
  const adminFeeAnnualPct = tenorMonths > 0 ? (adminFeeRate * 12 / tenorMonths) : 0;
  // Coupon
  const couponAnnualPct = Number(s.coupon_rate) || 0;
  // Total interest is already in s.total_interest (computed by backend)
  // Hide APR for pending sukuks (it's only meaningful after activation with actual dates)
  const isPending = s.status === 'pending' || !s.actual_issue_date;
  return (
    <>
      <tr style={{ borderBottom: "1px solid #f1f5f9", background: s.status === 'active' ? "#f0fdf4" : s.status === 'matured' ? "#dbeafe" : "#fff" }}>
        <td style={td}>{s.sequence_number}</td>
        <td style={td}>
          <span style={{ background: meta.bg, color: meta.color, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
            {meta.icon} {meta.label}
          </span>
        </td>
        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{fmt(s.principal)}</td>
        <td style={{ ...td, color: "#64748b" }}>{fmtDateDMY(s.planned_issue_date)}</td>
        <td style={{ ...td, color: "#0f172a" }}>{s.actual_issue_date ? fmtDateDMY(s.actual_issue_date) : "—"}</td>
        <td style={{ ...td, color: s.maturity_date ? "#dc2626" : "#94a3b8" }}>{fmtDateDMY(s.maturity_date)}</td>
        {/* v3.3.456 — Tenor in days (30/360) */}
        <td style={{ ...td, textAlign: "center", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#475569", fontWeight: 600 }} title={`= ${tenorMonths || "?"} شهر × 30 يوم`}>
          {tenorMonths > 0 ? tenorMonths * 30 : "—"}
        </td>
        {/* v3.3.471 — Payment count · v3.3.474 — hover popup with schedule */}
        {/* v3.3.471 — Payment count · v3.3.474/475 — Portal-rendered hover popup */}
        <td style={{ ...td, textAlign: "center", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
          {Number(s.payment_count) > 0 ? (
            <PaymentScheduleCell
              sukuk={s}
              schedule={aprInfo?.schedule}
            />
          ) : (
            <span style={{ color: "#94a3b8", fontWeight: 600 }} title="بدون جدول دفعات (سداد نهائي واحد)">—</span>
          )}
        </td>
        {/* v3.3.444 — Admin fee % (annual) */}
        <td style={{ ...td, textAlign: "center", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fed7aa", color: "#7c2d12", fontWeight: 700 }} title={`النسبة المطلقة ${adminFeeRate.toFixed(4)}% × (12 / ${tenorMonths || "?"} شهر)`}>
          {adminFeeAnnualPct > 0 ? `${adminFeeAnnualPct.toFixed(2)}%` : "—"}
        </td>
        {/* v3.3.444 — Admin fee SAR (flat) */}
        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fed7aa", color: "#7c2d12", fontWeight: 700 }} title={`مبلغ مقطوع = ${fmt(principal)} × ${adminFeeRate.toFixed(2)}%`}>
          {fmt(adminFeeAmount)}
        </td>
        {/* v3.3.444 — Interest annual % */}
        <td style={{ ...td, textAlign: "center", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fef3c7", color: "#92400e", fontWeight: 700 }} title="معدل الكوبون السنوي">
          {couponAnnualPct > 0 ? `${couponAnnualPct.toFixed(2)}%` : "—"}
        </td>
        {/* v3.3.444 — Interest total SAR */}
        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fef3c7", color: "#92400e", fontWeight: 700 }} title="إجمالي الفائدة على مدة الصك">
          {fmt(totalInterest)}
        </td>
        <td style={{ ...td, textAlign: "center", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
          {/* v3.3.477 — Replaced static span with interactive AprBreakdownCell (Portal popup) */}
          <AprBreakdownCell aprInfo={aprInfo} sukuk={s} isPending={isPending} />
        </td>
        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0891b2", fontWeight: 700 }}>{fmt(s.net_received)}</td>
        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#dc2626", fontWeight: 700 }}>{fmt(s.total_repayment)}</td>
        {/* v3.4.170 — الاستحقاق حسب العقد (نص حر، تحرير مباشر) */}
        <td style={{ ...td, textAlign: "center", background: "#f5f3ff", minWidth: 120 }}>
          <MilestoneCell value={s.contract_milestone} canEdit={canEdit} onSave={async (v) => { await sukuk.updateSukuk(s.id, { contract_milestone: v }); onChange(); }} />
        </td>
        {canEdit && (
          <td style={{ ...td, textAlign: "center" }}>
            {s.status === 'pending' && (
              <>
                <button onClick={() => setActivating(!activating)} title="تفعيل" style={{ padding: "3px 7px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>⚡ فعّل</button>
                <button onClick={remove} title="حذف" style={{ padding: "3px 7px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>🗑</button>
              </>
            )}
            {/* v3.3.438 — For active sukuks: show deactivate button (no pay button here, that's in الإصدارات القائمة) */}
            {s.status === 'active' && s.repayment_status !== 'paid' && (
              <>
                <button onClick={deactivate} disabled={busy} title="إلغاء التفعيل (إرجاع لحالة قيد الانتظار)" style={{ padding: "3px 7px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: busy ? "wait" : "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>↶ إلغاء التفعيل</button>
                <button onClick={() => setShowDocs(!showDocs)} title="المستندات" style={{ padding: "3px 7px", background: showDocs ? "#7c3aed" : "#e0e7ff", color: showDocs ? "#fff" : "#3730a3", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>📁 ملفات</button>
              </>
            )}
            {/* v3.3.438 — Paid/matured: hint to user that they need to unpay from الإصدارات القائمة first */}
            {(s.repayment_status === 'paid' || s.status === 'matured') && (
              <>
                <span title="هذا الصك مسدد. لإلغاء التفعيل، يجب أولاً إلغاء السداد من صفحة 'الإصدارات القائمة'" style={{ padding: "3px 7px", background: "#fef3c7", color: "#92400e", border: "1px dashed #d97706", borderRadius: 4, fontSize: 10, fontWeight: 700, marginLeft: 3, cursor: "help" }}>🔒 مسدد</span>
                <button onClick={() => setShowDocs(!showDocs)} title="المستندات" style={{ padding: "3px 7px", background: showDocs ? "#7c3aed" : "#e0e7ff", color: showDocs ? "#fff" : "#3730a3", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>📁 ملفات</button>
              </>
            )}
          </td>
        )}
      </tr>
      {activating && (
        <tr style={{ background: "#fef3c7", borderBottom: "1px solid #fcd34d" }}>
          <td colSpan={canEdit ? 18 : 17} style={{ padding: "8px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>📅 تاريخ استلام النقد:</span>
              <input type="date" value={activateDate} onChange={e => setActivateDate(e.target.value)} style={{ ...inputS, width: 150 }} />
              <button onClick={activate} disabled={busy} style={{ ...btnGreen, padding: "5px 12px", fontSize: 11 }}>{busy ? "..." : "تأكيد"}</button>
              <button onClick={() => setActivating(false)} style={{ ...btnGrey, padding: "5px 12px", fontSize: 11 }}>إلغاء</button>
            </div>
          </td>
        </tr>
      )}
      {showDocs && (
        <tr style={{ background: "#faf5ff", borderBottom: "1px solid #d8b4fe" }}>
          <td colSpan={canEdit ? 18 : 17} style={{ padding: "12px 16px" }}>
            <DocumentsSection
              scope="sukuk"
              entityId={s.id}
              canEdit={canEdit}
              title={`📄 مستندات الصك #${s.sequence_number}`}
              subtitle={`Drive: العقد/إصدار_${s.sequence_number} (السند المُفعّل · الهيكلية · مستندات الإصدار)`}
              themeColor="#7c3aed"
              themeBg="#faf5ff"
              themeBorder="#d8b4fe"
            />
          </td>
        </tr>
      )}
    </>
  );
}

function AddProgramButton({ contractId, programs, onAdded }) {
  const hasMain = programs.some(p => p.program_type === 'main');
  const hasSup = programs.some(p => p.program_type === 'supplementary');
  if (hasMain && hasSup) return null;
  const add = async (type) => {
    try {
      await sukuk.createProgram(contractId, {
        program_type: type,
        name: type === 'main' ? 'البرنامج الأساسي' : 'البرنامج التكميلي',
      });
      toast.success("تم");
      onAdded();
    } catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل"); }
  };
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
      {!hasMain && <button onClick={() => add('main')} style={{ padding: "6px 14px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ برنامج أساسي</button>}
      {!hasSup && <button onClick={() => add('supplementary')} style={{ padding: "6px 14px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ برنامج تكميلي</button>}
    </div>
  );
}

function CashFlowChart({ monthly, cashflows }) {
  const data = monthly.map(m => ({
    month: monthLabel(m.month),
    "تدفق داخل (فعلي)": Math.round(m.actual_in),
    "تدفق خارج (فعلي)": Math.round(m.actual_out),
    "تدفق داخل (متوقع)": Math.round(m.projected_in),
    "تدفق خارج (متوقع)": Math.round(m.projected_out),
    "صافي تراكمي": Math.round(m.cumulative),
  }));
  return (
    <div style={{ marginTop: 16, padding: 14, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12 }}>
      <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>📊 الجدول الزمني للتدفق النقدي</div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#475569" }} angle={-30} textAnchor="end" height={50} />
          <YAxis tick={{ fontSize: 10, fill: "#475569" }} tickFormatter={v => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} width={70} />
          <Tooltip formatter={v => fmt(v)} contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine y={0} stroke="#475569" />
          <Bar dataKey="تدفق داخل (فعلي)" stackId="a" fill="#16a34a" />
          <Bar dataKey="تدفق داخل (متوقع)" stackId="a" fill="#86efac" />
          <Bar dataKey="تدفق خارج (فعلي)" stackId="a" fill="#dc2626" />
          <Bar dataKey="تدفق خارج (متوقع)" stackId="a" fill="#fca5a5" />
          <Line type="monotone" dataKey="صافي تراكمي" stroke="#1e40af" strokeWidth={2.5} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Helpers / shared styles ───
const inputS = {
  padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12,
  fontFamily: "inherit", background: "#fff",
};
const btnGreen = {
  padding: "7px 16px", background: "#16a34a", color: "#fff",
  border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
};
const btnGrey = {
  padding: "7px 16px", background: "#e2e8f0", color: "#475569",
  border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
};
const th = {
  padding: "7px 10px", textAlign: "right", fontWeight: 700, color: "#475569", fontSize: 10,
};
const td = { padding: "6px 10px", textAlign: "right" };
const modalOverlay = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
};
const modalBox = {
  background: "#fff", borderRadius: 14, width: "100%", maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
};
const modalHead = {
  padding: "14px 18px", borderBottom: "1px solid #e2e8f0",
  display: "flex", justifyContent: "space-between", alignItems: "center",
};
const modalClose = {
  background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: "#64748b",
};

function NumInput({ label, value, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", marginBottom: 3 }}>{label}</div>
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...inputS, width: "100%", textAlign: "right", direction: "ltr" }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// OFFER IMPORT MODAL (v3.3.398)
// ═══════════════════════════════════════════════════════════════════
function OfferImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().match(/\.(xlsx|xls|xlsm)$/)) {
      toast.error("الملف يجب أن يكون Excel"); return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error("اختر ملفاً"); return; }
    setBusy(true);
    try {
      const r = await sukuk.importOffer(file);
      const data = r.data;
      const tranches = data.tranches_inserted || 0;
      const payments = data.payments_inserted || 0;
      let msg = `✓ تم استيراد العرض: ${data.offer.name}`;
      if (tranches > 0) msg += ` · ${tranches} إصدار`;
      if (payments > 0) msg += ` · ${payments} دفعة`;
      toast.success(msg);
      // Pass the new offer ID up so the parent can drill into it
      onImported(data.offer?.id);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الاستيراد");
    } finally { setBusy(false); }
  };

  return (
    <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalBox, maxWidth: 540 }}>
        <div style={modalHead}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <FiUpload size={18} style={{ color: "#0891b2" }} /> استيراد عرض من Excel
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>يدعم القالب الرسمي + ملفات Rael Proposal</div>
          </div>
          <button onClick={onClose} style={modalClose}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 12px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 12, color: "#92400e" }}>💡 حمّل القالب الفارغ كنقطة بداية</div>
            <button
              onClick={async () => {
                try { await sukuk.offerTemplate(); toast.success("تم تحميل القالب"); }
                catch (e) { toast.error("فشل"); }
              }}
              style={{ padding: "5px 12px", background: "#d97706", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
            ><FiDownload size={11} /> تحميل القالب</button>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
            style={{
              border: file ? "2px solid #16a34a" : "2px dashed #cbd5e1",
              background: file ? "#f0fdf4" : "#f8fafc",
              borderRadius: 10, padding: 22, textAlign: "center", marginBottom: 14, cursor: "pointer",
            }}
            onClick={() => document.getElementById("sukuk-offer-import").click()}
          >
            <input id="sukuk-offer-import" type="file" accept=".xlsx,.xls,.xlsm" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files?.[0])} />
            {file ? (
              <div>
                <FiFile size={28} style={{ color: "#16a34a", marginBottom: 6 }} />
                <div style={{ fontWeight: 700, color: "#15803d", fontSize: 13 }}>{file.name}</div>
                <div style={{ fontSize: 11, color: "#166534", marginTop: 3 }}>{(file.size / 1024).toFixed(1)} KB · اضغط للتغيير</div>
              </div>
            ) : (
              <div>
                <FiUpload size={28} style={{ color: "#94a3b8", marginBottom: 6 }} />
                <div style={{ fontWeight: 700, color: "#475569", fontSize: 13 }}>اسحب الملف أو اضغط للاختيار</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>XLSX, XLS, XLSM (حد أقصى 10MB)</div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={busy} style={btnGrey}>إلغاء</button>
            <button onClick={submit} disabled={!file || busy} style={{ ...btnGreen, background: !file ? "#cbd5e1" : "#0891b2", cursor: !file || busy ? "not-allowed" : "pointer" }}>
              {busy ? "جاري..." : <><FiUpload size={12} /> استيراد</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONTRACT IMPORT MODAL (v3.3.398)
// ═══════════════════════════════════════════════════════════════════
function ContractImportModal({ contracts, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState("new"); // "new" | "existing"
  const [targetId, setTargetId] = useState("");
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().match(/\.(xlsx|xls|xlsm)$/)) {
      toast.error("الملف يجب أن يكون Excel"); return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error("اختر ملفاً"); return; }
    if (target === "existing" && !targetId) { toast.error("اختر العقد"); return; }
    setBusy(true);
    try {
      const tid = target === "existing" ? parseInt(targetId) : null;
      const r = await sukuk.importContract(file, tid, tid ? mode : null);
      toast.success(`تم — ${r.data.sukuks_inserted || 0} صك`);
      onImported(r.data.contract_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل");
    } finally { setBusy(false); }
  };

  return (
    <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalBox, maxWidth: 580 }}>
        <div style={modalHead}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <FiUpload size={18} style={{ color: "#7c3aed" }} /> استيراد عقد من Excel
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>عقد كامل + برامج + كل الصكوك</div>
          </div>
          <button onClick={onClose} style={modalClose}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 12px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 12, color: "#92400e" }}>💡 حمّل القالب الفارغ مع أمثلة</div>
            <button
              onClick={async () => {
                try { await sukuk.contractTemplate(); toast.success("تم تحميل القالب"); }
                catch (e) { toast.error("فشل"); }
              }}
              style={{ padding: "5px 12px", background: "#d97706", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
            ><FiDownload size={11} /> تحميل القالب</button>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
            style={{
              border: file ? "2px solid #16a34a" : "2px dashed #cbd5e1",
              background: file ? "#f0fdf4" : "#f8fafc",
              borderRadius: 10, padding: 22, textAlign: "center", marginBottom: 14, cursor: "pointer",
            }}
            onClick={() => document.getElementById("sukuk-contract-import").click()}
          >
            <input id="sukuk-contract-import" type="file" accept=".xlsx,.xls,.xlsm" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files?.[0])} />
            {file ? (
              <div>
                <FiFile size={28} style={{ color: "#16a34a", marginBottom: 6 }} />
                <div style={{ fontWeight: 700, color: "#15803d", fontSize: 13 }}>{file.name}</div>
                <div style={{ fontSize: 11, color: "#166534", marginTop: 3 }}>{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div>
                <FiUpload size={28} style={{ color: "#94a3b8", marginBottom: 6 }} />
                <div style={{ fontWeight: 700, color: "#475569", fontSize: 13 }}>اسحب الملف أو اضغط للاختيار</div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>📍 وجهة الاستيراد:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", background: target === "new" ? "#eff6ff" : "transparent", borderRadius: 6, border: target === "new" ? "1px solid #93c5fd" : "1px solid #e2e8f0" }}>
                <input type="radio" checked={target === "new"} onChange={() => setTarget("new")} />
                <span style={{ fontSize: 12 }}>🆕 عقد جديد</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", background: target === "existing" ? "#eff6ff" : "transparent", borderRadius: 6, border: target === "existing" ? "1px solid #93c5fd" : "1px solid #e2e8f0" }}>
                <input type="radio" checked={target === "existing"} onChange={() => setTarget("existing")} />
                <span style={{ fontSize: 12 }}>🎯 تحديث عقد:</span>
                <select value={targetId} disabled={target !== "existing"} onChange={(e) => setTargetId(e.target.value)} style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1", fontFamily: "inherit", flex: 1 }}>
                  <option value="">اختر...</option>
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            </div>
          </div>

          {target === "existing" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>⚙️ معالجة الصكوك الموجودة:</div>
              <div style={{ display: "flex", gap: 8 }}>
                <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 10px", background: mode === "replace" ? "#fef2f2" : "transparent", borderRadius: 6, border: mode === "replace" ? "1px solid #fca5a5" : "1px solid #e2e8f0" }}>
                  <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b" }}>🗑️ استبدال</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>حذف القديم</div>
                  </div>
                </label>
                <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 10px", background: mode === "append" ? "#f0fdf4" : "transparent", borderRadius: 6, border: mode === "append" ? "1px solid #86efac" : "1px solid #e2e8f0" }}>
                  <input type="radio" checked={mode === "append"} onChange={() => setMode("append")} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>➕ إضافة</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>الاحتفاظ بالقديم</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={busy} style={btnGrey}>إلغاء</button>
            <button onClick={submit} disabled={!file || busy || (target === "existing" && !targetId)} style={{ ...btnGreen, background: (!file || busy || (target === "existing" && !targetId)) ? "#cbd5e1" : "#7c3aed", cursor: (!file || busy || (target === "existing" && !targetId)) ? "not-allowed" : "pointer" }}>
              {busy ? "جاري..." : <><FiUpload size={12} /> استيراد</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// OFFER DETAIL — جدول الإصدارات التفصيلي (v3.3.399)
// ═══════════════════════════════════════════════════════════════════
function OfferDetail({ offerId, canEdit, canExport, canImport, onBack, onConvert, onSelectTranche }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details"); // "details" | "documents"
  const [reimporting, setReimporting] = useState(false);
  const reimportInputRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.getOffer(offerId);
      setData(r.data);
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [offerId]);

  if (loading || !data) return <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>;
  const { offer, tranches } = data;
  const committed = tranches.filter(t => !t.is_optional);
  const optional = tranches.filter(t => t.is_optional);
  const committedTotal = committed.reduce((s, t) => s + Number(t.principal || 0), 0);
  const optionalTotal = optional.reduce((s, t) => s + Number(t.principal || 0), 0);
  // v3.3.442 — Calculate total admin fees (FLAT one-time per tranche = principal × admin_fee_rate%)
  const adminFeeCommitted = committed.reduce((s, t) => s + (Number(t.principal || 0) * Number(t.admin_fee_rate || offer.admin_fee_rate || 0) / 100), 0);
  const adminFeeOptional = optional.reduce((s, t) => s + (Number(t.principal || 0) * Number(t.admin_fee_rate || offer.admin_fee_rate || 0) / 100), 0);
  const adminFeeTotal = adminFeeCommitted + adminFeeOptional;
  const isConverted = offer.status === 'converted';

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
        <button onClick={onBack} style={{ padding: "6px 12px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FiArrowRight size={12} /> العودة لقائمة العروض
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{offer.name}</div>
          {offer.issuing_company && <div style={{ fontSize: 11, color: "#64748b" }}>🏢 {offer.issuing_company}</div>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {canExport && (
            <button
              onClick={async () => {
                try { await sukuk.exportOffer(offerId); toast.success("تم"); }
                catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل"); }
              }}
              style={{ padding: "6px 14px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <FiDownload size={12} /> تصدير
            </button>
          )}
          {/* v3.3.411 — Re-import: replace tranches/payments from updated Excel */}
          {canImport && !isConverted && (
            <>
              <input
                ref={reimportInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  if (!window.confirm(
                    "⚠ سيتم استبدال جميع الإصدارات والدفعات الحالية ببيانات الملف الجديد.\n\n" +
                    "هل تريد المتابعة؟\n\n" +
                    "ملاحظة: المستندات المرفوعة على Drive لن تتأثر."
                  )) return;
                  setReimporting(true);
                  try {
                    const r = await sukuk.reimportOffer(file, offerId);
                    const d = r.data;
                    let msg = "✓ تم تحديث العرض";
                    if (d.tranches_inserted) msg += ` · ${d.tranches_inserted} إصدار`;
                    if (d.payments_inserted) msg += ` · ${d.payments_inserted} دفعة`;
                    toast.success(msg);
                    load();
                  } catch (err) {
                    console.error(err);
                    toast.error(err?.response?.data?.error || "فشل الاستيراد");
                  } finally {
                    setReimporting(false);
                  }
                }}
              />
              <button
                onClick={() => reimportInputRef.current?.click()}
                disabled={reimporting}
                title="استبدال الإصدارات والدفعات بمحتوى ملف Excel جديد"
                style={{
                  padding: "6px 14px",
                  background: reimporting ? "#94a3b8" : "#7c3aed",
                  color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
                  cursor: reimporting ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <FiUpload size={12} /> {reimporting ? "جاري الاستيراد..." : "استيراد"}
              </button>
            </>
          )}
          {canEdit && !isConverted && (
            <button
              onClick={onConvert}
              disabled={tranches.length === 0}
              style={{
                padding: "6px 14px",
                background: tranches.length === 0 ? "#cbd5e1" : "#16a34a",
                color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: tranches.length === 0 ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <FiCheck size={12} /> تحويل إلى عقد
            </button>
          )}
        </div>
      </div>

      {/* v3.3.409 — Tab bar */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e8f0", marginBottom: 14 }}>
        <button
          onClick={() => setTab("details")}
          style={{
            padding: "8px 18px",
            background: tab === "details" ? "#0891b2" : "transparent",
            color: tab === "details" ? "#fff" : "#475569",
            border: "none",
            borderRadius: "6px 6px 0 0",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          📋 التفاصيل والإصدارات
        </button>
        <button
          onClick={() => setTab("documents")}
          style={{
            padding: "8px 18px",
            background: tab === "documents" ? "#0891b2" : "transparent",
            color: tab === "documents" ? "#fff" : "#475569",
            border: "none",
            borderRadius: "6px 6px 0 0",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          📁 المستندات
        </button>
      </div>

      {tab === "documents" ? (
        <DocumentsSection
          scope="offer"
          entityId={offerId}
          canEdit={canEdit && !isConverted}
          title="📄 مستندات العرض"
          subtitle={`Drive: Financing/${offer.name || ""}/العرض`}
          themeColor="#0891b2"
          themeBg="#ecfeff"
          themeBorder="#a5f3fc"
        />
      ) : (
        <>
      {/* Hero: Effective APR for the entire program (v3.3.403) */}
      {data.program_apr_pct !== null && data.program_apr_pct !== undefined && (
        <div style={{
          marginBottom: 12, padding: "14px 18px",
          background: "linear-gradient(135deg, #581c87 0%, #7c3aed 100%)",
          borderRadius: 12, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 4px 14px rgba(124,58,237,0.25)",
        }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, letterSpacing: "0.5px" }}>📊 EFFECTIVE PROGRAM APR</div>
            <div style={{ fontSize: 28, fontWeight: 800, marginTop: 2, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
              {data.program_apr_pct.toFixed(2)}%
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              {/* v3.3.445 — Weighted average for activated sukuks */}
              متوسط APR مرجح بالمبلغ للإصدارات المُفعَّلة فقط
            </div>
          </div>
          <div style={{ textAlign: "left", direction: "ltr", fontSize: 11, opacity: 0.9 }}>
            <div>💰 رسوم البرنامج: {fmt(data.upfront_program_fee || 0)} ريال</div>
            <div>📅 تُدفع: {data.fee_pay_date || "غير محدد"}</div>
            <div>✅ يعتمد على الإصدارات النشطة والمسددة فقط</div>
          </div>
        </div>
      )}

      {/* v3.3.416 — Prominent Program Fee Amount Box */}
      {/* v3.3.442 — Hero Boxes Row: Upfront program fee + Total admin fees */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {/* Upfront program fee */}
        <div style={{
          padding: "14px 18px",
          background: "linear-gradient(135deg, #92400e 0%, #d97706 100%)",
          borderRadius: 12, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 4px 14px rgba(217,119,6,0.25)",
          flexWrap: "wrap",
          gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, letterSpacing: "0.5px" }}>💰 رسوم البرنامج (Upfront)</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
              {fmt(data.upfront_program_fee || 0)} <span style={{ fontSize: 13, opacity: 0.85 }}>ريال</span>
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              {Number(offer.program_fee_rate || 0).toFixed(2)}% × الإصدارات الأساسية
            </div>
          </div>
          <div style={{ textAlign: "left", direction: "ltr", fontSize: 10, opacity: 0.95 }}>
            <div>📊 النسبة: <strong>{Number(offer.program_fee_rate || 0).toFixed(2)}%</strong></div>
            <div>📌 على: <strong>{fmt(committedTotal)}</strong></div>
            <div>📅 الدفع: <strong>{data.fee_pay_date ? (() => { const d = new Date(data.fee_pay_date); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })() : "—"}</strong></div>
          </div>
        </div>

        {/* v3.3.442 — Total admin fees */}
        <div style={{
          padding: "14px 18px",
          background: "linear-gradient(135deg, #7c2d12 0%, #c2410c 100%)",
          borderRadius: 12, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 4px 14px rgba(194,65,12,0.25)",
          flexWrap: "wrap",
          gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, letterSpacing: "0.5px" }}>📋 الرسوم الإدارية (إجمالي)</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
              {fmt(adminFeeTotal)} <span style={{ fontSize: 13, opacity: 0.85 }}>ريال</span>
            </div>
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              مبلغ مقطوع لكل إصدار = مبلغه × نسبة الإدارية
            </div>
          </div>
          <div style={{ textAlign: "left", direction: "ltr", fontSize: 10, opacity: 0.95 }}>
            <div>📌 الأساسية: <strong>{fmt(adminFeeCommitted)}</strong></div>
            {adminFeeOptional > 0 && <div>🎯 الاختيارية: <strong>{fmt(adminFeeOptional)}</strong></div>}
            <div>📊 سنوي مرجح: <strong>{annualizeAdminFee(offer.admin_fee_rate, offer.tenor_months).toFixed(2)}%</strong></div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, marginBottom: 14 }}>
        <Box label="إصدارات أساسية" value={`${committed.length}`} color="#0891b2" bg="#ecfeff" sub={fmt(committedTotal)} />
        <Box label="إصدارات اختيارية" value={`${optional.length}`} color="#7c3aed" bg="#faf5ff" sub={fmt(optionalTotal)} />
        <Box label="إجمالي العرض" value={fmt(committedTotal + optionalTotal)} color="#0f172a" bg="#f1f5f9" />
        <Box
          label="الكوبون اليومي %"
          value={`${(offer.coupon_daily_rate !== null && offer.coupon_daily_rate !== undefined ? Number(offer.coupon_daily_rate) : (Number(offer.coupon_rate) || 0) / 365).toFixed(4)}%`}
          color="#0891b2"
          bg="#ecfeff"
          sub={`سنوي: ${Number(offer.coupon_rate || 0).toFixed(4)}%`}
        />
        <Box label="رسوم البرنامج %" value={`${offer.program_fee_rate}%`} color="#7c2d12" bg="#fef3c7" />
        <Box
          label="إدارية سنوي %"
          value={`${annualizeAdminFee(offer.admin_fee_rate, offer.tenor_months).toFixed(4)}%`}
          color="#7c2d12"
          bg="#fed7aa"
          sub={`مطلق: ${Number(offer.admin_fee_rate || 0).toFixed(4)}% · ${offer.tenor_months || 0} شهر`}
        />
      </div>

      {/* Two sections: committed + optional */}
      <TrancheSection
        title="📌 الإصدارات الأساسية"
        color="#0891b2"
        bg="#ecfeff"
        border="#a5f3fc"
        offer={offer}
        tranches={committed}
        aprByTrancheId={data.apr_by_tranche || {}}
        isOptional={false}
        canEdit={canEdit && !isConverted}
        onChange={load}
        onSelectTranche={onSelectTranche}
      />

      <TrancheSection
        title="➕ الإصدارات الاختيارية"
        color="#7c3aed"
        bg="#faf5ff"
        border="#d8b4fe"
        offer={offer}
        tranches={optional}
        aprByTrancheId={data.apr_by_tranche || {}}
        isOptional={true}
        canEdit={canEdit && !isConverted}
        onChange={load}
        onSelectTranche={onSelectTranche}
      />

      {/* v3.3.404 — Gantt chart of all tranches */}
      {tranches.length > 0 && (
        <TranchesGanttChart
          tranches={tranches}
          aprByTrancheId={data.apr_by_tranche || {}}
          feePayDate={data.fee_pay_date}
          upfrontProgramFee={data.upfront_program_fee}
        />
      )}

      {isConverted && (
        <div style={{ marginTop: 12, padding: 10, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, color: "#15803d", fontSize: 12, textAlign: "center", fontWeight: 700 }}>
          ✓ هذا العرض تحوّل إلى عقد ولا يمكن تعديله بعد الآن
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ─── Section: list of tranches (committed or optional) ───
function TrancheSection({ title, color, bg, border, offer, tranches, aprByTrancheId, isOptional, canEdit, onChange, onSelectTranche }) {
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);

  return (
    <div style={{ marginBottom: 14, background: "#fff", border: `1.5px solid ${border}`, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: bg, borderBottom: `1px solid ${border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color }}>{title}</div>
          <div style={{ fontSize: 11, color, marginTop: 2, opacity: 0.85 }}>
            {tranches.length} إصدار · إجمالي {fmt(tranches.reduce((s, t) => s + Number(t.principal || 0), 0))}
          </div>
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setAdding(!adding); setGenerating(false); }} style={{ padding: "5px 12px", background: adding ? "#e2e8f0" : color, color: adding ? "#475569" : "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              {adding ? "إلغاء" : "+ إصدار"}
            </button>
            <button onClick={() => { setGenerating(!generating); setAdding(false); }} style={{ padding: "5px 12px", background: generating ? "#e2e8f0" : "#0891b2", color: generating ? "#475569" : "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <FiZap size={11} /> توليد جماعي
            </button>
          </div>
        )}
      </div>

      {adding && (
        <NewTrancheForm
          offer={offer}
          isOptional={isOptional}
          onCancel={() => setAdding(false)}
          onCreated={() => { setAdding(false); onChange(); }}
        />
      )}
      {generating && (
        <GenerateOfferScheduleForm
          offer={offer}
          isOptional={isOptional}
          existing={tranches}
          onCancel={() => setGenerating(false)}
          onDone={() => { setGenerating(false); onChange(); }}
        />
      )}

      {tranches.length === 0 && !adding && !generating ? (
        <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
          لا توجد إصدارات — اضغط "+ إصدار" أو "توليد جماعي"
        </div>
      ) : tranches.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {/* v3.3.414 — Reordered columns per user spec:
                    1. #         2. المبلغ         3. تاريخ الإصدار  4. المدة
                    5. برنامج %  6. إجمالي البرنامج 7. إدارية %      8. إدارية ريال
                    9. كوبون يومي  10. سنوي  11. مطلق
                    12. APR فعلي  13. الصافي  14. الفائدة  15. السداد
                    16. الاستحقاق  17. شروط الإصدار  18. إجراءات
                */}
                <th style={th}>#</th>
                <th style={th}>المبلغ</th>
                <th style={th}>تاريخ الإصدار</th>
                <th style={th} title="المدة بالشهر (شهر = 30 يوم)">المدة (شهر)</th>
                <th style={th} title="المدة بالأيام = الأشهر × 30 (شركة التمويل: 30/360)">المدة (يوم)</th>
                {/* Program fee group (yellow) */}
                <th style={{ ...th, background: "#fef3c7", color: "#92400e" }} title="نسبة رسوم البرنامج">برنامج %</th>
                <th style={{ ...th, background: "#fef3c7", color: "#92400e", textAlign: "left" }} title="مبلغ البرنامج = المبلغ × النسبة">إجمالي البرنامج</th>
                {/* Admin fee group (orange) — v3.3.422: added annualized rate */}
                <th style={{ ...th, background: "#fed7aa", color: "#9a3412" }} title="نسبة الرسوم الإدارية المقطوعة من المبلغ">إدارية %</th>
                <th style={{ ...th, background: "#fed7aa", color: "#9a3412", textAlign: "left" }} title="مبلغ مقطوع = النسبة × المبلغ">إدارية ريال</th>
                <th style={{ ...th, background: "#fed7aa", color: "#9a3412" }} title="النسبة السنوية المعادلة = الإدارية × (12 / المدة بالأشهر) — للمقارنة مع الكوبون السنوي">إدارية سنوي %</th>
                {/* Coupon group (cyan) */}
                <th style={{ ...th, background: "#cffafe", color: "#0e7490" }} title="الكوبون اليومي — هذا هو المُدخَل الأساسي (ما يُكتب في العرض)">كوبون يومي %</th>
                <th style={{ ...th, background: "#cffafe", color: "#0e7490" }} title="نسبة الكوبون السنوية = اليومي × 365">كوبون سنوي %</th>
                <th style={{ ...th, background: "#cffafe", color: "#0e7490" }} title="النسبة المطلقة على الإصدار = اليومي × عدد أيام الإصدار">كوبون مطلق %</th>
                {/* APR (purple) */}
                <th style={{ ...th, background: "#f3e8ff", color: "#581c87" }} title="معدّل الفائدة الفعلي السنوي شامل توقيت رسوم البرنامج">APR فعلي</th>
                {/* Outputs (green) */}
                <th style={{ ...th, textAlign: "left" }} title="المبلغ المستلم نقداً = المبلغ − رسوم إدارية − رسوم برنامج (للاختياري)">الصافي</th>
                <th style={{ ...th, textAlign: "left" }}>الفائدة</th>
                <th style={{ ...th, textAlign: "left" }}>السداد</th>
                <th style={th}>الاستحقاق</th>
                <th style={th} title="شروط الإصدار · ملاحظات">شروط الإصدار</th>
                {canEdit && <th style={th}>إجراءات</th>}
              </tr>
            </thead>
            <tbody>
              {tranches.map(t => (
                <TrancheRow
                  key={t.id}
                  tranche={t}
                  offer={offer}
                  aprInfo={aprByTrancheId?.[t.id]}
                  canEdit={canEdit}
                  onChange={onChange}
                  onSelect={onSelectTranche}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TrancheRow({ tranche: t, offer, aprInfo, canEdit, onChange, onSelect }) {
  const [editing, setEditing] = useState(false);
  const DAYS_PER_YEAR = 360; // v3.3.456 — 30/360 day-count convention (financing company requirement)
  // v3.3.413 — Daily rate is the master input; annual derived
  const initialDaily = t.coupon_daily_rate !== null && t.coupon_daily_rate !== undefined
    ? Number(t.coupon_daily_rate)
    : (Number(t.coupon_rate) || 0) / DAYS_PER_YEAR;
  const [b, setB] = useState({
    principal: t.principal,
    planned_issue_date: t.planned_issue_date ? String(t.planned_issue_date).slice(0, 10) : "",
    tenor_months: t.tenor_months,
    coupon_daily_rate: initialDaily,
    admin_fee_rate: t.admin_fee_rate,
    program_fee_rate: t.program_fee_rate,
    notes: t.notes || "",
  });

  const principal = Number(t.principal) || 0;
  const cr = Number(t.coupon_rate) || 0;
  const tm = Number(t.tenor_months) || 24;
  const totalInterest = (principal * cr / 100) * (tm / 12);
  const totalRepayment = principal + totalInterest;
  const maturity = t.planned_issue_date ? (() => {
    const d = new Date(t.planned_issue_date);
    d.setMonth(d.getMonth() + tm);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  })() : "—";

  // Live derived values for edit form
  const editDaily = parseFloat(b.coupon_daily_rate) || 0;
  const editAnnual = editDaily * DAYS_PER_YEAR;
  const editTenorDays = (() => {
    if (b.planned_issue_date && b.tenor_months) {
      const d = new Date(b.planned_issue_date);
      const start = d.getTime();
      d.setMonth(d.getMonth() + parseInt(b.tenor_months));
      return Math.round((d.getTime() - start) / 86400000);
    }
    return parseInt(b.tenor_months) * 30;
  })();
  const editAbsolute = editDaily * editTenorDays;

  const save = async () => {
    try {
      await sukuk.updateOfferTranche(t.id, {
        principal: parseFloat(b.principal) || 0,
        planned_issue_date: b.planned_issue_date || null,
        tenor_months: parseInt(b.tenor_months) || 24,
        coupon_daily_rate: parseFloat(b.coupon_daily_rate) || 0,
        admin_fee_rate: parseFloat(b.admin_fee_rate) || 0,
        program_fee_rate: parseFloat(b.program_fee_rate) || 0,
        notes: b.notes || null,
      });
      setEditing(false);
      onChange();
      toast.success("تم");
    } catch (e) { toast.error("فشل"); }
  };
  const remove = async () => {
    if (!window.confirm(`حذف الإصدار #${t.sequence_number}؟`)) return;
    try { await sukuk.deleteOfferTranche(t.id); onChange(); toast.success("تم"); }
    catch (e) { console.error(e); toast.error(e?.response?.data?.error || "فشل"); }
  };

  if (editing) {
    return (
      <tr style={{ background: "#fef9c3", borderBottom: "1px solid #fde68a" }}>
        {/* 1. # */}
        <td style={td}>{t.sequence_number}</td>
        {/* 2. المبلغ */}
        <td style={td}><input type="number" value={b.principal} onChange={e => setB(p => ({ ...p, principal: e.target.value }))} style={miniInput} /></td>
        {/* 3. تاريخ الإصدار */}
        <td style={td}><input type="date" value={b.planned_issue_date} onChange={e => setB(p => ({ ...p, planned_issue_date: e.target.value }))} style={{ ...miniInput, width: 120 }} /></td>
        {/* 4. المدة */}
        <td style={td}><input type="number" value={b.tenor_months} onChange={e => setB(p => ({ ...p, tenor_months: e.target.value }))} style={{ ...miniInput, width: 50 }} /></td>
        {/* 5. برنامج % */}
        <td style={td}><input type="number" step="0.01" value={b.program_fee_rate} onChange={e => setB(p => ({ ...p, program_fee_rate: e.target.value }))} style={{ ...miniInput, width: 60 }} /></td>
        {/* 6. إجمالي البرنامج (محسوب) */}
        <td style={{ ...td, fontSize: 10, color: "#92400e", direction: "ltr" }}>(محسوب)</td>
        {/* 7. إدارية % */}
        <td style={td}><input type="number" step="0.01" value={b.admin_fee_rate} onChange={e => setB(p => ({ ...p, admin_fee_rate: e.target.value }))} style={{ ...miniInput, width: 60 }} /></td>
        {/* 8. إدارية ريال (محسوب) */}
        <td style={{ ...td, fontSize: 10, color: "#92400e", direction: "ltr" }}>(محسوب)</td>
        {/* v3.3.422 — إدارية سنوي % (live preview, read-only) */}
        <td style={{ ...td, fontSize: 10, color: "#7c2d12", direction: "ltr", fontWeight: 700 }}>
          ← {(() => {
            const tmNum = parseInt(b.tenor_months) || 0;
            const afNum = parseFloat(b.admin_fee_rate) || 0;
            return tmNum > 0 ? (afNum * 12 / tmNum).toFixed(4) : "0.0000";
          })()}%
        </td>
        {/* 9. كوبون يومي — master input */}
        <td style={td}>
          <input
            type="number"
            step="0.0001"
            value={b.coupon_daily_rate}
            onChange={e => setB(p => ({ ...p, coupon_daily_rate: e.target.value }))}
            style={{ ...miniInput, width: 80, background: "#ecfeff", borderColor: "#0891b2", fontWeight: 800 }}
            title="المُدخَل الأساسي · يُحسب منه السنوي والمطلق"
          />
        </td>
        {/* 10. سنوي — read-only derived */}
        <td style={{ ...td, fontSize: 10, color: "#475569", direction: "ltr" }}>
          ← {editAnnual.toFixed(4)}%
        </td>
        {/* 11. مطلق — read-only derived */}
        <td style={{ ...td, fontSize: 10, color: "#1e40af", direction: "ltr", fontWeight: 700 }}>
          ← {editAbsolute.toFixed(4)}%
        </td>
        {/* 12-16. APR + outputs (محسوب) */}
        <td colSpan={5} style={{ ...td, fontSize: 10, color: "#92400e" }}>(تُحسب تلقائياً)</td>
        {/* 17. شروط الإصدار (notes) */}
        <td style={td}>
          <input
            type="text"
            value={b.notes}
            onChange={e => setB(p => ({ ...p, notes: e.target.value }))}
            placeholder="شروط الإصدار..."
            style={{ ...miniInput, width: 160 }}
          />
        </td>
        {canEdit && (
          <td style={{ ...td, textAlign: "center" }}>
            <button onClick={save} style={{ padding: "3px 8px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>✓</button>
            <button onClick={() => setEditing(false)} style={{ padding: "3px 8px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>✕</button>
          </td>
        )}
      </tr>
    );
  }
  // v3.3.412 — Compute display values for new dual-display columns
  const adminFeeRateNum = Number(t.admin_fee_rate) || 0;
  const adminFeeAmount = principal * adminFeeRateNum / 100;  // FLAT one-time, not annualized
  // v3.3.413 — Coupon: daily is master; annual = daily × 365; absolute = daily × tenor_days
  // (DAYS_PER_YEAR already declared above in component)
  // Server-computed values are most accurate (uses actual issue→maturity days)
  const couponDailyPct = aprInfo?.coupon_daily_rate !== undefined
    ? Number(aprInfo.coupon_daily_rate)
    : (Number(t.coupon_daily_rate) || (cr / DAYS_PER_YEAR));
  const couponAnnualPct = aprInfo?.coupon_annual_rate !== undefined
    ? Number(aprInfo.coupon_annual_rate)
    : cr;
  const tenorDaysComputed = aprInfo?.tenor_days || (tm * 30);
  const couponAbsolutePct = aprInfo?.coupon_absolute_rate !== undefined
    ? Number(aprInfo.coupon_absolute_rate)
    : (couponDailyPct * tenorDaysComputed);
  // Use server-computed values when available, fallback to local computation
  const netReceived = aprInfo?.net_received !== undefined
    ? Number(aprInfo.net_received)
    : (() => {
        const progFee = t.is_optional ? principal * (Number(t.program_fee_rate) || 0) / 100 : 0;
        return principal - adminFeeAmount - progFee;
      })();
  // v3.3.417 — Compute program fee amount per tranche from ITS OWN rate
  // (works for both optional and committed; if rate=0, amount=0 correctly)
  const programFeeRateNum = Number(t.program_fee_rate) || 0;
  const programFeeAmount = principal * programFeeRateNum / 100;

  return (
    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
      {/* 1. # */}
      <td style={td}>
        {onSelect ? (
          <button
            onClick={() => onSelect(t.id)}
            title="عرض جدول الدفعات"
            style={{
              padding: "2px 8px",
              background: aprInfo?.has_custom_payments ? "#dbeafe" : "#f1f5f9",
              color: aprInfo?.has_custom_payments ? "#1e40af" : "#475569",
              border: aprInfo?.principal_mismatch ? "1px solid #f59e0b" : "1px solid transparent",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 800,
              cursor: "pointer",
              direction: "ltr",
            }}
          >
            #{t.sequence_number}
            {aprInfo?.has_custom_payments && (
              <span style={{ marginRight: 3, fontSize: 9, opacity: 0.85 }}>
                ({aprInfo.payment_count})
              </span>
            )}
            {aprInfo?.principal_mismatch && <span style={{ marginRight: 3 }}>⚠</span>}
            {!aprInfo?.has_custom_payments && <span style={{ marginRight: 3, fontSize: 9, opacity: 0.6 }}>📅</span>}
          </button>
        ) : (
          t.sequence_number
        )}
      </td>
      {/* 2. المبلغ */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{fmt(principal)}</td>
      {/* 3. تاريخ الإصدار */}
      <td style={{ ...td, color: "#475569" }}>{fmtDateDMY(t.planned_issue_date)}</td>
      {/* 4. المدة */}
      <td style={td}>{tm}</td>
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#475569", fontWeight: 600 }} title={`= ${tm} × 30 يوم`}>{tm * 30}</td>
      {/* 5. برنامج % */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fef3c7", color: "#92400e" }} title="نسبة رسوم البرنامج">{programFeeRateNum}%</td>
      {/* 6. إجمالي البرنامج */}
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fef3c7", color: "#78350f", fontWeight: 700 }}
          title={t.is_optional ? `مبلغ البرنامج (اختياري) = ${programFeeRateNum}% × ${fmt(principal)}` : `حصة من رسوم البرنامج المدفوعة upfront`}>
        {fmt(programFeeAmount)}
      </td>
      {/* 7. إدارية % */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fed7aa", color: "#9a3412" }} title="نسبة الرسوم الإدارية المقطوعة">{adminFeeRateNum}%</td>
      {/* 8. إدارية ريال */}
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fed7aa", color: "#7c2d12", fontWeight: 700 }} title={`مبلغ مقطوع = ${adminFeeRateNum}% × ${fmt(principal)}`}>{fmt(adminFeeAmount)}</td>
      {/* v3.3.422 — إدارية سنوي %: annualized admin fee rate (annualized over tenor) */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#fed7aa", color: "#7c2d12", fontWeight: 700 }} title={`النسبة السنوية المعادلة = ${adminFeeRateNum}% × (12 / ${tm}) — للمقارنة مع الكوبون السنوي`}>{(tm > 0 ? (adminFeeRateNum * 12 / tm) : 0).toFixed(4)}%</td>
      {/* 9. كوبون يومي % */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0891b2", fontWeight: 800, background: "#cffafe" }} title={`المُدخَل الأساسي · ${tenorDaysComputed} يوم`}>{couponDailyPct.toFixed(4)}%</td>
      {/* 10. كوبون سنوي % */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#cffafe", color: "#0e7490" }} title="السنوي = اليومي × 365">{couponAnnualPct.toFixed(4)}%</td>
      {/* 11. كوبون مطلق % */}
      <td style={{ ...td, direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#1e40af", fontWeight: 700, background: "#cffafe" }} title={`المطلق = ${couponDailyPct.toFixed(4)}% × ${tenorDaysComputed} يوم`}>{couponAbsolutePct.toFixed(4)}%</td>
      {/* 12. APR فعلي */}
      <td style={{ ...td, textAlign: "center", direction: "ltr", fontVariantNumeric: "tabular-nums", background: "#f3e8ff" }}>
        {aprInfo && aprInfo.apr_pct !== null && aprInfo.apr_pct !== undefined ? (
          <span
            title={aprInfo.program_fee_share > 0 ? `حصة من رسوم البرنامج: ${fmt(aprInfo.program_fee_share)}` : (t.is_optional ? "اختياري — رسوم البرنامج تُدفع عند الإصدار" : "بدون حصة من رسوم البرنامج")}
            style={{
              display: "inline-block",
              padding: "2px 8px",
              background: "#faf5ff",
              color: "#581c87",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 800,
              border: "1px solid #d8b4fe",
            }}
          >
            {aprInfo.apr_pct.toFixed(2)}%
          </span>
        ) : (
          <span style={{ color: "#94a3b8", fontSize: 10 }}>—</span>
        )}
      </td>
      {/* 13. الصافي */}
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#0891b2", fontWeight: 700 }}>{fmt(netReceived)}</td>
      {/* 14. الفائدة */}
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#15803d" }}>{fmt(totalInterest)}</td>
      {/* 15. السداد */}
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#dc2626", fontWeight: 700 }}>{fmt(totalRepayment)}</td>
      {/* 16. الاستحقاق */}
      <td style={{ ...td, color: maturity !== "—" ? "#dc2626" : "#94a3b8" }}>{maturity}</td>
      {/* 17. شروط الإصدار (notes) */}
      <td style={{ ...td, color: "#475569", fontSize: 10, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.notes || ""}>
        {t.notes ? (t.notes.length > 28 ? t.notes.slice(0, 28) + "…" : t.notes) : <span style={{ color: "#cbd5e1" }}>—</span>}
      </td>
      {canEdit && (
        <td style={{ ...td, textAlign: "center" }}>
          {onSelect && (
            <button onClick={() => onSelect(t.id)} title="جدول الدفعات" style={{ padding: "3px 7px", background: "#dbeafe", color: "#1e40af", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>📅</button>
          )}
          <button onClick={() => setEditing(true)} title="تعديل" style={{ padding: "3px 7px", background: "#e0e7ff", color: "#3730a3", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>✎</button>
          <button onClick={remove} title="حذف" style={{ padding: "3px 7px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>🗑</button>
        </td>
      )}
    </tr>
  );
}

function NewTrancheForm({ offer, isOptional, onCancel, onCreated }) {
  const DAYS_PER_YEAR = 360; // v3.3.456 — 30/360 day-count convention (financing company requirement)
  // v3.3.413 — Daily rate is master; default from offer's daily, else from annual/365
  const offerDaily = offer.coupon_daily_rate !== null && offer.coupon_daily_rate !== undefined
    ? Number(offer.coupon_daily_rate)
    : ((Number(offer.coupon_rate) || 13) / DAYS_PER_YEAR);
  const [b, setB] = useState({
    principal: offer.tranche_size || 750000,
    planned_issue_date: new Date().toISOString().slice(0, 10),
    tenor_months: offer.tenor_months || 24,
    coupon_daily_rate: offerDaily,
    admin_fee_rate: offer.admin_fee_rate || 1.2,
    program_fee_rate: offer.program_fee_rate || 1.5,
    notes: "",
  });
  const set = (k, v) => setB(p => ({ ...p, [k]: v }));

  // Live derived: annual + absolute
  const dailyNum = parseFloat(b.coupon_daily_rate) || 0;
  const annualPreview = dailyNum * DAYS_PER_YEAR;
  const tenorDaysPreview = (() => {
    if (b.planned_issue_date && b.tenor_months) {
      const d = new Date(b.planned_issue_date);
      const start = d.getTime();
      d.setMonth(d.getMonth() + parseInt(b.tenor_months));
      return Math.round((d.getTime() - start) / 86400000);
    }
    return parseInt(b.tenor_months) * 30;
  })();
  const absolutePreview = dailyNum * tenorDaysPreview;

  const submit = async () => {
    if (!b.principal || parseFloat(b.principal) <= 0) { toast.error("المبلغ مطلوب"); return; }
    try {
      await sukuk.addOfferTranche(offer.id, {
        principal: parseFloat(b.principal),
        planned_issue_date: b.planned_issue_date || null,
        tenor_months: parseInt(b.tenor_months) || 24,
        coupon_daily_rate: parseFloat(b.coupon_daily_rate) || 0,
        admin_fee_rate: parseFloat(b.admin_fee_rate) || 0,
        program_fee_rate: parseFloat(b.program_fee_rate) || 0,
        is_optional: isOptional,
        notes: b.notes || null,
      });
      toast.success("تم");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };
  return (
    <div style={{ padding: 12, background: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1.3fr 0.7fr 1fr 0.9fr 0.9fr 1.3fr auto", gap: 6, alignItems: "end" }}>
        <NumInput label="المبلغ" value={b.principal} onChange={v => set("principal", v)} />
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", marginBottom: 3 }}>تاريخ الإصدار</div>
          <input type="date" value={b.planned_issue_date} onChange={e => set("planned_issue_date", e.target.value)} style={{ ...inputS, width: "100%" }} />
        </div>
        <NumInput label="المدة" value={b.tenor_months} onChange={v => set("tenor_months", v)} />
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0891b2", marginBottom: 3 }}>كوبون يومي %</div>
          <input
            type="number"
            step="0.0001"
            value={b.coupon_daily_rate}
            onChange={e => set("coupon_daily_rate", e.target.value)}
            style={{ ...inputS, width: "100%", background: "#ecfeff", borderColor: "#0891b2", fontWeight: 800 }}
            title="المُدخَل الأساسي"
          />
          <div style={{ fontSize: 9, color: "#64748b", marginTop: 2, direction: "ltr", textAlign: "left" }}>
            سنوي: {annualPreview.toFixed(4)}% · مطلق: {absolutePreview.toFixed(4)}%
          </div>
        </div>
        <NumInput label="إدارية %" value={b.admin_fee_rate} onChange={v => set("admin_fee_rate", v)} />
        <NumInput label="برنامج %" value={b.program_fee_rate} onChange={v => set("program_fee_rate", v)} />
        <input value={b.notes} onChange={e => set("notes", e.target.value)} placeholder="ملاحظات" style={{ ...inputS, width: "100%" }} />
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={submit} style={{ ...btnGreen, padding: "6px 12px" }}>✓</button>
          <button onClick={onCancel} style={{ ...btnGrey, padding: "6px 12px" }}>✕</button>
        </div>
      </div>
    </div>
  );
}

function GenerateOfferScheduleForm({ offer, isOptional, existing, onCancel, onDone }) {
  const [count, setCount] = useState(isOptional ? 5 : 22);
  const [principal, setPrincipal] = useState(offer.tranche_size || 750000);
  const [firstDate, setFirstDate] = useState(new Date().toISOString().slice(0, 10));
  const [intervalDays, setIntervalDays] = useState(offer.issuance_interval_days || 15);
  const [replace, setReplace] = useState(existing.length > 0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await sukuk.generateOfferSchedule(offer.id, {
        count: parseInt(count),
        principal: parseFloat(principal),
        first_date: firstDate,
        interval_days: parseInt(intervalDays),
        is_optional: isOptional,
        replace_existing: replace,
      });
      toast.success(`تم توليد ${r.data.count} إصدار`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ padding: 12, background: "#cffafe", borderBottom: "2px solid #0891b2" }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: "#155e75", marginBottom: 8 }}>
        ⚡ توليد جدول جماعي ({isOptional ? "اختياري" : "أساسي"}) — كل الإصدارات بنفس المبلغ والمعدلات
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.3fr 1fr auto auto", gap: 6, alignItems: "end" }}>
        <NumInput label="العدد" value={count} onChange={setCount} />
        <NumInput label="مبلغ كل إصدار" value={principal} onChange={setPrincipal} />
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}>تاريخ أول إصدار</div>
          <input type="date" value={firstDate} onChange={e => setFirstDate(e.target.value)} style={{ ...inputS, width: "100%" }} />
        </div>
        <NumInput label="الفاصل (يوم)" value={intervalDays} onChange={setIntervalDays} />
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#155e75", marginBottom: 6 }}>
          <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} />
          استبدال
        </label>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={submit} disabled={busy} style={{ ...btnGreen, padding: "6px 12px" }}>{busy ? "..." : "توليد"}</button>
          <button onClick={onCancel} style={{ ...btnGrey, padding: "6px 12px" }}>✕</button>
        </div>
      </div>
    </div>
  );
}

const miniInput = {
  padding: "3px 6px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11,
  fontFamily: "inherit", background: "#fff", textAlign: "right", direction: "ltr",
  width: 90,
};

// ═══════════════════════════════════════════════════════════════════
// GANTT CHART — Tranche Timeline (v3.3.404)
// ═══════════════════════════════════════════════════════════════════
function TranchesGanttChart({ tranches, aprByTrancheId, feePayDate, upfrontProgramFee }) {
  const [hoveredId, setHoveredId] = useState(null);

  // Compute issue/maturity dates for each tranche
  const items = tranches
    .map((t, idx) => {
      const issueDate = t.planned_issue_date ? new Date(t.planned_issue_date) : null;
      if (!issueDate) return null;
      const tenor = Number(t.tenor_months) || 24;
      const matDate = new Date(issueDate);
      matDate.setMonth(matDate.getMonth() + tenor);
      return {
        id: t.id,
        seq: t.sequence_number,
        principal: Number(t.principal) || 0,
        coupon: Number(t.coupon_rate) || 0,
        tenor,
        isOptional: t.is_optional,
        issueDate,
        matDate,
        apr: aprByTrancheId?.[t.id]?.apr_pct,
        netReceived: aprByTrancheId?.[t.id]?.net_received,
        totalRepayment: aprByTrancheId?.[t.id]?.total_repayment,
      };
    })
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  // Determine timeline bounds
  const allDates = [];
  for (const it of items) {
    allDates.push(it.issueDate.getTime());
    allDates.push(it.matDate.getTime());
  }
  if (feePayDate) allDates.push(new Date(feePayDate).getTime());
  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));
  // Add small padding
  minDate.setDate(minDate.getDate() - 15);
  maxDate.setDate(maxDate.getDate() + 15);
  const totalMs = maxDate.getTime() - minDate.getTime();

  // Build month markers (for x-axis grid lines)
  const monthMarkers = [];
  const markerDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  while (markerDate <= maxDate) {
    monthMarkers.push(new Date(markerDate));
    markerDate.setMonth(markerDate.getMonth() + 1);
  }
  // For very long timelines, only label every 3 months
  const monthInterval = monthMarkers.length > 36 ? 6 : monthMarkers.length > 18 ? 3 : 1;

  // Helper: convert date → x% of timeline
  const dateToPct = (d) => ((d.getTime() - minDate.getTime()) / totalMs) * 100;

  // Sort items by issue date for visual consistency
  items.sort((a, b) => a.issueDate.getTime() - b.issueDate.getTime());

  const rowHeight = 28;
  const barHeight = 18;
  const labelWidth = 70;  // px reserved for "#1" label on left
  const chartHeight = items.length * rowHeight + 80;  // +80 for headers

  // Today marker
  const today = new Date();
  const todayPct = today >= minDate && today <= maxDate ? dateToPct(today) : null;

  // Format date short
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const fmtMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  return (
    <div style={{ marginTop: 16, padding: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
            📅 الجدول الزمني للإصدارات (Gantt Chart)
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {items.length} إصدار · من {fmtDate(minDate)} إلى {fmtDate(maxDate)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 11, alignItems: "center", flexWrap: "wrap" }}>
          <LegendDot color="#0891b2" label="أساسي" />
          <LegendDot color="#7c3aed" label="اختياري" />
          {feePayDate && <LegendDot color="#dc2626" label="رسوم البرنامج" shape="line" />}
          <LegendDot color="#16a34a" label="اليوم" shape="line" />
        </div>
      </div>

      <div style={{ overflowX: "auto", overflowY: "visible" }}>
        <div style={{ position: "relative", width: "100%", minWidth: 800 }}>
          {/* Container with header + chart */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Top axis: month labels */}
            <div style={{ display: "flex", height: 28, position: "relative", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ width: labelWidth, flexShrink: 0, paddingRight: 6, fontSize: 10, color: "#64748b", fontWeight: 700, alignSelf: "flex-end" }}>
                #
              </div>
              <div style={{ flex: 1, position: "relative" }}>
                {monthMarkers.map((m, idx) => {
                  if (idx % monthInterval !== 0) return null;
                  return (
                    <div
                      key={idx}
                      style={{
                        position: "absolute",
                        right: `${dateToPct(m)}%`,
                        top: 0,
                        transform: "translateX(50%)",
                        fontSize: 9,
                        color: "#475569",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtMonth(m)}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tranche rows */}
            <div style={{ position: "relative" }}>
              {/* Vertical month grid lines (full height) */}
              <div style={{ position: "absolute", inset: 0, marginRight: labelWidth, pointerEvents: "none" }}>
                {monthMarkers.map((m, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: "absolute",
                      right: `${dateToPct(m)}%`,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: idx % monthInterval === 0 ? "#e2e8f0" : "#f1f5f9",
                    }}
                  />
                ))}
                {/* Fee pay date marker (red dashed) */}
                {feePayDate && (
                  <div
                    style={{
                      position: "absolute",
                      right: `${dateToPct(new Date(feePayDate))}%`,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: "#dc2626",
                      borderLeft: "2px dashed #dc2626",
                      zIndex: 2,
                    }}
                    title={`رسوم البرنامج: ${fmtDate(new Date(feePayDate))} — ${fmt(upfrontProgramFee || 0)}`}
                  />
                )}
                {/* Today marker (green dashed) */}
                {todayPct !== null && (
                  <div
                    style={{
                      position: "absolute",
                      right: `${todayPct}%`,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: "#16a34a",
                      borderLeft: "2px dashed #16a34a",
                      zIndex: 2,
                    }}
                    title={`اليوم: ${fmtDate(today)}`}
                  />
                )}
              </div>

              {/* Each tranche row */}
              {items.map((it, idx) => {
                const startPct = dateToPct(it.issueDate);
                const endPct = dateToPct(it.matDate);
                const widthPct = endPct - startPct;
                const color = it.isOptional ? "#7c3aed" : "#0891b2";
                const lightColor = it.isOptional ? "#faf5ff" : "#ecfeff";
                const isHovered = hoveredId === it.id;

                return (
                  <div
                    key={it.id}
                    onMouseEnter={() => setHoveredId(it.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      display: "flex",
                      height: rowHeight,
                      alignItems: "center",
                      borderBottom: "1px solid #f1f5f9",
                      background: isHovered ? "#fef9c3" : (idx % 2 === 0 ? "#fff" : "#fafafa"),
                      transition: "background 0.15s",
                      position: "relative",
                    }}
                  >
                    {/* Left label: # */}
                    <div style={{
                      width: labelWidth, flexShrink: 0, paddingRight: 8, paddingLeft: 4,
                      fontSize: 11, fontWeight: 700, color: "#0f172a",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span style={{
                        background: lightColor, color, padding: "2px 6px", borderRadius: 3,
                        fontSize: 10, fontWeight: 800,
                      }}>
                        #{it.seq}
                      </span>
                      <span style={{ fontSize: 9, color: "#64748b", fontWeight: 600 }}>
                        {it.tenor}ش
                      </span>
                    </div>

                    {/* Bar area */}
                    <div style={{ flex: 1, position: "relative", height: "100%" }}>
                      <div
                        style={{
                          position: "absolute",
                          right: `${startPct}%`,
                          width: `${widthPct}%`,
                          top: (rowHeight - barHeight) / 2,
                          height: barHeight,
                          background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
                          borderRadius: 4,
                          boxShadow: isHovered ? `0 2px 8px ${color}80` : "0 1px 2px rgba(0,0,0,0.1)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0 6px",
                          transition: "all 0.15s",
                          transform: isHovered ? "scaleY(1.1)" : "scaleY(1)",
                          zIndex: isHovered ? 3 : 1,
                        }}
                      >
                        {/* Issue date dot */}
                        <div style={{
                          position: "absolute",
                          right: -4, top: "50%", transform: "translateY(-50%)",
                          width: 8, height: 8, borderRadius: "50%",
                          background: "#fff", border: `2px solid ${color}`,
                        }} />
                        {/* Maturity square */}
                        <div style={{
                          position: "absolute",
                          left: -4, top: "50%", transform: "translateY(-50%)",
                          width: 8, height: 8,
                          background: "#fff", border: `2px solid ${color}`,
                        }} />
                        {/* Bar label (amount, only if wide enough) */}
                        {widthPct > 8 && (
                          <span style={{
                            color: "#fff", fontSize: 9, fontWeight: 700,
                            direction: "ltr", fontVariantNumeric: "tabular-nums",
                            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                            whiteSpace: "nowrap", margin: "0 auto",
                          }}>
                            {it.principal >= 1000000 ? `${(it.principal / 1000000).toFixed(1)}M` : `${(it.principal / 1000).toFixed(0)}K`}
                          </span>
                        )}
                      </div>

                      {/* Tooltip on hover */}
                      {isHovered && (
                        <div style={{
                          position: "absolute",
                          right: `${startPct}%`,
                          top: rowHeight + 4,
                          background: "#0f172a",
                          color: "#fff",
                          padding: "8px 12px",
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          zIndex: 10,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                          pointerEvents: "none",
                          minWidth: 220,
                        }}>
                          <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4, color: it.isOptional ? "#d8b4fe" : "#67e8f9" }}>
                            إصدار #{it.seq} {it.isOptional ? "(اختياري)" : "(أساسي)"}
                          </div>
                          <div style={{ direction: "ltr", textAlign: "right" }}>
                            <div>📅 {fmtDate(it.issueDate)} → {fmtDate(it.matDate)}</div>
                            <div>💰 المبلغ: {fmt(it.principal)} · {it.tenor} شهر</div>
                            {it.netReceived !== undefined && <div>💵 الصافي: {fmt(it.netReceived)}</div>}
                            {it.totalRepayment !== undefined && <div>🔻 السداد: {fmt(it.totalRepayment)}</div>}
                            <div>📈 الكوبون: {it.coupon}%</div>
                            {it.apr !== null && it.apr !== undefined && (
                              <div style={{ color: "#d8b4fe", fontWeight: 800 }}>📊 APR فعلي: {it.apr.toFixed(2)}%</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Summary footer */}
      <div style={{ marginTop: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 6, fontSize: 11, color: "#64748b", display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div>🟢 الدائرة = تاريخ الإصدار</div>
        <div>🟦 المربع = تاريخ الاستحقاق</div>
        <div>📏 طول الشريط = مدة الإصدار</div>
        <div>💡 مرّر الفأرة على أي شريط لرؤية التفاصيل</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label, shape = "circle" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {shape === "line" ? (
        <div style={{ width: 14, height: 2, background: color, borderTop: `2px dashed ${color}` }} />
      ) : (
        <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
      )}
      <span style={{ color: "#475569", fontWeight: 600 }}>{label}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SUKUKS GANTT — for active contracts (v3.3.404)
// ═══════════════════════════════════════════════════════════════════
function SukuksGanttChart({ sukuks, aprBySukukId, contract }) {
  const [hoveredId, setHoveredId] = useState(null);

  const items = sukuks
    .map(s => {
      // Use actual issue date if active/matured, else planned
      const issueDate = s.actual_issue_date || s.planned_issue_date;
      if (!issueDate) return null;
      const issueD = new Date(issueDate);
      const tenor = Number(s.tenor_months) || (() => {
        // Compute tenor from maturity if available
        if (s.maturity_date) {
          const m = new Date(s.maturity_date);
          return Math.round((m - issueD) / (30.44 * 86400000));
        }
        return 24;
      })();
      const matD = s.maturity_date ? new Date(s.maturity_date) : (() => {
        const d = new Date(issueD);
        d.setMonth(d.getMonth() + tenor);
        return d;
      })();
      return {
        id: s.id,
        seq: s.sequence_number,
        principal: Number(s.principal) || 0,
        coupon: Number(s.coupon_rate) || 0,
        tenor,
        isOptional: s.program_type === 'supplementary',
        status: s.status,
        actualIssue: !!s.actual_issue_date,
        issueDate: issueD,
        matDate: matD,
        apr: aprBySukukId?.[s.id]?.apr_pct,
        netReceived: Number(s.net_received) || aprBySukukId?.[s.id]?.net_received,
        totalRepayment: Number(s.total_repayment) || 0,
      };
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  const allDates = [];
  for (const it of items) {
    allDates.push(it.issueDate.getTime());
    allDates.push(it.matDate.getTime());
  }
  if (contract?.signing_date) allDates.push(new Date(contract.signing_date).getTime());
  if (contract?.program_fee_paid_date) allDates.push(new Date(contract.program_fee_paid_date).getTime());

  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));
  minDate.setDate(minDate.getDate() - 15);
  maxDate.setDate(maxDate.getDate() + 15);
  const totalMs = maxDate.getTime() - minDate.getTime();

  const monthMarkers = [];
  const markerDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  while (markerDate <= maxDate) {
    monthMarkers.push(new Date(markerDate));
    markerDate.setMonth(markerDate.getMonth() + 1);
  }
  const monthInterval = monthMarkers.length > 36 ? 6 : monthMarkers.length > 18 ? 3 : 1;

  const dateToPct = (d) => ((d.getTime() - minDate.getTime()) / totalMs) * 100;
  items.sort((a, b) => a.issueDate.getTime() - b.issueDate.getTime());

  const rowHeight = 28;
  const barHeight = 18;
  const labelWidth = 90;
  const today = new Date();
  const todayPct = today >= minDate && today <= maxDate ? dateToPct(today) : null;
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const fmtMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  // Status color mapping
  const statusColors = {
    pending: { bar: "#94a3b8", light: "#f1f5f9" },          // gray (not yet issued)
    active: { bar: "#16a34a", light: "#f0fdf4" },           // green (issued, not yet repaid)
    matured: { bar: "#1e40af", light: "#dbeafe" },          // blue (repaid)
    cancelled: { bar: "#dc2626", light: "#fef2f2" },        // red (cancelled)
  };

  return (
    <div style={{ marginTop: 16, padding: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
            📅 الجدول الزمني للصكوك (Gantt Chart)
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {items.length} صك · من {fmtDate(minDate)} إلى {fmtDate(maxDate)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 11, alignItems: "center", flexWrap: "wrap" }}>
          <LegendDot color="#94a3b8" label="قيد الانتظار" />
          <LegendDot color="#16a34a" label="نشط" />
          <LegendDot color="#1e40af" label="مسدد" />
          {contract?.program_fee_paid_date && <LegendDot color="#dc2626" label="رسوم البرنامج" shape="line" />}
          <LegendDot color="#16a34a" label="اليوم" shape="line" />
        </div>
      </div>

      <div style={{ overflowX: "auto", overflowY: "visible" }}>
        <div style={{ position: "relative", width: "100%", minWidth: 800 }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Top axis */}
            <div style={{ display: "flex", height: 28, position: "relative", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ width: labelWidth, flexShrink: 0, paddingRight: 6, fontSize: 10, color: "#64748b", fontWeight: 700, alignSelf: "flex-end" }}>
                # / حالة
              </div>
              <div style={{ flex: 1, position: "relative" }}>
                {monthMarkers.map((m, idx) => {
                  if (idx % monthInterval !== 0) return null;
                  return (
                    <div
                      key={idx}
                      style={{
                        position: "absolute",
                        right: `${dateToPct(m)}%`,
                        top: 0,
                        transform: "translateX(50%)",
                        fontSize: 9,
                        color: "#475569",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtMonth(m)}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rows */}
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", inset: 0, marginRight: labelWidth, pointerEvents: "none" }}>
                {monthMarkers.map((m, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: "absolute",
                      right: `${dateToPct(m)}%`,
                      top: 0, bottom: 0, width: 1,
                      background: idx % monthInterval === 0 ? "#e2e8f0" : "#f1f5f9",
                    }}
                  />
                ))}
                {contract?.program_fee_paid_date && (
                  <div
                    style={{
                      position: "absolute",
                      right: `${dateToPct(new Date(contract.program_fee_paid_date))}%`,
                      top: 0, bottom: 0, width: 2,
                      background: "#dc2626",
                      borderLeft: "2px dashed #dc2626",
                      zIndex: 2,
                    }}
                    title={`رسوم البرنامج: ${fmtDate(new Date(contract.program_fee_paid_date))}`}
                  />
                )}
                {todayPct !== null && (
                  <div
                    style={{
                      position: "absolute",
                      right: `${todayPct}%`,
                      top: 0, bottom: 0, width: 2,
                      background: "#16a34a",
                      borderLeft: "2px dashed #16a34a",
                      zIndex: 2,
                    }}
                    title={`اليوم: ${fmtDate(today)}`}
                  />
                )}
              </div>

              {items.map((it, idx) => {
                const startPct = dateToPct(it.issueDate);
                const endPct = dateToPct(it.matDate);
                const widthPct = endPct - startPct;
                const sc = statusColors[it.status] || statusColors.pending;
                const isHovered = hoveredId === it.id;
                const programColor = it.isOptional ? "#7c3aed" : "#0891b2";

                return (
                  <div
                    key={it.id}
                    onMouseEnter={() => setHoveredId(it.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      display: "flex", height: rowHeight, alignItems: "center",
                      borderBottom: "1px solid #f1f5f9",
                      background: isHovered ? "#fef9c3" : (idx % 2 === 0 ? "#fff" : "#fafafa"),
                      transition: "background 0.15s", position: "relative",
                    }}
                  >
                    <div style={{
                      width: labelWidth, flexShrink: 0, paddingRight: 8, paddingLeft: 4,
                      fontSize: 10, fontWeight: 700, color: "#0f172a",
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4,
                    }}>
                      <span style={{
                        background: programColor + "22", color: programColor,
                        padding: "2px 5px", borderRadius: 3, fontSize: 9, fontWeight: 800,
                      }}>
                        #{it.seq}
                      </span>
                      <span style={{
                        background: sc.light, color: sc.bar,
                        padding: "1px 5px", borderRadius: 3, fontSize: 8, fontWeight: 700,
                      }}>
                        {it.status === 'pending' ? '⏳' : it.status === 'active' ? '✅' : it.status === 'matured' ? '💰' : '✕'}
                      </span>
                    </div>

                    <div style={{ flex: 1, position: "relative", height: "100%" }}>
                      <div
                        style={{
                          position: "absolute",
                          right: `${startPct}%`,
                          width: `${widthPct}%`,
                          top: (rowHeight - barHeight) / 2,
                          height: barHeight,
                          background: `linear-gradient(90deg, ${sc.bar} 0%, ${sc.bar}dd 100%)`,
                          borderRadius: 4,
                          // Optional sukuks: dashed border
                          border: it.isOptional ? "1px dashed #fff" : "none",
                          boxShadow: isHovered ? `0 2px 8px ${sc.bar}80` : "0 1px 2px rgba(0,0,0,0.1)",
                          cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s",
                          transform: isHovered ? "scaleY(1.1)" : "scaleY(1)",
                          zIndex: isHovered ? 3 : 1,
                        }}
                      >
                        <div style={{
                          position: "absolute",
                          right: -4, top: "50%", transform: "translateY(-50%)",
                          width: 8, height: 8, borderRadius: "50%",
                          background: it.actualIssue ? sc.bar : "#fff",
                          border: `2px solid ${sc.bar}`,
                        }} />
                        <div style={{
                          position: "absolute",
                          left: -4, top: "50%", transform: "translateY(-50%)",
                          width: 8, height: 8,
                          background: it.status === 'matured' ? sc.bar : "#fff",
                          border: `2px solid ${sc.bar}`,
                        }} />
                        {widthPct > 8 && (
                          <span style={{
                            color: "#fff", fontSize: 9, fontWeight: 700,
                            direction: "ltr", fontVariantNumeric: "tabular-nums",
                            textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                            whiteSpace: "nowrap",
                          }}>
                            {it.principal >= 1000000 ? `${(it.principal / 1000000).toFixed(1)}M` : `${(it.principal / 1000).toFixed(0)}K`}
                          </span>
                        )}
                      </div>

                      {isHovered && (
                        <div style={{
                          position: "absolute",
                          right: `${startPct}%`,
                          top: rowHeight + 4,
                          background: "#0f172a", color: "#fff",
                          padding: "8px 12px", borderRadius: 6,
                          fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                          zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                          pointerEvents: "none", minWidth: 240,
                        }}>
                          <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4, color: it.isOptional ? "#d8b4fe" : "#67e8f9" }}>
                            صك #{it.seq} {it.isOptional ? "(تكميلي)" : "(أساسي)"} · {it.status === 'pending' ? 'قيد الانتظار' : it.status === 'active' ? 'نشط' : it.status === 'matured' ? 'مسدد' : 'ملغي'}
                          </div>
                          <div style={{ direction: "ltr", textAlign: "right" }}>
                            <div>📅 {fmtDate(it.issueDate)} → {fmtDate(it.matDate)}</div>
                            <div>💰 المبلغ: {fmt(it.principal)} · {it.tenor} شهر</div>
                            {it.netReceived !== undefined && <div>💵 الصافي: {fmt(it.netReceived)}</div>}
                            <div>🔻 السداد: {fmt(it.totalRepayment)}</div>
                            <div>📈 الكوبون: {it.coupon}%</div>
                            {it.apr !== null && it.apr !== undefined && (
                              <div style={{ color: "#d8b4fe", fontWeight: 800 }}>📊 APR فعلي: {it.apr.toFixed(2)}%</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 6, fontSize: 11, color: "#64748b", display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div>🟢 دائرة ممتلئة = إصدار فعلي  ·  ⭕ دائرة فارغة = مخطط</div>
        <div>🟦 مربع ممتلئ = مسدد  ·  ◻️ مربع فارغ = منتظر</div>
        <div>📐 شريط متقطع الحدود = اختياري</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TRANCHE DETAIL PAGE — Custom payment schedule (v3.3.405)
// ═══════════════════════════════════════════════════════════════════
function TrancheDetailPage({ trancheId, canEdit, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await sukuk.getTrancheDetails(trancheId);
      setData(r.data);
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [trancheId]);

  if (loading || !data) return <div style={{ padding: 30, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>;
  const { tranche: t, offer, payments } = data;

  const principal = Number(t.principal) || 0;
  const couponRate = Number(t.coupon_rate) || 0;
  const tenor = Number(t.tenor_months) || 24;
  const adminFeeRate = Number(t.admin_fee_rate) || 0;
  const progFeeRate = Number(t.program_fee_rate) || 0;
  const adminFee = principal * adminFeeRate * (tenor / 12) / 100;
  const trancheProgFee = t.is_optional ? principal * progFeeRate / 100 : 0;
  const netReceived = principal - adminFee - trancheProgFee;
  const expectedInterest = principal * couponRate * (tenor / 12) / 100;
  const matDate = t.planned_issue_date ? (() => {
    const d = new Date(t.planned_issue_date);
    d.setMonth(d.getMonth() + tenor);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  })() : null;

  const sumPrincipal = payments.reduce((s, p) => s + Number(p.principal_amount || 0), 0);
  const sumInterest = payments.reduce((s, p) => s + Number(p.interest_amount || 0), 0);
  const sumTotal = sumPrincipal + sumInterest;
  const principalMismatch = payments.length > 0 && Math.abs(sumPrincipal - principal) > 0.01;

  // Compute APR from actual payments via XIRR (client-side approximation)
  // v3.3.407: use effective_date (computed from month_offset) when available
  const actualApr = (() => {
    if (payments.length === 0 || !t.planned_issue_date) return null;
    const flows = [{ date: t.planned_issue_date, amount: netReceived }];
    for (const p of payments) {
      const total = Number(p.principal_amount || 0) + Number(p.interest_amount || 0);
      const date = p.effective_date || p.payment_date;
      if (total > 0 && date) flows.push({ date, amount: -total });
    }
    return clientXirr(flows);
  })();

  const isOpt = t.is_optional;
  const themeColor = isOpt ? "#7c3aed" : "#0891b2";
  const themeBg = isOpt ? "#faf5ff" : "#ecfeff";
  const themeBorder = isOpt ? "#d8b4fe" : "#a5f3fc";

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
        <button onClick={onBack} style={{ padding: "6px 12px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FiArrowRight size={12} /> العودة لتفاصيل العرض
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>
            <span style={{ background: themeBg, color: themeColor, padding: "3px 10px", borderRadius: 5, fontSize: 13, marginLeft: 8 }}>
              إصدار #{t.sequence_number} {isOpt ? "(اختياري)" : "(أساسي)"}
            </span>
            {offer && <span style={{ color: "#64748b" }}>{offer.name}</span>}
          </div>
        </div>
        <div style={{ width: 110 }}></div>
      </div>

      {/* Mismatch warning */}
      {principalMismatch && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8, color: "#92400e", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>⚠</span>
          <div>
            <strong>تنبيه:</strong> مجموع Principal في جدول الدفعات ({fmt(sumPrincipal)}) لا يساوي مبلغ الإصدار ({fmt(principal)}).
            الفرق: {fmt(principal - sumPrincipal)}
          </div>
        </div>
      )}
      {payments.length === 0 && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8, color: "#92400e", fontSize: 13 }}>
          ⚠ هذا الإصدار بدون دفعات. سيتم إنشاء دفعة افتراضية تلقائياً عند الاستحقاق.
        </div>
      )}

      {/* Tranche details summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginBottom: 14 }}>
        <Box label="المبلغ" value={fmt(principal)} color="#0f172a" bg="#f1f5f9" />
        <Box label="تاريخ الإصدار" value={fmtDateDMY(t.planned_issue_date)} color="#475569" bg="#f8fafc" />
        <Box label="المدة" value={`${tenor} شهر (${tenor * 30} يوم)`} color="#475569" bg="#f8fafc" />
        <Box label="الكوبون %" value={`${couponRate}%`} color="#1e40af" bg="#dbeafe" />
        <Box label="الصافي" value={fmt(netReceived)} color="#0891b2" bg="#ecfeff" />
        <Box label="فائدة متوقعة" value={fmt(expectedInterest)} color="#15803d" bg="#f0fdf4" />
        <Box label="استحقاق افتراضي" value={matDate || "—"} color="#dc2626" bg="#fef2f2" />
        {actualApr !== null && (
          <Box label="APR من الدفعات" value={`${(actualApr * 100).toFixed(2)}%`} color="#581c87" bg="#faf5ff" sub="حسب الجدول الفعلي" />
        )}
      </div>

      {/* Payment schedule table */}
      <div style={{ background: "#fff", border: `1.5px solid ${themeBorder}`, borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "10px 14px", background: themeBg, borderBottom: `1px solid ${themeBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: themeColor }}>📅 جدول دفعات السداد</div>
            <div style={{ fontSize: 11, color: themeColor, marginTop: 2, opacity: 0.85 }}>
              {payments.length} دفعة · مجموع Principal: {fmt(sumPrincipal)} · مجموع Interest: {fmt(sumInterest)}
              <span style={{ marginRight: 6, opacity: 0.7 }}>· 💡 دفعة الاستحقاق افتراضية وتُحدَّث تلقائياً</span>
            </div>
          </div>
          {canEdit && (
            <button onClick={() => setAdding(!adding)} style={{ padding: "5px 12px", background: adding ? "#e2e8f0" : themeColor, color: adding ? "#475569" : "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              {adding ? "إلغاء" : "+ دفعة جزئية"}
            </button>
          )}
        </div>

        {adding && (
          <NewPaymentForm
            trancheId={trancheId}
            tranche={t}
            existingPayments={payments}
            onCancel={() => setAdding(false)}
            onCreated={() => { setAdding(false); load(); }}
          />
        )}

        {payments.length === 0 && !adding ? (
          <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            لا توجد دفعات. أعد تحميل الصفحة وستُنشأ دفعة الاستحقاق افتراضياً.
          </div>
        ) : payments.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={th}>#</th>
                <th style={th} title="عدد الأشهر من تاريخ الإصدار (يتحرك تلقائياً مع تاريخ الإصدار)">بعد كم شهر</th>
                <th style={th}>تاريخ الدفعة (محسوب)</th>
                <th style={{ ...th, textAlign: "left" }}>Principal</th>
                <th style={{ ...th, textAlign: "left" }}>Interest</th>
                <th style={{ ...th, textAlign: "left" }}>الإجمالي</th>
                <th style={th}>ملاحظات</th>
                {canEdit && <th style={th}>إجراءات</th>}
              </tr>
            </thead>
            <tbody>
              {payments.map((p, idx) => (
                <PaymentRow
                  key={p.id}
                  payment={p}
                  tranche={t}
                  seq={idx + 1}
                  canEdit={canEdit}
                  onChange={load}
                />
              ))}
              {/* Totals row */}
              <tr style={{ background: themeBg, fontWeight: 800, borderTop: `2px solid ${themeBorder}` }}>
                <td colSpan={3} style={{ ...td, color: themeColor, textAlign: "left" }}>الإجمالي:</td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: principalMismatch ? "#dc2626" : "#15803d" }}>
                  {fmt(sumPrincipal)}
                  {principalMismatch && <span style={{ fontSize: 9, marginRight: 4 }}>≠ {fmt(principal)}</span>}
                </td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#15803d" }}>{fmt(sumInterest)}</td>
                <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#dc2626" }}>{fmt(sumTotal)}</td>
                {canEdit && <td colSpan={2}></td>}
                {!canEdit && <td></td>}
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Single payment row (display + inline edit) ───
// v3.3.407: month_offset is the source of truth, payment_date is derived
function PaymentRow({ payment: p, tranche, seq, canEdit, onChange }) {
  const [editing, setEditing] = useState(false);
  const [b, setB] = useState({
    month_offset: p.month_offset !== null && p.month_offset !== undefined ? String(p.month_offset) : "",
    principal_amount: p.principal_amount || 0,
    interest_amount: p.interest_amount || 0,
    notes: p.notes || "",
  });

  // Compute the derived effective date for display/preview
  const computedDate = (() => {
    if (!tranche.planned_issue_date || !b.month_offset) return null;
    const m = parseFloat(b.month_offset);
    if (isNaN(m)) return null;
    const wholeMonths = Math.floor(m);
    const fracDays = Math.round((m - wholeMonths) * 30);
    const d = new Date(tranche.planned_issue_date);
    d.setMonth(d.getMonth() + wholeMonths);
    d.setDate(d.getDate() + fracDays);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  })();

  const total = (Number(p.principal_amount) || 0) + (Number(p.interest_amount) || 0);
  const effectiveDateRaw = p.effective_date || (p.payment_date ? String(p.payment_date).slice(0, 10) : null);
  const effectiveDate = effectiveDateRaw ? fmtDateDMY(effectiveDateRaw) : null;
  // v3.3.410 — Detect the "maturity payment" (auto-default at month_offset == tenor)
  const tenor = Number(tranche.tenor_months) || 24;
  const isMaturity = p.month_offset !== null && p.month_offset !== undefined && Number(p.month_offset) >= tenor - 0.01;

  const save = async () => {
    if (b.month_offset === "" || b.month_offset === null) { toast.error("عدد الأشهر مطلوب"); return; }
    const m = parseFloat(b.month_offset);
    if (isNaN(m)) { toast.error("عدد الأشهر غير صالح"); return; }
    try {
      await sukuk.updateTranchePayment(p.id, {
        month_offset: m,
        principal_amount: parseFloat(b.principal_amount) || 0,
        interest_amount: parseFloat(b.interest_amount) || 0,
        notes: b.notes || null,
      });
      setEditing(false);
      onChange();
      toast.success("تم");
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل"); }
  };

  const remove = async () => {
    if (!window.confirm(`حذف الدفعة #${seq}؟`)) return;
    try { await sukuk.deleteTranchePayment(p.id); onChange(); toast.success("تم"); }
    catch (e) { console.error(e); toast.error("فشل"); }
  };

  if (editing) {
    return (
      <tr style={{ background: "#fef9c3", borderBottom: "1px solid #fde68a" }}>
        <td style={td}>{seq}</td>
        <td style={td}>
          <input
            type="number"
            step="0.5"
            value={b.month_offset}
            onChange={e => setB(p => ({ ...p, month_offset: e.target.value }))}
            placeholder="مثلاً 4"
            style={{ ...miniInput, width: 90 }}
          />
        </td>
        <td style={{ ...td, fontSize: 10, color: "#92400e", fontWeight: 600 }}>
          {computedDate ? `← ${computedDate}` : "(محسوب)"}
        </td>
        <td style={td}><input type="number" value={b.principal_amount} onChange={e => setB(p => ({ ...p, principal_amount: e.target.value }))} style={miniInput} /></td>
        <td style={td}><input type="number" value={b.interest_amount} onChange={e => setB(p => ({ ...p, interest_amount: e.target.value }))} style={miniInput} /></td>
        <td style={{ ...td, fontSize: 11, color: "#92400e" }}>(محسوب)</td>
        <td style={td}><input value={b.notes} onChange={e => setB(p => ({ ...p, notes: e.target.value }))} placeholder="ملاحظات" style={{ ...miniInput, width: 160 }} /></td>
        <td style={{ ...td, textAlign: "center" }}>
          <button onClick={save} style={{ padding: "3px 8px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>✓</button>
          <button onClick={() => setEditing(false)} style={{ padding: "3px 8px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>✕</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{
      borderBottom: "1px solid #f1f5f9",
      background: isMaturity ? "#fef3c7" : "transparent",
    }}>
      <td style={td}>{seq}</td>
      <td style={{ ...td, color: "#0f172a", fontWeight: 700, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
        {p.month_offset !== null && p.month_offset !== undefined ? `${Number(p.month_offset)} شهر` : "—"}
        {isMaturity && <span style={{ fontSize: 9, marginRight: 4, color: "#92400e", fontWeight: 800 }}>🎯 استحقاق</span>}
      </td>
      <td style={{ ...td, color: "#475569", fontSize: 11 }}>
        {effectiveDate || "—"}
        {p.month_offset !== null && p.month_offset !== undefined && (
          <span style={{ fontSize: 8, color: "#94a3b8", marginRight: 4, opacity: 0.7 }}>📐</span>
        )}
      </td>
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#0891b2" }}>{fmt(p.principal_amount || 0)}</td>
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#15803d" }}>{fmt(p.interest_amount || 0)}</td>
      <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#dc2626" }}>{fmt(total)}</td>
      <td style={{ ...td, fontSize: 10, color: "#64748b" }}>{p.notes || (isMaturity ? "تُحدَّث تلقائياً" : "—")}</td>
      {canEdit && (
        <td style={{ ...td, textAlign: "center" }}>
          {!isMaturity && (
            <button onClick={() => setEditing(true)} title="تعديل" style={{ padding: "3px 7px", background: "#e0e7ff", color: "#3730a3", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 3 }}>✎</button>
          )}
          {isMaturity ? (
            <span title="دفعة الاستحقاق تُحسب تلقائياً ولا تُحذف يدوياً" style={{ padding: "3px 7px", background: "#fef3c7", color: "#92400e", border: "none", borderRadius: 4, fontSize: 10, fontWeight: 700, opacity: 0.7 }}>🔒 افتراضية</span>
          ) : (
            <button onClick={remove} title="حذف" style={{ padding: "3px 7px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>🗑</button>
          )}
        </td>
      )}
    </tr>
  );
}

function NewPaymentForm({ trancheId, tranche, existingPayments, onCancel, onCreated }) {
  // Suggest a default month_offset based on last payment + 3 or 6
  const suggestOffset = () => {
    const lastPayment = existingPayments[existingPayments.length - 1];
    if (lastPayment && lastPayment.month_offset !== null && lastPayment.month_offset !== undefined) {
      return Number(lastPayment.month_offset) + 3;
    }
    if (lastPayment && lastPayment.payment_date && tranche.planned_issue_date) {
      const diff = (new Date(lastPayment.payment_date).getTime() - new Date(tranche.planned_issue_date).getTime()) / 86400000;
      return Math.round(diff / 30) + 3;
    }
    return 6;
  };
  const [b, setB] = useState({
    month_offset: suggestOffset(),
    principal_amount: 0,
    interest_amount: 0,
    notes: "",
  });
  const set = (k, v) => setB(prev => ({ ...prev, [k]: v }));

  // Live preview of computed effective date
  const previewDate = (() => {
    if (!tranche.planned_issue_date || b.month_offset === "" || b.month_offset === null) return null;
    const m = parseFloat(b.month_offset);
    if (isNaN(m)) return null;
    const wholeMonths = Math.floor(m);
    const fracDays = Math.round((m - wholeMonths) * 30);
    const d = new Date(tranche.planned_issue_date);
    d.setMonth(d.getMonth() + wholeMonths);
    d.setDate(d.getDate() + fracDays);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  })();

  const submit = async () => {
    if (b.month_offset === "" || b.month_offset === null) { toast.error("عدد الأشهر مطلوب"); return; }
    const m = parseFloat(b.month_offset);
    if (isNaN(m)) { toast.error("عدد الأشهر غير صالح"); return; }
    try {
      await sukuk.addTranchePayment(trancheId, {
        month_offset: m,
        principal_amount: parseFloat(b.principal_amount) || 0,
        interest_amount: parseFloat(b.interest_amount) || 0,
        notes: b.notes || null,
      });
      toast.success("تم");
      onCreated();
    } catch (e) { console.error(e); toast.error(e.response?.data?.error || "فشل"); }
  };

  return (
    <div style={{ padding: 12, background: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr 1fr 1fr 1.5fr auto", gap: 6, alignItems: "end" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", marginBottom: 3 }}>بعد كم شهر *</div>
          <input
            type="number"
            step="0.5"
            value={b.month_offset}
            onChange={e => set("month_offset", e.target.value)}
            placeholder="مثلاً 4"
            style={{ ...inputS, width: "100%" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", marginBottom: 3 }}>تاريخ الدفعة (محسوب)</div>
          <div style={{
            padding: "6px 10px",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            borderRadius: 5,
            fontSize: 12,
            color: "#475569",
            fontWeight: 600,
            direction: "ltr",
          }}>
            {previewDate || "—"}
          </div>
        </div>
        <NumInput label="Principal" value={b.principal_amount} onChange={v => set("principal_amount", v)} />
        <NumInput label="Interest" value={b.interest_amount} onChange={v => set("interest_amount", v)} />
        <input value={b.notes} onChange={e => set("notes", e.target.value)} placeholder="ملاحظات" style={inputS} />
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={submit} style={{ ...btnGreen, padding: "6px 12px" }}>✓</button>
          <button onClick={onCancel} style={{ ...btnGrey, padding: "6px 12px" }}>✕</button>
        </div>
      </div>
    </div>
  );
}

// Client-side XIRR for live UI feedback (mirrors server logic)
function clientXirr(flows, guess = 0.1) {
  if (!flows || flows.length < 2) return null;
  const cleanFlows = flows
    .filter(f => f.date && Number(f.amount) !== 0)
    .map(f => ({
      t: (f.date instanceof Date ? f.date : new Date(f.date)).getTime(),
      a: Number(f.amount) || 0,
    }))
    .sort((a, b) => a.t - b.t);
  if (cleanFlows.length < 2) return null;
  const hasP = cleanFlows.some(f => f.a > 0);
  const hasN = cleanFlows.some(f => f.a < 0);
  if (!hasP || !hasN) return null;
  const t0 = cleanFlows[0].t;
  const years = cleanFlows.map(f => (f.t - t0) / (365.25 * 86400000));
  const npv = (rate) => {
    let s = 0;
    for (let i = 0; i < cleanFlows.length; i++) s += cleanFlows[i].a / Math.pow(1 + rate, years[i]);
    return s;
  };
  // Bisection (more robust)
  let lo = -0.99, hi = 10;
  if (npv(lo) * npv(hi) > 0) return null;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const v = npv(mid);
    if (Math.abs(v) < 1e-7) return mid;
    if (v * npv(lo) < 0) hi = mid;
    else lo = mid;
    if (hi - lo < 1e-9) return mid;
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════
// DOCUMENTS SECTION — Drive-backed file uploads (v3.3.408)
// scope = "offer" | "contract" | "sukuk"
// ═══════════════════════════════════════════════════════════════════
function DocumentsSection({ scope, entityId, canEdit, title, subtitle, themeColor = "#0891b2", themeBg = "#ecfeff", themeBorder = "#a5f3fc", enableCategories = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("عقد");
  const [filterCategory, setFilterCategory] = useState("الكل");
  const fileInputRef = React.useRef(null);

  const CATEGORIES = ["عقد", "اتفاقية", "أخرى"];
  const CATEGORY_COLORS = {
    "عقد": { bg: "#fef3c7", color: "#92400e" },
    "اتفاقية": { bg: "#dbeafe", color: "#1e40af" },
    "أخرى": { bg: "#f1f5f9", color: "#475569" },
  };

  const apiList = scope === "offer" ? sukuk.getOfferDocuments
    : scope === "contract" ? sukuk.getContractDocuments
    : sukuk.getSukukDocuments;
  const apiUpload = scope === "offer" ? sukuk.uploadOfferDocument
    : scope === "contract" ? sukuk.uploadContractDocument
    : sukuk.uploadSukukDocument;

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiList(entityId);
      setDocs(r.data.documents || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope, entityId]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (enableCategories) fd.append("category", selectedCategory);
        await apiUpload(entityId, fd);
        ok++;
      } catch (err) {
        fail++;
        console.error(err);
      }
    }
    setUploading(false);
    e.target.value = "";
    if (ok > 0) toast.success(`تم رفع ${ok} ملف${fail > 0 ? ` · فشل ${fail}` : ''}`);
    else toast.error("فشل رفع الملفات");
    load();
  };

  const updateCategory = async (doc, newCat) => {
    try {
      await sukuk.updateDocument(doc.id, { category: newCat });
      load();
    } catch (e) { console.error(e); toast.error("فشل تحديث الفئة"); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`حذف "${doc.file_name}" من النظام؟ (الملف الأصلي يبقى في Drive للأمان)`)) return;
    try { await sukuk.deleteDocument(doc.id); toast.success("تم"); load(); }
    catch (e) { console.error(e); toast.error("فشل"); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    const KB = 1024, MB = KB * 1024;
    if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
    if (bytes >= KB) return `${(bytes / KB).toFixed(0)} KB`;
    return `${bytes} B`;
  };

  const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const fileIcon = (mime, name) => {
    if (!mime && !name) return "📄";
    const lower = (name || "").toLowerCase();
    if (lower.endsWith(".pdf") || (mime || "").includes("pdf")) return "📕";
    if (lower.endsWith(".xlsx") || lower.endsWith(".xls") || (mime || "").includes("spreadsheet")) return "📊";
    if (lower.endsWith(".docx") || lower.endsWith(".doc") || (mime || "").includes("word")) return "📘";
    if (lower.endsWith(".pptx") || lower.endsWith(".ppt") || (mime || "").includes("presentation")) return "📙";
    if ((mime || "").startsWith("image/")) return "🖼️";
    if ((mime || "").startsWith("video/")) return "🎬";
    if (lower.endsWith(".zip") || lower.endsWith(".rar")) return "🗄️";
    return "📄";
  };

  // Apply category filter
  const filteredDocs = enableCategories && filterCategory !== "الكل"
    ? docs.filter(d => (d.category || "أخرى") === filterCategory)
    : docs;

  // Count documents per category for filter chips
  const categoryCounts = enableCategories ? CATEGORIES.reduce((acc, cat) => {
    acc[cat] = docs.filter(d => (d.category || "أخرى") === cat).length;
    return acc;
  }, {}) : {};

  return (
    <div style={{ marginTop: 0, background: "#fff", border: `1.5px solid ${themeBorder}`, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: themeBg, borderBottom: `1px solid ${themeBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: themeColor, display: "flex", alignItems: "center", gap: 6 }}>
            📁 {title || "المستندات"}
          </div>
          <div style={{ fontSize: 11, color: themeColor, marginTop: 2, opacity: 0.85 }}>
            {subtitle || "ملفات مرفقة على Google Drive"} · {docs.length} ملف
          </div>
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {enableCategories && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>الفئة:</span>
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  style={{
                    padding: "5px 8px",
                    border: `1px solid ${themeBorder}`,
                    borderRadius: 5,
                    fontSize: 12,
                    fontWeight: 700,
                    background: "#fff",
                    color: themeColor,
                    cursor: "pointer",
                  }}
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
              disabled={uploading}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                padding: "6px 14px",
                background: uploading ? "#94a3b8" : themeColor,
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 700,
                cursor: uploading ? "wait" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {uploading ? "⏳ جاري الرفع..." : "+ رفع ملفات"}
            </button>
          </div>
        )}
      </div>

      {/* Category filter chips */}
      {enableCategories && docs.length > 0 && (
        <div style={{ padding: "8px 14px", background: "#fafbfc", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            onClick={() => setFilterCategory("الكل")}
            style={{
              padding: "4px 12px",
              background: filterCategory === "الكل" ? themeColor : "#fff",
              color: filterCategory === "الكل" ? "#fff" : "#475569",
              border: `1px solid ${filterCategory === "الكل" ? themeColor : "#cbd5e1"}`,
              borderRadius: 14,
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            الكل ({docs.length})
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              style={{
                padding: "4px 12px",
                background: filterCategory === cat ? themeColor : "#fff",
                color: filterCategory === cat ? "#fff" : "#475569",
                border: `1px solid ${filterCategory === cat ? themeColor : "#cbd5e1"}`,
                borderRadius: 14,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                opacity: categoryCounts[cat] === 0 ? 0.5 : 1,
              }}
            >
              {cat} ({categoryCounts[cat] || 0})
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>جاري التحميل...</div>
      ) : filteredDocs.length === 0 ? (
        <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
          📂 {docs.length === 0 ? "لا توجد مستندات بعد." : "لا توجد مستندات في هذه الفئة."} {canEdit && docs.length === 0 && "اضغط '+ رفع ملفات' لإضافة مستندات."}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <th style={{ ...th, width: 30 }}></th>
              {enableCategories && <th style={th}>الفئة</th>}
              <th style={th}>اسم الملف</th>
              <th style={th}>الحجم</th>
              <th style={th}>تاريخ الرفع</th>
              <th style={th}>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocs.map(d => {
              const cat = d.category || "أخرى";
              const catColor = CATEGORY_COLORS[cat] || CATEGORY_COLORS["أخرى"];
              return (
                <tr key={d.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ ...td, textAlign: "center", fontSize: 18 }}>{fileIcon(d.mime_type, d.file_name)}</td>
                  {enableCategories && (
                    <td style={td}>
                      {canEdit ? (
                        <select
                          value={cat}
                          onChange={e => updateCategory(d, e.target.value)}
                          style={{
                            padding: "2px 6px",
                            border: "none",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            background: catColor.bg,
                            color: catColor.color,
                            cursor: "pointer",
                          }}
                        >
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : (
                        <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, background: catColor.bg, color: catColor.color }}>
                          {cat}
                        </span>
                      )}
                    </td>
                  )}
                  <td style={{ ...td, fontWeight: 700, color: "#0f172a", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }} title={d.file_name}>
                    {d.file_name}
                    {d.notes && <div style={{ fontSize: 10, color: "#64748b", fontWeight: 400, marginTop: 1 }}>{d.notes}</div>}
                  </td>
                  <td style={{ ...td, color: "#64748b", direction: "ltr" }}>{fmtSize(d.size_bytes)}</td>
                  <td style={{ ...td, color: "#64748b", direction: "ltr" }}>{fmtDate(d.uploaded_at)}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {d.drive_web_link && (
                      <a
                        href={d.drive_web_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ padding: "3px 8px", background: "#dbeafe", color: "#1e40af", border: "none", borderRadius: 4, fontSize: 10, fontWeight: 700, marginLeft: 3, textDecoration: "none", display: "inline-block" }}
                      >
                        👁 عرض
                      </a>
                    )}
                    {d.drive_download_link && (
                      <a
                        href={d.drive_download_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ padding: "3px 8px", background: "#dcfce7", color: "#15803d", border: "none", borderRadius: 4, fontSize: 10, fontWeight: 700, marginLeft: 3, textDecoration: "none", display: "inline-block" }}
                      >
                        ⬇ تحميل
                      </a>
                    )}
                    {canEdit && (
                      <button onClick={() => remove(d)} title="حذف" style={{ padding: "3px 7px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>🗑</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
